// AUTO-GENERATED by `cargo run -p shared --bin gen-ts`. Do not edit.

export type CampaignId = string;

export type CampaignFlightId = string;

export type AudienceId = string;

export type CreativeId = string;

export type PixelId = string;

export type UserId = string;

export type SessionId = string;

export type AssetId = string;

export type ProductionId = string;

export type VenueId = string;

export type EventId = string;

export type OrderId = string;

export type TicketId = string;

export type AdSetId = CampaignFlightId;

export type Channel = "meta" | "ctv" | "dsp";

export type CampaignStatus = "draft" | "ready_to_publish" | "live" | "paused" | "ended";

export type CreativeStatus = "draft" | "review" | "approved" | "archived";

export type AudienceType = "direct" | "lookalike" | "retargeting";

export type MediaType = "image" | "video";

export type EventStatus = "on_sale" | "sold_out" | "soft" | "completed" | "cancelled" | "unknown";

export type IngestRunStatus = "queued" | "running" | "completed" | "failed" | "interrupted";

export type IngestTrigger = "refresh_48h" | "backfill_30d" | "manual";

export type CorrectionKind = "gap" | "topup";

export type PixelStatus = "active" | "paused" | "archived";

export type ProductionTag = "new" | "hit" | "critic" | "preview" | "open_run" | "open" | "touring" | "closed";

export type ProductionSource = "api" | "manual";

export type SyncTarget = "meta" | "ttd";

export type SyncStatus = "pending" | "syncing" | "synced" | "failed";

export type Role = "admin" | "planner" | "reporter";

export type CapabilityLevel = "full" | "view" | "own" | "none";

export type UserStatus = "active" | "invited" | "disabled";

export type LogLevel = "debug" | "info" | "warn" | "error";

export type LogSource = "frontend" | "api_warehouse" | "api_media" | "api_encorepro" | "worker" | "system";

export type Component = "frontend" | "api_worker" | "pixel_worker" | "batch_worker";

export type SeatsSource = "api" | "manual";

export type AssetKind = "image" | "video" | "banner_html";

export type Campaign = { id: CampaignId, name: string, production_id: ProductionId, channel: Channel, status: CampaignStatus, operational_status?: CampaignOperationalStatus | null, audience_id: AudienceId, budget_cents: bigint, spent_cents: bigint, start_date: string, end_date: string, flights: Array<CampaignFlight>, markup_on: boolean, starred: boolean, created_at: string, updated_at: string, sync: SyncState | null,
/**
 * Set while the user has armed "launch when ready": the scheduled sweep
 * performs the launch once preflight turns ready. The campaign stays
 * `draft` while armed.
 */
auto_launch_requested_at: string | null, };

export type CampaignOperationalState = "draft" | "armed" | "ready_to_publish" | "live" | "paused" | "paused_by_system" | "pausing" | "scheduled" | "starting" | "partially_active" | "needs_attention" | "unverified" | "ended";

export type CampaignObservedState = "active" | "paused" | "starting" | "blocked" | "partially_active" | "unknown";

export type CampaignCalendarPhase = "before_first_flight" | "in_flight" | "between_flights" | "finished";

export type CampaignOperationalStatus = { state: CampaignOperationalState, requested_state: CampaignStatus, observed_state: CampaignObservedState | null, observed_at: string | null, stale: boolean, pause_reason: string | null, reason: string | null, recovery: CreativeBindingRecoverySummary | null, next_flight_date?: string | null,
/**
 * Some of the campaign's audiences match nobody and SAM holds their ad sets or ad
 * groups paused while the rest deliver (0024), in words
 * (`failure_copy::audience_hold_words`). Absent when nothing is held, and when every
 * audience is: the campaign is then paused, and `pause_reason` says why.
 */
audience_hold?: string | null,
/**
 * Where the campaign stands against its flights today ([`CampaignCalendarPhase`]);
 * absent with no flights, and in rows stored before 2026-10-19.
 */
phase?: CampaignCalendarPhase | null,
/**
 * The last day of the last flight, so a finished campaign can say when it ended.
 */
last_flight_end?: string | null,
/**
 * Who paused the provider campaign, as the runtime row records it (`user`, `system`,
 * `schedule`): a pause the schedule made is never "paused by SAM".
 */
pause_origin?: string | null, };

export type CreativeBindingRecoverySummary = { operation_id: string, status: CreativeBindingRecoveryStatus, can_retry: boolean, attempts: number, max_attempts: number, next_retry_at: string | null, reason: string | null, completed_ads: number, total_ads: number, };

export type CreativeBindingRecoveryStatus = "repairing" | "waiting" | "blocked" | "complete";

export type CampaignFlight = { id: CampaignFlightId, campaign_id: CampaignId, name: string, start_date: string, end_date: string, budget_cents: bigint, };

export type CampaignAudienceSegment = { campaign_id: CampaignId, campaign_revision: number, position: number, audience_id: AudienceId, audience_revision: number, allocation_bps: number, };

export type AudienceRevisionSnapshot = { audience_id: AudienceId, revision: number, name: string, description: string | null, audience_type: AudienceType, rules: RuleGroup, created_at: string, };

export type CampaignCreative = { campaign_id: CampaignId, campaign_revision: number, position: number, creative_id: CreativeId,
/**
 * The creative revision this campaign revision flies. Pinned at plan save
 * (the newest approved revision, else the current one); a published
 * campaign keeps it whatever the creative does afterwards.
 */
creative_revision: number,
/**
 * When an operator paused this slot (0030), or `None` while it delivers.
 * Written last by the slot-change lane, in the batch that marks the
 * change `done`, so every plan-derived reader expects a paused slot's ads
 * PAUSED and never repairs them back. A plan write leaves it alone
 * (only drafts are written, and a draft has no provider ads).
 */
paused_at?: string | null, };

export type CreativeRolloutStatus = "queued" | "running" | "done" | "failed" | "cancelled";

export type CampaignCreativeRollout = { id: string, campaign_id: CampaignId, campaign_revision: number, position: number, creative_id: CreativeId, from_revision: number, to_revision: number, target: SyncTarget, status: CreativeRolloutStatus, requested_by: string, requested_by_name: string | null, requested_at: string, started_at: string | null, completed_at: string | null, attempts: number, last_error: string | null,
/**
 * `last_error` in words (`crate::failure_copy`), built at read time.
 */
failure?: FailureView | null,
/**
 * Provider objects per audience segment, as JSON text: for Meta
 * `{"ads":[{"segment_position":0,"retired_ad_id":"…","ad_id":"…"}]}`,
 * for TTD `{"ad_groups":[{"segment_position":0,"ad_group_id":"…"}],
 * "added":[…],"removed":[…]}`.
 */
details_json: string, };

export type AddCampaignCreativeRequest = { creative_id: CreativeId, creative_revision: number, expected_campaign_revision: number, idempotency_key: string, };

export type CampaignCreativeAddition = { id: string, campaign_id: CampaignId, campaign_revision: number, position: number, creative_id: CreativeId, creative_revision: number, status: CreativeRolloutStatus, requested_by: string, requested_by_name: string | null, requested_at: string, started_at: string | null, completed_at: string | null, updated_at: string, attempts: number, last_error: string | null,
/**
 * `last_error` in words (`crate::failure_copy`), built at read time.
 */
failure?: FailureView | null,
/**
 * What the addition created per audience segment, as JSON text — the same
 * shape a rollout records, minus anything retired.
 */
details_json: string, };

export type ExtendCampaignFlightRequest = {
/**
 * The journal row's id: a replayed request answers the row it already made.
 */
idempotency_key: string,
/**
 * The revision the caller extends; a mismatch is a 409.
 */
expected_revision: number,
/**
 * The new flight's name; the server names it when absent.
 */
flight_name?: string | null, start_date: string, end_date: string,
/**
 * Gross, as the operator enters it.
 */
budget_cents: bigint, };

export type CampaignFlightExtensionStatus = "queued" | "running" | "committed" | "done" | "failed" | "cancelled";

export type FlightCreateState = "none" | "dispatched" | "rejected" | "unknown";

export type CampaignFlightExtension = { id: string, campaign_id: CampaignId, campaign_revision: number, target: SyncTarget, flight_id: CampaignFlightId, flight_name: string, start_date: string, end_date: string,
/**
 * Gross.
 */
budget_cents: bigint, status: CampaignFlightExtensionStatus, create_state: FlightCreateState,
/**
 * The extension holds the campaign's activation: its TTD ad groups stay off
 * until the flight is committed or verifiably gone.
 */
activation_hold: boolean, cancel_requested_at: string | null, last_error: string | null,
/**
 * `last_error` in words (`crate::failure_copy`), built at read time.
 */
failure?: FailureView | null, attempts: number, requested_by: string, requested_at: string, updated_at: string, };

export type CreativeRolloutRequest = {
/**
 * The approved revision to roll out.
 */
creative_revision: number,
/**
 * The revision the caller believes the slot flies now; a mismatch is a
 * 409, so two people cannot roll the same slot from a stale screen.
 */
expected_creative_revision: number, };

export type CreativeRolloutResponse = { rollout: CampaignCreativeRollout, };

export type CreativeSlotOp = "pause" | "resume";

export type CampaignCreativeSlotChange = { id: string, campaign_id: CampaignId, campaign_revision: number, position: number, creative_id: CreativeId, op: CreativeSlotOp, target: SyncTarget,
/**
 * Shares the rollout vocabulary; `cancelled` never occurs here.
 */
status: CreativeRolloutStatus, requested_by: string, requested_by_name: string | null, requested_at: string, started_at: string | null, completed_at: string | null, attempts: number, last_error: string | null,
/**
 * `last_error` in words (`crate::failure_copy`), built at read time.
 */
failure?: FailureView | null,
/**
 * Provider objects per audience segment, as JSON text: for Meta
 * `{"ads":[{"segment_position":0,"ad_id":"…","status":"paused"}]}`, for
 * TTD `{"ad_groups":[{"segment_position":0,"ad_group_id":"…"}],
 * "detached":[…]}` or `"attached":[…]`.
 */
details_json: string, };

export type CreativeSlotChangeRequest = {
/**
 * The revision the caller believes the slot flies now; a mismatch is a
 * 409, so a stale screen cannot pause a slot that has since rolled over.
 */
expected_creative_revision: number, };

export type CreativeSlotChangeResponse = { change: CampaignCreativeSlotChange, };

export type PlatformDeliveryGroup = { campaign_id: CampaignId, campaign_revision: number, audience_segment_position: number, target: SyncTarget, provider_delivery_group_id: string | null, provider_status: string | null, last_error: string | null, created_at: string, updated_at: string, };

export type CampaignPlan = { campaign: Campaign, revision: number, audience_segments: Array<CampaignAudienceSegment>, audience_revisions: Array<AudienceRevisionSnapshot>, creatives: Array<CampaignCreative>, delivery_groups: Array<PlatformDeliveryGroup>,
/**
 * Every creative rollout requested at this plan revision, newest first.
 * Empty for drafts and for plans loaded before 0024.
 */
creative_rollouts: Array<CampaignCreativeRollout>,
/**
 * Every pause or resume requested at this plan revision, newest first
 * (0030). Empty for drafts and for plans loaded before it.
 */
creative_slot_changes: Array<CampaignCreativeSlotChange>, };

export type CampaignOverviewStatusFilter = "draft" | "ready_to_publish" | "live" | "paused" | "ended" | "armed";

export type CampaignOverviewSort = "needs_attention" | "campaign" | "production" | "channel" | "status" | "audience_type" | "start_date" | "end_date" | "budget" | "gross_spend" | "pacing" | "roas" | "cpa" | "tickets" | "ticket_value" | "impressions" | "cpm" | "clicks" | "ctr";

export type CampaignOverviewSortDirection = "asc" | "desc";

export type CampaignOverviewRequest = { operational_status?: CampaignOperationalState | null, from: string, to: string,
/**
 * Stable campaign selector used by the Reports-style campaign filter.
 */
campaign_id?: CampaignId | null,
/**
 * Compatibility free-text search. The Campaigns screen no longer emits
 * this value, but older signed-in URLs and clients remain valid.
 */
q?: string | null, channel?: Channel | null, status?: CampaignOverviewStatusFilter | null, production_id?: ProductionId | null,
/**
 * ANY semantics against the campaign's current revision-pinned segments.
 */
audience_types: Array<AudienceType>, sort: CampaignOverviewSort, direction: CampaignOverviewSortDirection, };

export type CampaignOverviewPerformance = { gross_spend_cents?: bigint | null,
/**
 * Gross spend over the same closed provider scopes as attribution.
 */
attribution_gross_spend_cents?: bigint | null, impressions?: bigint | null, clicks?: bigint | null, ctr?: number | null,
/**
 * Gross/client CPM in cents per thousand impressions.
 */
cpm_cents?: number | null,
/**
 * Direct tickets (the campaign's own show only), provider-attributed, in
 * millionths: `/reports` → Performance's `assigned_conversions_micros`.
 * The browser derives Tickets, Ticket Value, CPA and ROAS from these
 * primitives with the same formulas `/reports` uses
 * (`frontend/src/lib/performance-metrics.ts`).
 */
tickets_micros?: bigint | null,
/**
 * Direct ticket value in currency-major-unit millionths:
 * `assigned_revenue_micros`.
 */
ticket_value_micros?: bigint | null, };

export type CampaignOverviewPacingStatus = "behind" | "on_pace" | "ahead" | "not_started" | "not_launched" | "paused" | "ended" | "unavailable";

export type CampaignOverviewPacing = { status: CampaignOverviewPacingStatus,
/**
 * Actual / planned; 1.0 is exactly on plan.
 */
ratio?: number | null, actual_gross_spend_cents?: bigint | null, planned_spend_cents?: bigint | null, variance_cents?: bigint | null, cutoff?: string | null, };

export type CampaignOverviewTrendPoint = { day: string,
/**
 * Null means no complete delivery publication covers this day. A
 * published zero is represented as `0`.
 */
actual_gross_spend_cents?: bigint | null, planned_spend_cents: bigint, };

export type CampaignOverviewFlightSpend = { flight_id: CampaignFlightId,
/**
 * Gross spend on the flight's days so far (today's provisional delivery
 * included, as `/reports` includes it). Null when a closed day of the
 * flight has no delivery publication: unknown, never $0.
 */
gross_spend_cents?: bigint | null, };

export type CampaignOverviewFlightSpends = { flights: Array<CampaignOverviewFlightSpend>,
/**
 * Gross spend on days inside no current flight (an earlier revision's
 * schedule). Shown when it is not zero.
 */
unallocated_gross_spend_cents: bigint, };

export type CampaignOverviewAggregatePacing = { ratio?: number | null, actual_gross_spend_cents?: bigint | null, planned_spend_cents?: bigint | null, };

export type CampaignOverviewKpis = { budget_cents: bigint, gross_spend_cents?: bigint | null, attribution_gross_spend_cents?: bigint | null, pacing: CampaignOverviewAggregatePacing,
/**
 * The listed campaigns' direct primitives summed before any ratio, as
 * `/reports` totals them.
 */
tickets_micros?: bigint | null, ticket_value_micros?: bigint | null, };

export type CampaignOverviewRow = { campaign_id: CampaignId, campaign_name: string, production_id: ProductionId, production_name: string, channel: Channel, status: CampaignStatus, operational_status?: CampaignOperationalStatus | null, auto_launch_requested_at?: string | null, audience_types: Array<AudienceType>, start_date: string, end_date: string, budget_cents: bigint, performance: CampaignOverviewPerformance, pacing: CampaignOverviewPacing,
/**
 * Exactly fourteen common ET dates, oldest first, whenever a delivery
 * cutoff is available.
 */
trend_14d: Array<CampaignOverviewTrendPoint>, };

export type CampaignOverviewResponse = { rows: Array<CampaignOverviewRow>, kpis: CampaignOverviewKpis, freshness: ReportFreshness, methodology: ReportMethodology, revision: DataRevisionStamp, performance_from: string, performance_to: string, delivery_cutoff?: string | null,
/**
 * Per-flight spend, only when the request names one campaign.
 */
flight_spend?: CampaignOverviewFlightSpends | null, };

export type DataDomain = "identity_rbac" | "catalog" | "campaigns" | "audiences" | "creatives" | "pixels" | "configuration" | "delivery_reporting" | "attribution_reporting";

export type DataDomainRevision = { domain: DataDomain, revision: bigint, updated_at: string, };

export type DataRevisionManifest = { observed_at: string,
/**
 * Stable SHA-256 fingerprint of the authenticated identity, capability
 * projection, and production scope. It never embeds their raw values.
 */
scope_fingerprint: string,
/**
 * Only domains the authenticated caller is allowed to observe.
 */
domains: Array<DataDomainRevision>, };

export type DataRevisionStamp = { observed_at: string, scope_fingerprint: string, domains: Array<DataDomainRevision>, };

export type Audience = { id: AudienceId, name: string, description: string | null, audience_type: AudienceType, rules: RuleGroup, estimated_size: bigint | null, size_estimated_at: string | null, created_at: string, updated_at: string, sync: SyncState | null,
/**
 * The reconciler that owns this row, or `None` for the operator-owned rows
 * that are every audience a human creates.
 *
 * Set only by the API's managed-audience reconciler: the create route
 * builds its `Audience` from a request body with no such field, so a client
 * cannot reach it. Present on the read type because the editor has to know
 * not to offer edit, archive or seed controls for a row the cron will
 * simply rewrite on its next tick.
 *
 * `#[serde(default)]` so a stored payload written before the column existed
 * still decodes.
 */
managed_by: string | null, };

export type AttachCreativeAssetRequest = { asset_id: AssetId, placement_tag: string, width?: number | null, height?: number | null, duration_ms?: number | null, };

export type Creative = { id: CreativeId, name: string, production_id: ProductionId, channel: Channel, status: CreativeStatus, media_type: MediaType, format: string, spec: string, destination_url: string, assets: Array<CreativeAsset>, created_by: UserId, agency: string | null, created_at: string, last_used_at: string | null,
/**
 * Provider state of the *current* revision (see `revision`).
 */
sync: SyncState | null,
/**
 * Every campaign whose *current* revision has left draft and still pins
 * one of this creative's revisions. A pinned revision is immutable, so an
 * edit of its content saves a new revision instead. Computed on read.
 */
pinned_by: Array<CreativePin>,
/**
 * The latest revision — the one `status`, `spec`, `destination_url`,
 * `assets` and `sync` describe. Content is immutable per revision:
 * editing an approved or pinned revision mints `revision + 1`.
 */
revision: number,
/**
 * The newest approved revision, if any. Campaigns pin this one. It can
 * lag `revision` while a newer draft is being worked on.
 */
approved_revision: number | null,
/**
 * sha256 over the current revision's spec, destination and assets — the
 * key every review artefact and provider name is derived from. `None`
 * until the backfill tick has computed it for a pre-0023 row.
 */
content_hash: string | null, };

export type CreativeAsset = { asset_id: AssetId, r2_key: string,
/**
 * Short-lived, production-scoped browser read URL minted by the API.
 * Persistence layers leave this `None`; it is populated only on an
 * authorized creative response.
 */
view_url?: string | null, kind: AssetKind, width: number | null, height: number | null, duration_ms: number | null, byte_size: bigint, upload_status: AssetStatus, placement_tags: Array<string>,
/**
 * How a Display banner animates, read from its bytes when it was attached
 * (`shared::image_probe`). `None` for a still image's predecessor rows, video and
 * anything attached before the probe existed.
 */
animation?: AssetAnimation | null, };

export type AssetAnimation = { frame_count: number, cycle_ms: number, plays: number, };

export type CreativePin = { campaign_id: CampaignId, campaign_name: string, campaign_status: CampaignStatus, operational_status?: CampaignOperationalStatus | null, campaign_revision: number, creative_revision: number,
/**
 * When a rollout retired this pin — the campaign moved on to another
 * revision at that instant. `None` for a pin the campaign still flies.
 */
replaced_at: string | null, };

export type CreativeRevisionStatus = "draft" | "review" | "approved";

export type CreativeRevisionProviderObject = { target: SyncTarget, provider_creative_id: string, };

export type CreativeRevisionSnapshot = { creative_id: CreativeId, revision: number, status: CreativeRevisionStatus, content_hash: string | null, spec: string, destination_url: string, assets: Array<CreativeAsset>,
/**
 * `authored`, `backfill_0023` or `restore`.
 */
origin: string, created_by: UserId, created_by_name: string | null, created_at: string, submitted_at: string | null, approved_by: UserId | null, approved_by_name: string | null, approved_at: string | null,
/**
 * Every campaign revision that ever pinned this revision, any status.
 */
pins: Array<CreativePin>, provider_objects: Array<CreativeRevisionProviderObject>,
/**
 * Provider state of this revision, if a sync was ever attempted for it.
 */
sync: SyncState | null, };

export type CreativeRevisionsResponse = { revisions: Array<CreativeRevisionSnapshot>, };

export type CreativeContentWriteResponse = { creative: Creative, created_revision?: number | null, };

export type SaveCreativeContentRequest = { expected_revision: number, expected_content_hash: string, spec: string, destination_url: string, assets: Array<AttachCreativeAssetRequest>, };

export type MetaCreativeSpec = { post_text: string, headline: string, link_desc: string, cta: string, display_url: string, };

export type Pixel = { id: PixelId, tenant_id: string, description: string, status: PixelStatus, deployed_at: string, owner_email: string, domains: Array<string>, meta_pixel_id: string | null, ttd_adv_id: string | null, ttd_ct_id: string | null, ttd_ct_id_pageview: string | null, default_venue_id: VenueId | null, default_production_id: ProductionId | null,
/**
 * Universal-pixel dataLayer harvest + field-mapping config (JSON-in-TEXT
 * `extraction_json`). Defaults to inert (auto-harvest on, no remap).
 */
extraction: PixelExtraction,
/**
 * PII harvest + advanced-matching + identity toggles (`pii_json`).
 * Defaults to no PII.
 */
pii: PixelPii,
/**
 * Per-platform × per-event drop toggles (`firing_json`). Defaults to all
 * provisioned platforms firing.
 */
firing: FiringConfig, };

export type PixelCreate = { id: PixelId, tenant_id: string, description: string, owner_email: string, domains: Array<string>, meta_pixel_id: string | null, ttd_adv_id: string | null, ttd_ct_id: string | null, ttd_ct_id_pageview: string | null, default_venue_id: VenueId | null, default_production_id: ProductionId | null, extraction: PixelExtraction, pii: PixelPii, firing: FiringConfig, };

export type PixelDefaults = {
/**
 * DERIVED, never stored: true when at least one identifier is set. A row
 * whose every field is NULL prefills nothing, so it must not claim to be
 * configured — that keeps "clear every field" indistinguishable from "no
 * row at all" and makes a reset idempotent.
 */
configured: boolean, tenant_id: string | null, meta_pixel_id: string | null, ttd_adv_id: string | null, ttd_ct_id: string | null, ttd_ct_id_pageview: string | null, updated_at: string | null, };

export type PixelDefaultsUpsert = { tenant_id: string | null, meta_pixel_id: string | null, ttd_adv_id: string | null, ttd_ct_id: string | null, ttd_ct_id_pageview: string | null, };

export type Production = { id: ProductionId, name: string, source_reference: string, first_event_starts_at: string | null, last_event_starts_at: string | null, venue_id: VenueId | null, genre: string | null, avg_price_cents: bigint | null, tag: ProductionTag | null, heat: number | null, week_gross_cents: bigint | null,
/**
 * Raw fill components over this production's seated events (events with a known
 * seat count), for weighted `Σ sold ÷ Σ capacity` aggregation. `None` when the
 * production has no seated events.
 */
seated_sold: bigint | null, seated_capacity: bigint | null, thumbnail_url: string | null, source: ProductionSource, last_synced_at: string,
/**
 * Operator toggle: this production stays everywhere in the product but
 * the alert evaluators skip it (data quality broken by design — e.g. a
 * CC-fee bucket or rehearsal artifact). Defaulted for rolling deploys.
 */
alerts_exempt: boolean, };

export type RunningProductionsSummary = { generated_at: string, source_last_synced_at: string | null, total: number, api: number, manual: number, production_ids: Array<ProductionId>, };

export type Event = { id: EventId, production_id: ProductionId, venue_id: VenueId, performance_at: string, capacity: number, tickets_sold: number, revenue_cents: bigint, pace: number, last_sale_min_ago: number | null, daily_sales_14d: Array<number>, ticket_breakdown: TicketBreakdown, status: EventStatus, last_synced_at: string, };

export type TicketBreakdown = { general: number, vip: number, comp: number, };

export type Venue = { id: VenueId, name: string | null, address_1: string | null, address_2: string | null, city: string | null, postcode: string | null, state: string | null, state_code: string | null, country: string | null, country_code: string | null, slug: string | null, neighborhood: string | null, seats: number | null, seats_source: SeatsSource, venue_type: string | null, week_gross_cents: bigint | null, tickets: bigint | null, avg_price_cents: bigint | null, events: bigint | null, last_synced_at: string, };

export type DailyRollup = { date: string, campaign_id: CampaignId, channel: Channel, spend_cents: bigint, impressions: bigint, clicks: bigint, direct_tickets: bigint, direct_revenue_cents: bigint, halo_tickets: bigint, halo_revenue_cents: bigint, };

export type PlacementRow = { date: string, channel: Channel, placement: string, campaign_id: CampaignId | null, impressions: bigint, clicks: bigint, spend_cents: bigint, tickets: bigint,
/**
 * Additive denominator used to recompute VCR across arbitrary groups.
 */
video_starts: bigint,
/**
 * Additive numerator used to recompute VCR across arbitrary groups.
 */
video_completions: bigint,
/**
 * Convenience ratio for this row (`video_completions / video_starts`).
 */
vcr: number | null,
/**
 * Whether these figures are final, or still being restated by the
 * provider.
 *
 * This is a **label**, never a filter. Rows are returned whatever it says;
 * a `false` day is a real measurement that can still move — today's
 * delivery, or a day inside the provider's restatement window — and must
 * render as provisional rather than being withheld or shown as settled.
 * `None` means no scope backed the row at all, which callers must not
 * collapse into `Some(false)`.
 */
is_complete: boolean | null, };

export type MetaPolicyScope = "campaign" | "ad_set" | "audience" | "creative" | "ad";

export type MetaPolicyControl = "enforced" | "default" | "editable" | "derived";

export type MetaPolicyRow = { code: string, label: string, value: string, control: MetaPolicyControl, detail: string | null, items: Array<string>, technical_value: string | null, configured: boolean | null, };

export type MetaPolicySection = { scope: MetaPolicyScope, title: string, summary: string, rows: Array<MetaPolicyRow>, };

export type MetaDeliveryContract = { version: string, graph_api_version: string, sections: Array<MetaPolicySection>, };

export type CreativeSpecs = { meta: MetaCreativeSpecs, ctv: CtvCreativeSpecs, display: DisplayCreativeSpecs, };

export type MetaCreativeSpecs = {
/**
 * The aspect ratios a Meta creative carries, one file per ratio, each tagged with
 * `tag` at attach.
 */
formats: Array<CreativeFormatSpec>,
/**
 * How many of `formats` a creative needs before it can be submitted.
 */
min_formats: number, image: CreativeFileLimits, video: CreativeVideoLimits, post_text: CreativeCopyLimit, headline: CreativeCopyLimit, link_description: CreativeCopyLimit,
/**
 * The call-to-action labels offered.
 */
ctas: Array<string>, };

export type CreativeFormatSpec = { tag: string, ratio_label: string, min_width: number, min_height: number, aspect_ratio: number, aspect_tolerance: number, };

export type CreativeFileLimits = { mime_types: Array<string>, max_bytes: number, };

export type CreativeVideoLimits = { mime_types: Array<string>,
/**
 * The accepted video codecs (sample entries), checked on the file's own bytes.
 */
codecs: Array<string>,
/**
 * The other rules the bytes are held to.
 */
byte_rules: Array<string>, max_bytes: number, min_duration_ms: number, max_duration_ms: number, };

export type CreativeCopyLimit = { recommended_chars: number, max_chars: number, };

export type CtvCreativeSpecs = {
/**
 * The one file a CTV creative carries, tagged with `tag` at attach.
 */
tag: string, mime_types: Array<string>, max_bytes: number, min_width: number, min_height: number, aspect_ratio: number, aspect_tolerance: number, durations_ms: Array<number>, duration_tolerance_ms: number, };

export type DisplayCreativeSpecs = { mime_types: Array<string>, max_bytes: number,
/**
 * Past this a banner is warned about (some sites may refuse it), never refused.
 */
recommended_max_bytes: number,
/**
 * An animation should stop within this; past it a banner is warned about, never refused.
 */
animation_max_ms: number,
/**
 * Each banner is tagged `dsp_{width}x{height}_{n}` at attach.
 */
sizes: Array<DisplaySizeSpec>, policy_notes: Array<string>, };

export type DisplaySizeSpec = { width: number, height: number, name: string, };

export type CreativeReadiness = { creative_id: string, status: CreativeStatus, revision: number, content_hash: string | null, ready_for_review: boolean, problems: Array<CreativeReadinessProblem>,
/**
 * The page where a person adds or replaces this creative's files, while it is a draft.
 */
upload_url: string | null, };

export type CreativeReadinessProblem = {
/**
 * `status`, `content` or `files`.
 */
code: string, message: string, };

export type CreativeSubmitForReviewRequest = { expected_revision: number, expected_content_hash: string, };

export type MetaPreviewPlacement = "facebook_feed" | "instagram_feed" | "instagram_explore_home" | "instagram_story" | "facebook_story" | "instagram_reels" | "facebook_reels";

export type MetaPreviewStatus = "pending" | "generating" | "ready" | "failed";

export type MetaPreviewVariant = { placement: MetaPreviewPlacement, label: string, ad_format: string, status: MetaPreviewStatus, embed_url: string | null, width: number | null, height: number | null, generated_at: string | null, refresh_after: string | null, expires_at: string | null, error_code: string | null, };

export type MetaPreviewBundle = { creative_id: string, content_hash: string, status: MetaPreviewStatus,
/**
 * Every placement has been rendered by Meta at least once for this exact
 * content hash. This is the durable fact the approval gate cares about: a
 * human reviewed these bytes, and a signed URL ageing out later does not
 * un-review them.
 */
rendered: boolean,
/**
 * `rendered` *and* the cached iframe URLs are younger than the soft TTL.
 * Purely a hint that the gallery should re-mint them in the background —
 * never a gate. The cached URL is served either way.
 */
fresh: boolean, variants: Array<MetaPreviewVariant>, reviewed_placements: Array<MetaPreviewPlacement>,
/**
 * Decisions recorded by signed-in reviewers. Unlike `reviewed_placements`
 * this carries `changes_requested` too, so the UI can name the placement and
 * reviewer blocking approval instead of silently failing the gate later.
 */
internal_feedback: Array<CreativeReviewFeedback>, external_feedback: Array<CreativeReviewFeedback>, };

export type CreativeReviewSession = { id: string, creative_id: string, content_hash: string, share_url: string, expires_at: string, };

export type CreativeReviewDecision = "reviewed" | "changes_requested";

export type CreativeReviewDecisionRequest = { placement: MetaPreviewPlacement, decision: CreativeReviewDecision, reviewer_name: string, comment: string | null, };

export type CreativeReviewFeedback = { placement: MetaPreviewPlacement, decision: CreativeReviewDecision, reviewer_name: string, comment: string | null, decided_at: string, };

export type CreativeApprovalVerdict = "approved" | "changes_requested";

export type CreativeReviewLink = { creative_id: string, creative_name: string,
/**
 * The package row behind the link, which the staff list and the approval
 * record name.
 */
package_id: string,
/**
 * `/creative-review#p=<share token>`. The token is derived from the
 * package id, so the staff list can hand the same URL back while the link
 * is live.
 */
share_url: string, expires_at: string,
/**
 * The creative already had a live link at this content; nothing was minted.
 */
reused: boolean, };

export type CreativeReviewDestination = {
/**
 * The landing page the ad links to.
 */
url: string,
/**
 * The domain the ad shows under the copy (`MetaCreativeSpec::display_url`).
 */
display_url: string,
/**
 * The button label (`MetaCreativeSpec::cta`), as the editor stores it.
 */
cta_label: string, };

export type CreativeReviewPackageItem = { creative_id: string, creative_name: string,
/**
 * The creative was edited after the link was shared, so the previews below
 * are not what the package was built from. Decisions are refused.
 */
superseded: boolean,
/**
 * Which kind of creative this is: a Meta item carries `previews`, a Display item
 * carries `display`.
 */
channel: Channel,
/**
 * Meta's rendered placement previews. `None` on a Display item.
 */
previews?: MetaPreviewBundle | null,
/**
 * A Display item's banners, each shown at its own size. `None` on a Meta item.
 */
display?: DisplayReviewBundle | null,
/**
 * This reviewer's own latest verdict, if they have already responded.
 */
my_verdict: CreativeApprovalVerdict | null,
/**
 * A preview refresh was requested for these stale frames and has not
 * landed yet; the page keeps showing the old frames and polls meanwhile.
 */
refreshing: boolean,
/**
 * The creative's current destination. `None` when its stored spec does not
 * parse as a Meta spec. Describes the current content, so a `superseded`
 * item's destination is not what the link was built from: hide it there.
 */
destination: CreativeReviewDestination | null, };

export type DisplayReviewBundle = {
/**
 * The landing page every banner links to (the creative's destination URL).
 */
click_through_url: string, banners: Array<DisplayReviewBanner>, };

export type DisplayReviewBanner = { placement_tag: string, width: number, height: number, byte_size: bigint,
/**
 * A short-lived read URL, minted on every package load. `None` when it could not
 * be minted; the page then says the image is unavailable.
 */
view_url?: string | null, animation?: AssetAnimation | null,
/**
 * It loops for ever or animates longer than TTD's 15 seconds.
 */
exceeds_animation_limit: boolean, };

export type CreativeReviewPackageSummary = { id: string, name: string, creative_ids: Array<string>, expires_at: string,
/**
 * Revoked before revoking was removed. Historical only: nothing sets it
 * any more, and a revoked package is never `live`.
 */
revoked: boolean, created_at: string,
/**
 * Neither expired nor revoked: reviewers can still open it.
 */
live: boolean,
/**
 * The package holds the listed creative at its current content. A link
 * whose creative has changed since still opens, but every decision on it
 * is refused, so the share panel offers a new link instead of copying it.
 */
current: boolean,
/**
 * The link, for copying again. Present only while the package is `live`
 * and its token is the derived one; a link minted before derived tokens
 * cannot be reproduced and reads `None`, though it still works for
 * reviewers until it expires.
 */
share_url: string | null, approvals: Array<CreativeApprovalRecord>,
/**
 * Everyone who confirmed an email for this package, one row per
 * confirmation, ordered by email.
 */
reviewers: Array<CreativeReviewerActivity>, };

export type CreativeReviewerActivity = { verification_id: string, email: string, verified_at: string,
/**
 * The last time the package was opened with this confirmation. Recorded at
 * most once every 15 minutes, and absent for confirmations made before
 * activity was recorded.
 */
last_opened_at: string | null,
/**
 * How many times the emailed link was opened again after the first
 * confirmation. More than expected suggests a forwarded email.
 */
reopen_count: number,
/**
 * A browser that may view but not decide (neither the one that asked for
 * the email nor the one that first opened it) has opened the package with
 * this confirmation: the emailed link was forwarded or opened elsewhere.
 */
viewed_from_other_browsers: boolean, access_expires_at: string, };

export type CreativeApprovalRecord = { id: string, creative_id: string, email: string, verdict: CreativeApprovalVerdict, comment: string | null, decided_at: string, ip_address: string | null, user_agent: string | null,
/**
 * False once the creative has been edited since this decision was made.
 */
current: boolean, };

export type PublicReviewPackage = {
/**
 * The page's storage key for this reviewer's access token. Not a secret:
 * it opens nothing without the token.
 */
package_id: string, name: string, verified_email: string, expires_at: string, items: Array<CreativeReviewPackageItem>,
/**
 * Whether this browser may record a decision. Only the browser that asked
 * for the email and the one that first opened the emailed link may; any
 * other browser holding a forwarded link can view but not decide. A
 * confirmation that registered no browser (made before this rule, or from
 * an older page) may decide from any browser.
 */
can_decide: boolean, };

export type PublicReviewPackageRequest = {
/**
 * The share token the page was opened with. When present it must name the
 * same package the access token does, or the request is refused.
 */
token?: string | null,
/**
 * The reviewer access token from `verify/complete`.
 */
access?: string | null,
/**
 * This browser's device id (see [`ReviewVerifyStartRequest::device`]). It
 * decides the answer's `can_decide`; a malformed value is treated exactly
 * as an absent one.
 */
device?: string | null, };

export type CreateReviewPackageRequest = {
/**
 * The context a newly minted link is named with, followed by the
 * creative's own name; the reviewer sees it on the page and in the email.
 * A reused link keeps the name it was minted with.
 */
name: string, creative_ids: Array<string>, };

export type CreativeApprovalRequest = { creative_id: string, verdict: CreativeApprovalVerdict, comment: string | null,
/**
 * The reviewer access token from `verify/complete`, the only credential
 * `/approvals` reads. Absent is the uniform 404.
 */
access?: string | null,
/**
 * This browser's device id (see [`ReviewVerifyStartRequest::device`]). A
 * browser that is not one of the two that may decide is refused with a
 * 409; a malformed value is treated exactly as an absent one.
 */
device?: string | null, };

export type ReviewVerifyStartRequest = { token: string, email: string,
/**
 * The creative the reviewer was looking at, carried into the emailed link
 * as `&c=`. One the package does not hold is left out of the link without
 * comment, so the endpoint never says which creatives a package contains.
 */
creative_id?: string | null,
/**
 * This browser's device id: 32 random bytes, base64url without padding,
 * kept in localStorage. The browser that asks for the email is one of the
 * two that may decide on the creatives it opens. Only its SHA-256 is
 * stored, and a malformed value is treated exactly as an absent one.
 */
device?: string | null, };

export type ReviewVerifyCompleteRequest = { challenge: string,
/**
 * The package share token from the emailed link (`&p=`). When present it
 * must be the token of the package the challenge belongs to.
 */
token?: string | null,
/**
 * This browser's device id (see [`ReviewVerifyStartRequest::device`]). The
 * browser whose click first claims the link is the other one that may
 * decide; later clicks register nothing. A malformed value is treated
 * exactly as an absent one.
 */
device?: string | null, };

export type ReviewVerifyCompleteResponse = { access_token: string,
/**
 * Fixed at the first confirmation: 72 hours later, or the package's own
 * expiry if that comes first.
 */
access_expires_at: string, package: PublicReviewPackage, };

export type SpilloverFlow = { date: string, advertised_production_id: ProductionId, purchased_production_id: ProductionId, audience_type: AudienceType,
/**
 * Provider-attributed conversion count in millionths. Consumers should
 * prefer this additive value over the rounded compatibility ticket count.
 */
attributed_conversions_micros: bigint, tickets: bigint,
/**
 * Provider-attributed currency units in millionths.
 */
attributed_revenue_micros: bigint, revenue_cents: bigint, };

export type MarkupRow = { date: string, campaign_id: CampaignId,
/**
 * Production assigned by the immutable campaign revision that generated
 * this delivery, never the campaign's mutable current projection.
 */
production_id: ProductionId, channel: Channel, spend_cents: bigint, marked_up: boolean, markup_cents: bigint, };

export type IngestRun = { id: string, trigger: IngestTrigger, status: IngestRunStatus,
/**
 * Operator id/name for manual runs; None for scheduled.
 */
requested_by: string | null, requested_by_name: string | null, window_from: bigint, window_to: bigint,
/**
 * Single-production scope (manual only); None = all productions.
 */
production_id: ProductionId | null, tickets_in: bigint | null, tickets_new: bigint | null, tickets_updated: bigint | null, tickets_out: bigint | null, resolved_ct: bigint | null, carried_ct: bigint | null, anonymous_ct: bigint | null,
/**
 * Recency (ms-epoch) of the freshest ticket the source returned.
 */
source_max_created_at: bigint | null,
/**
 * `now - source_max_created_at`, in whole hours — high ⇒ EncorePro stale.
 */
source_lag_hours: bigint | null, corrections_gaps: bigint | null, corrections_topups: bigint | null, corrections_tickets: bigint | null, corrections_productions: bigint | null, error_message: string | null, progress: number | null, progress_note: string | null, created_at: bigint, started_at: bigint | null,
/**
 * Most recent liveness stamp written by the owning batch process. A
 * `running` status without a recent heartbeat must not be presented as
 * actively progressing.
 */
heartbeat_at: bigint | null, finished_at: bigint | null, };

export type IngestCorrection = { id: string, run_id: string, production_id: ProductionId, day: bigint, old_tickets: bigint, new_tickets: bigint, delta: bigint, kind: CorrectionKind, trigger: IngestTrigger, created_at: bigint, };

export type EnqueueBackfill = { date_from: bigint, date_to: bigint, production_id: ProductionId | null, };

export type PipelineMonitorPolicy = { upcoming_window_days: bigint, stall_after_days: bigint, run_heartbeat_stale_secs: bigint, };

export type ProductionStat = { production_id: ProductionId, tickets: bigint, refunds: bigint, revenue_cents: bigint, email_ct: bigint, phone_ct: bigint, anon_ct: bigint, person_resolved_ct: bigint,
/**
 * Sum of the daily distinct-household counts over the window (a reach proxy;
 * slightly overcounts households active on multiple days).
 */
household_reach_ct: bigint, null_venue_ct: bigint, null_event_ct: bigint, missing_seat_ct: bigint, backfill_ct: bigint, missing_currency_ct: bigint, missing_value_ct: bigint, missing_address_ct: bigint, active_days: number, last_active_day: bigint | null, };

export type ProductionStatDay = { production_id: ProductionId, day: bigint, tickets: bigint, refunds: bigint, revenue_cents: bigint, email_ct: bigint, phone_ct: bigint, anon_ct: bigint, person_resolved_ct: bigint,
/**
 * Distinct households reached that day (`COUNT(DISTINCT household_id)`).
 */
household_reach_ct: bigint, null_venue_ct: bigint, null_event_ct: bigint, missing_seat_ct: bigint, backfill_ct: bigint, missing_currency_ct: bigint, missing_value_ct: bigint, missing_address_ct: bigint, };

export type AdSet = CampaignFlight;

export type AudiencePlannerRequest = { audience_type: AudienceType, rules: RuleGroup, };

export type AudiencePreviewRequest = { rules: RuleGroup, projections: Array<Projection>, };

export type AudiencePreviewResponse = { count: bigint, total_reach: bigint, computed_at: string, breakdowns: Array<ProjectionResult>, };

export type Projection = { "kind": "count" } | { "kind": "by_zip_prefix", len: number, } | { "kind": "by_recency_band" } | { "kind": "by_production_genre" } | { "kind": "by_venue" } | { "kind": "by_audience_type_seed" } | { "kind": "by_age_range" } | { "kind": "by_gender" } | { "kind": "by_income" } | { "kind": "by_education" } | { "kind": "by_household_has_children" } | { "kind": "lookalike_model" };

export type ProjectionResult = { "kind": "count", count: bigint, } | { "kind": "by_zip_prefix", len: number, buckets: Array<ZipBucket>, } | { "kind": "by_recency_band", buckets: Array<RecencyBucket>, } | { "kind": "by_production_genre", buckets: Array<GenreBucket>, } | { "kind": "by_venue", buckets: Array<VenueBucket>, } | { "kind": "by_audience_type_seed", buckets: Array<SeedBucket>, } | { "kind": "by_age_range", insight: DemographicBreakdown, } | { "kind": "by_gender", insight: DemographicBreakdown, } | { "kind": "by_income", insight: DemographicBreakdown, } | { "kind": "by_education", insight: DemographicBreakdown, } | { "kind": "by_household_has_children", insight: DemographicBreakdown, } | { "kind": "lookalike_model", model: LookalikeModelExplanation, };

export type InsightStatus = "ready" | "insufficient_data" | "unavailable";

export type DemographicBreakdown = { status: InsightStatus, buckets: Array<DemographicBucket>, };

export type DemographicBucket = {
/**
 * Exact string or boolean accepted by the corresponding audience filter.
 */
value: AudienceBucketValue, display_label: string,
/**
 * Stable zero-based order from the internal bucket catalog.
 */
order: number,
/**
 * Estimated composition percentage for this bucket.
 */
percentage: number,
/**
 * Extrapolated count across the complete matched `user_id` population.
 */
estimated_users: bigint,
/**
 * Always true for an extrapolated planner composition count.
 */
approximate: boolean, };

export type AudienceBenchmarkFreshness = "ready" | "stale" | "unavailable";

export type AudienceBenchmarkProfileKind = "all_buyers" | "production_buyers";

export type AudienceBenchmarkDimension = "age" | "gender" | "income" | "education" | "household_has_children";

export type AudienceBenchmarkDemographic = {
/**
 * One of the five intentionally public buyer-comparison dimensions.
 */
dimension: AudienceBenchmarkDimension,
/**
 * Ready data contains every exact selectable filter bucket in catalog
 * order. Insufficient/unavailable data contains no buckets.
 */
insight: DemographicBreakdown, };

export type AudienceBenchmarkProfile = { kind: AudienceBenchmarkProfileKind,
/**
 * Null for `all_buyers`; required for `production_buyers`.
 */
production_id: ProductionId | null,
/**
 * Exactly age, gender, income, education, and household-children, in that
 * order. No enrichment-coverage or upload-eligibility dimensions exist.
 */
demographics: Array<AudienceBenchmarkDemographic>, };

export type AudiencePlannerBenchmarksResponse = {
/**
 * Immutable publication identifier; null only when unavailable.
 */
snapshot_id: string | null,
/**
 * Ready through 36 hours, stale through seven days, then unavailable.
 */
freshness: AudienceBenchmarkFreshness,
/**
 * Cohort calculation timestamp; null only when unavailable.
 */
computed_at: string | null,
/**
 * Inclusive trailing buyer-window boundary; null only when unavailable.
 */
window_start: string | null,
/**
 * Upper trailing buyer-window boundary; null only when unavailable.
 */
window_end: string | null,
/**
 * Distinct buyers of any production; null only when unavailable.
 */
all_buyers: AudienceBenchmarkProfile | null,
/**
 * Requested production's buyers. Null when no production was requested,
 * it did not exist in this snapshot, or the whole snapshot is unavailable.
 */
production_buyers: AudienceBenchmarkProfile | null, };

export type ZipBucket = { zip_prefix: string, users: bigint, };

export type RecencyBucket = { band: string, users: bigint, };

export type GenreBucket = { genre: string, users: bigint, };

export type VenueBucket = { venue_id: VenueId, users: bigint, };

export type SeedBucket = { seed_audience_id: AudienceId, users: bigint, };

export type LookalikeModelExplanation = { status: InsightStatus,
/**
 * Cohort size the fit saw, in **resolved** people — those who resolved into the consumer
 * universe, not raw buyers. Identity resolution loses a substantial fraction, so the two
 * numbers differ enough that labelling them apart matters.
 */
cohort_resolved: bigint,
/**
 * Shrinkage applied, in thousandths. 1000 is unshrunk; 0 is no personalisation at all.
 */
lambda_permille: bigint, dimensions: Array<LookalikeDimensionExplanation>, suppressed_dimensions: Array<LookalikeSuppressedDimension>,
/**
 * Size at every published selectivity, so the slider is local.
 */
size_curve: Array<LookalikeCurvePoint>, selection: LookalikeSelection, caveats: Array<LookalikeCaveat>,
/**
 * When the fit was computed. A lookalike is a nightly artifact, so freshness is part of
 * reading it.
 */
computed_at: string,
/**
 * When an accelerated fit for this seed is queued but not yet published. This is the
 * signal that a non-`ready` status means "being fitted now — minutes away" rather than
 * "waits for tonight's run". Absent both when a fit is published and when nothing has
 * requested one.
 */
fit_requested_at: string | null, };

export type LookalikeDimensionExplanation = { dimension: string, status: InsightStatus,
/**
 * Empty unless `status` is `ready`.
 */
buckets: Array<LookalikeBucketWeight>,
/**
 * Sum of `|weight|` across the dimension — how much of the model's discrimination this
 * dimension supplies. This is the number the factor-contribution bars rank on, and it is
 * a divergence rather than a headcount, which is why it cannot reuse the headcount bars.
 */
total_contribution: bigint, };

export type LookalikeBucketWeight = {
/**
 * Exact value accepted by the corresponding audience filter, so the explainer can offer
 * the bucket as a filter and land the operator on the same people the model scored.
 *
 * `None` for the distance bands, and that absence is meaningful rather than a lookup
 * that failed: a band is defined relative to eight derived centres, so no `Rule::Geo`
 * expresses it. This is also why the bands live outside `AUDIENCE_BUCKET_CATALOG`,
 * every entry of which is bound to a user-selectable rule.
 */
value: AudienceBucketValue | null, display_label: string,
/**
 * Stable zero-based order from the internal bucket catalog.
 */
order: number,
/**
 * Signed, scaled and clamped. Positive pulls people in, negative pushes them out.
 */
weight: bigint,
/**
 * Share of the seed cohort in this bucket.
 */
seed_percentage: number,
/**
 * Share of the tenant baseline in this bucket.
 */
baseline_percentage: number, };

export type LookalikeSuppressedDimension = { dimension: string, reason: LookalikeSuppressionReason, };

export type LookalikeSuppressionReason = "below_privacy_floor" | "not_implemented";

export type LookalikeCurvePoint = {
/**
 * Requested share of the scored population, in basis points. Basis points rather than
 * percent because 1 % of the consumer universe is over two million people, so the useful
 * range lies below what a percent can express.
 */
share_bps: number,
/**
 * People the selection would actually contain. Larger than the request, because integer
 * scores tie and a threshold cannot cut between people who score identically.
 */
people: bigint, };

export type LookalikeSelection = { share_bps: number,
/**
 * Realised size at this point. Always `approximate` in a preview: the published histogram
 * describes the scored population at fit time, and the exact set is materialized by the
 * nightly job.
 */
people: bigint, approximate: boolean, };

export type LookalikeCaveat = "assumes_independent_dimensions" | "size_is_modelled" | "weights_shrunk_for_small_cohort" | "some_dimensions_withheld" | "size_is_before_customer_exclusion";

export type JobRunSummary = { started_at: string, finished_at: string | null, status: string | null, rows_in: bigint | null, rows_out: bigint | null, };

export type JobCursorSummary = { scope: string, cursor: string, updated_at: string, };

export type EventsPaceRow = { production_id: string, sales_last_hour: bigint, sales_prev_hour: bigint, delta_pct: number, };

export type EventsPaceResponse = { as_of: string, productions: Array<EventsPaceRow>, };

export type PixelActivityRow = { pixel_id: string, event_type: string, count_1h: bigint, count_24h: bigint, count_7d: bigint, last_event_at: string | null, };

export type PixelActivityResponse = { as_of: string, rows: Array<PixelActivityRow>, };

export type PixelSample = { web_event_id: string, event_type: string, event_at: string | null, domain: string, vars: Record<string, unknown>, };

export type PixelSamplesResponse = { as_of: string, samples: Array<PixelSample>, };

export type LogRelayBatch = { lines: Array<AppLogLine>, };

export type LogsPage = { lines: Array<AppLogLine>, next_cursor: string | null, };

export type LogComponentStat = { component: string,
/**
 * Lines stored, as stored (raw).
 */
total: bigint, errors: bigint, warns: bigint, last_ts: string | null,
/**
 * Lines the component would have written with no sampling: each sampled
 * `http_request` success line counts as its recorded `sample_rate` (the
 * configured rate for older lines without one), every other line as 1.
 * The denominator of the corrected error rate; `total` stays raw.
 */
estimated_total: bigint, };

export type LogStats = { as_of: string, window_hours: bigint, total: bigint, errors: bigint, warns: bigint, components: Array<LogComponentStat>,
/**
 * Every component's [`LogComponentStat::estimated_total`], summed.
 */
estimated_total: bigint, };

export type LogGroup = { signature: string, component: string, level: string, count: bigint, first_ts: string | null, last_ts: string | null, sample_message: string, };

export type LogGroupsPage = { as_of: string, groups: Array<LogGroup>,
/**
 * More groups matched the window than `groups` holds, which are the most
 * frequent. Absent from an older batch-worker, where it reads false.
 */
truncated: boolean, };

export type LogArchiveEntry = {
/**
 * Full R2 object key, e.g. `logs/2026/07/09/14/<uuid>.ndjson`.
 */
key: string, size: bigint, uploaded: string | null, };

export type LogArchiveResponse = { entries: Array<LogArchiveEntry>,
/**
 * True when the R2 listing was truncated (more objects exist).
 */
truncated: boolean, };

export type AlertKind = "error_spike" | "new_error" | "silence" | "job_failure" | "metric_breach";

export type AlertStatus = "active" | "acked" | "resolved";

export type AlertActorCount = { user_id: string, occurrences: bigint, };

export type AlertAffectedUser = { id: string, name: string, email: string, occurrences: bigint, };

export type AlertCodeLocation = { file: string, line: number | null, module: string | null, };

export type AlertEntityRef = { kind: string, id: string, revision: bigint | null, };

export type AlertComponentRelease = { component: string, release: string, };

export type AlertProviderDiagnostic = { provider: string, operation: string | null, status: number | null, code: string | null, subcode: string | null, request_id: string | null, };

export type AlertEvidence = { at: string | null, level: string, component: string | null, message: string, trace_id: string | null, request_id: string | null, route: string | null, http_status: number | null, error_kind: string | null, code_location: AlertCodeLocation | null, };

export type AlertDiagnostic = { environment: string | null, schema: string | null, root_cause: string | null, explanation: string | null, suggested_action: string | null, tenant_ids: Array<string>, routes: Array<string>, operations: Array<string>, error_kinds: Array<string>, http_statuses: Array<number>, max_latency_ms: bigint | null, trace_ids: Array<string>, request_ids: Array<string>, releases: Array<AlertComponentRelease>, config_revisions: Array<bigint>, code_location: AlertCodeLocation | null, entity: AlertEntityRef | null, dependency: string | null, provider: AlertProviderDiagnostic | null, retryable: boolean | null, terminal: boolean | null, attempt: bigint | null, max_attempts: bigint | null,
/**
 * Distinct affected actors observed in the bounded incident evidence,
 * including any beyond the smaller `actors` identity sample retained here.
 */
affected_user_count: bigint, actors: Array<AlertActorCount>, evidence: Array<AlertEvidence>, };

export type Alert = { id: string, created_at: string, updated_at: string, kind: AlertKind,
/**
 * `error` | `warn` | `critical`.
 */
severity: string, component: string | null, signature: string | null, title: string, detail: string, count: bigint, first_seen: string, last_seen: string, status: AlertStatus, acked_by: string | null, acked_at: string | null,
/**
 * CSV of channels the alert was delivered to (e.g. `webhook,email`).
 */
delivered: string | null,
/**
 * The `alert_rules.id` that produced this incident (`None` for legacy rows).
 */
rule_id: string | null, rule_name: string | null, category: AlertCategory | null,
/**
 * The entity the incident is about (production_id / campaign_id) — powers
 * group-by-production in the UI.
 */
scope_ref: string | null, production_id: string | null, resolved_at: string | null, resolved_by: string | null,
/**
 * Sanitized, PII-free evidence persisted with the incident when available.
 * The API returns it only to callers with full Logs access.
 */
diagnostic: AlertDiagnostic | null,
/**
 * D1-resolved identities. APIs must leave this empty unless the caller has
 * full Logs access; batch-worker may populate it immediately before email.
 */
affected_users: Array<AlertAffectedUser>, };

export type AlertStatusCounts = { active: bigint, acked: bigint, resolved: bigint, };

export type AlertsResponse = { alerts: Array<Alert>,
/**
 * Count of `active` (unacknowledged) alerts — drives the nav badge.
 */
active_count: bigint,
/**
 * Exact counts per status over the whole table, for the KPI strip.
 */
status_counts: AlertStatusCounts,
/**
 * Set when older resolved alerts exist beyond `alerts`: pass it back as
 * `resolved_before` to read the next page.
 */
resolved_cursor: string | null, };

export type AlertCategory = "system" | "backfill_quality" | "pixel" | "campaign_pacing" | "roas";

export type AlertScope = { "level": "global" } | { "level": "production", production_id: ProductionId | null, } | { "level": "campaign", campaign_id: CampaignId | null, } | { "level": "pixel", pixel_id: PixelId | null, };

export type Comparator = "lt" | "lte" | "gt" | "gte" | "eq" | "ne";

export type AlertMetric = "anonymous_rate" | "missing_email_rate" | "person_resolved_rate" | "null_venue_rate" | "null_event_rate" | "refund_rate" | "missing_value_rate" | "missing_currency_rate" | "missing_seat_rate" | "ticket_volume_drop_pct" | "correction_count" | "source_lag_hours" | "ingest_run_failed" | "backfill_age_hours" | "pixel_event_count" | "pixel_purchase_count" | "pixel_pageview_count" | "pixel_event_volume_drop_pct" | "pixel_purchase_missing_value_rate" | "pixel_purchase_missing_currency_rate" | "pixel_identity_resolved_rate" | "roas" | "pace_index" | "spend_cents" | "avg_capacity" | "capi_failure_quarantine_rate" | "capi_delivery_halted_pct" | "capi_ticket_cursor_lag_hours" | "capi_oldest_pending_secs" | "error_rate_pct";

export type MetricSource = "production_stats" | "ingest_corrections" | "ingest_runs" | "web_events" | "reporting" | "events" | "conversions" | "logs";

export type MetricDirection = "higher_is_better" | "lower_is_better";

export type StatusBand = "ok" | "warn" | "critical" | "unknown";

export type MetricMeta = { source: MetricSource,
/**
 * `pct` | `count` | `hours` | `secs` | `ratio` | `cents` | `bool`.
 */
unit: string, supports_production_scope: boolean,
/**
 * Whether this metric is evaluable today (false ⇒ "coming soon").
 */
available: boolean,
/**
 * Which way is "good" — the ONE home for higher- vs lower-is-better.
 */
direction: MetricDirection, default_min_tickets: bigint, };

export type MetricWindow = { "kind": "latest_complete_day" } | { "kind": "rolling_days", n: number, } | { "kind": "rolling_hours", n: number, } | { "kind": "aggregate30d" };

export type MetricGuards = { min_tickets: bigint | null, min_spend_cents: bigint | null, min_impressions: bigint | null, };

export type AlertTrigger = { "type": "error_spike", component: string | null, signature: string | null, min_count: bigint, window_minutes: bigint, } | { "type": "new_error", component: string | null, window_minutes: bigint, } | { "type": "silence", component: string | null, silence_minutes: bigint, } | { "type": "job_failure", job: string | null, } | { "type": "metric", metric: AlertMetric, op: Comparator, threshold: number, window: MetricWindow, guards: MetricGuards, } | { "type": "system_metric", metric: AlertMetric, warn: number, critical: number, window: MetricWindow, guards: MetricGuards, };

export type AlertSeverity = "warn" | "error" | "critical";

export type AlertActivationMode = "off" | "always" | "campaign_running";

export type ChannelKind = "webhook" | "email";

export type NotificationChannel = { id: string, name: string, kind: ChannelKind,
/**
 * Slack/Discord webhook URL. Nulled in list responses; set via
 * create/update. `configured` reflects whether a URL is stored.
 */
webhook_url: string | null, recipients: Array<string>, configured: boolean, created_at: string, updated_at: string, };

export type AlertRule = { id: string, name: string,
/**
 * Compatibility projection of `activation_mode != off`.
 */
enabled: boolean, activation_mode: AlertActivationMode, category: AlertCategory, scope: AlertScope, trigger: AlertTrigger, severity: AlertSeverity,
/**
 * Ids into `notification_channels`. Empty ⇒ in-app only.
 */
channel_ids: Array<string>,
/**
 * Don't re-deliver the same incident within this many minutes.
 */
cooldown_minutes: bigint,
/**
 * Auto-flip active→resolved when the condition clears.
 */
auto_resolve: boolean,
/**
 * A locked system row: cannot be deleted or reshaped. Its activation can
 * be changed through the narrow activation endpoint; system-metric rows
 * additionally expose the narrow threshold editor.
 */
protected: boolean,
/**
 * The metric-status snapshot key this row drives: a
 * `shared::system_metrics` catalog key for system-metric rows, or a
 * batch-worker health key (e.g. `reporting_run_age_hours`) for the
 * protected reporting dead-men. `None` for custom rules, log detectors,
 * and the heartbeat.
 */
metric_key: string | null, owner_id: string | null, created_at: string, updated_at: string, last_evaluated_at: string | null, last_triggered_at: string | null, };

export type AlertMetricCatalogEntry = { metric: AlertMetric, label: string, category: AlertCategory, unit: string, supports_production_scope: boolean,
/**
 * Server-computed `campaign_running` capability per scope — the exact
 * predicate `AlertTrigger::supports_campaign_running_scope` enforces, so
 * the editor never re-derives (and never drifts from) the validator.
 */
supports_campaign_running_production: boolean, supports_campaign_running_pixel: boolean, available: boolean, direction: MetricDirection, default_op: Comparator, default_threshold: number, default_window: MetricWindow, default_min_tickets: bigint,
/**
 * Whether the editor should require a denominator guard for this metric.
 */
rate_guard: boolean,
/**
 * What this metric measures — one sentence (from [`metric_explainer`]).
 */
summary: string,
/**
 * How the number is computed: source, formula, window, guards.
 */
calculation: string,
/**
 * A worked numeric example.
 */
example: string, };

export type AlertEditorDefaults = { cooldown_minutes: bigint, error_min_count: bigint, error_window_minutes: bigint, silence_minutes: bigint, };

export type AlertRulesResponse = { rules: Array<AlertRule>, enabled_count: bigint, metric_catalog: Array<AlertMetricCatalogEntry>, available_categories: Array<AlertCategory>, editor_defaults: AlertEditorDefaults,
/**
 * rule id → its explainer (what it watches, how it is computed, example).
 */
explainers: { [key in string]?: RuleExplainer }, };

export type NotificationChannelsResponse = { channels: Array<NotificationChannel>, };

export type AlertRuleUpsert = { name: string, enabled: boolean,
/**
 * Optional for backward compatibility. On create, an omitted mode is
 * derived from `enabled`; on update, `enabled=true` preserves an existing
 * `campaign_running` mode and otherwise means `always`.
 */
activation_mode?: AlertActivationMode, category: AlertCategory, scope: AlertScope, trigger: AlertTrigger, severity: AlertSeverity, channel_ids: Array<string>, cooldown_minutes: bigint, auto_resolve: boolean, };

export type AlertRuleActivationUpdate = { activation_mode: AlertActivationMode, };

export type RuleExplainer = {
/**
 * One plain-English sentence: what this rule watches.
 */
summary: string,
/**
 * How the number is computed: source, formula, window, guards.
 */
calculation: string,
/**
 * A worked numeric example, ending in the band outcome.
 */
example: string,
/**
 * Real direction, so the UI never infers it from threshold ordering.
 */
direction?: MetricDirection,
/**
 * Screen where the underlying data lives (the system catalog deep link).
 */
deep_link?: string, };

export type NotificationChannelUpsert = { name: string, kind: ChannelKind, webhook_url: string | null, recipients: Array<string>, };

export type HealthStatus = "up" | "degraded" | "down" | "quiet";

export type ComponentHealth = {
/**
 * Stable id: `batch_worker | data_stores | config | ingest | audience_benchmarks |
 * api_worker | pixel_worker | frontend | pixel_flow | meta_credential |
 * ttd_credential | xero_credential`. `data_stores` is PostgreSQL, Trino and the lake as
 * batch-worker's prober last measured them, read from its heartbeat.
 */
component: string, status: HealthStatus,
/**
 * Most recent positive signal (heartbeat / ingest run / activity), if any.
 */
last_seen: string | null,
/**
 * Recent error count in the observation window (0 for liveness-only rows).
 */
errors: bigint,
/**
 * Short human-readable summary, e.g. "last beat 12s ago" or "3 errors/1h".
 */
detail: string, };

export type HealthSummary = { as_of: string, components: Array<ComponentHealth>,
/**
 * True iff every component is `Up` or `Quiet`.
 */
ok: boolean, };

export type MetricScopeStatus = {
/**
 * The entity id (production_id / pixel_id / …).
 */
scope_ref: string,
/**
 * Display label (falls back to the id when no nicer name is known).
 */
label: string, value: number, band: StatusBand,
/**
 * Deep-link to that specific entity's screen.
 */
link: string, };

export type SystemMetricStatus = {
/**
 * Stable catalog key (`shared::system_metrics`).
 */
key: string, label: string,
/**
 * `pct` | `count` | `hours` | `secs` | `ratio` | `cents` | `bool`.
 */
unit: string, direction: MetricDirection,
/**
 * The rollup value (portfolio aggregate for per-entity metrics). `None` ⇒
 * no data / stale ⇒ band is never `Ok`.
 */
value: number | null, warn: number, critical: number,
/**
 * SERVER-computed band. `Unknown` when stale — never silently green.
 */
band: StatusBand,
/**
 * Deep-link to the owning screen.
 */
link: string,
/**
 * Offending entities (per-production / per-pixel), empty for global metrics
 * and for a healthy aggregate.
 */
breaching: Array<MetricScopeStatus>, detail: string | null,
/**
 * When the snapshot row was last written by the evaluator.
 */
updated_at: string,
/**
 * True when the snapshot is older than `METRIC_STATUS_STALE_SECS` — the band
 * is forced to `Unknown` and callers should show "stale", not green.
 */
stale: boolean, };

export type SystemHealthResponse = { as_of: string, metrics: Array<SystemMetricStatus>,
/**
 * Worst band across all metrics — drives the pill color in one read.
 */
overall: StatusBand, };

export type FailureCode = "meta_confirm_slow" | "audience_converge_blocked" | "provider_rate_limited" | "provider_rejected" | "provider_readback_mismatch" | "provider_credential" | "provider_unavailable" | "provider_timeout" | "not_delivering_yet" | "creative_not_ready" | "audience_not_ready" | "creative_asset_missing" | "creative_readback_mismatch" | "previews_incomplete" | "creative_audit_rejected" | "audience_too_small" | "audience_too_large_for_ttd" | "audience_too_large_for_meta" | "audience_matches_nobody" | "audience_upload_rejected" | "audience_empty_checking" | "audience_empty_held" | "audience_empty_pausing" | "audience_empty_nothing_live" | "audience_empty_resuming" | "audience_empty_ttd_waiting" | "platform_blip" | "sam_paused_live" | "sam_paused_live_capped" | "invoice_sent_flag_pending" | "invoice_send_unknown" | "invoice_email_refused" | "invoice_setup_missing" | "invoice_original_not_payable" | "invoice_interrupted" | "invoice_review_again" | "invoice_not_settled" | "invoice_xero_conflict" | "delivery_check_no_access" | "delivery_history_expired" | "delivery_check_not_configured" | "delivery_check_unavailable" | "flight_extension_not_saved" | "unknown";

export type FailureRetry = "automatic" | "button" | "none";

export type FailureView = { code: FailureCode, headline: string, body: string, next_step: string, retry: FailureRetry,
/**
 * "Meta" or "The Trade Desk" when the failure names one.
 */
provider: string | null,
/**
 * The provider's own sentence, re-sanitized and bounded, when it gave one.
 */
provider_said: string | null,
/**
 * Several provider messages, each re-sanitized and bounded, when the provider
 * gave a list (Xero's `ValidationErrors`); shown one per line instead of
 * `provider_said`.
 */
provider_messages?: Array<string>,
/**
 * The stored text, redacted, URL-free and bounded: what support needs.
 */
technical: string, };

export type AudienceSyncStatus = { audience_id: AudienceId, status: AudienceSyncState,
/**
 * Raw revision-scoped status for each destination. These make the
 * blocking campaign wizard explicit about which platform is still
 * working or failed without requiring it to infer state from detail
 * payloads that are absent during early phases.
 */
meta_status: string | null, ttd_status: string | null, meta: MetaAudienceSync | null, ttd: TtdAudienceSync | null, last_error: string | null,
/**
 * `last_error` in words (`crate::failure_copy`), built at read time.
 */
failure?: FailureView | null, updated_at: string, next_refresh_at: string | null, };

export type AudienceSyncState = "pending" | "creating_at_platforms" | "awaiting_pii" | "uploading_pii_meta" | "uploading_pii_ttd" | "polling_meta" | "polling_ttd" | "binding_ttd" | "synced" | "failed";

export type MetaAudienceSync = { audience_id: string, session_id: string | null, num_received: bigint, num_matched: bigint, num_invalid_entries: bigint, stage: string | null, progress: number, last_polled_at: string | null, step_progress: StepProgress | null, count_floor_since: string | null, prospect_wire_rows: bigint | null, recent_buyer_wire_rows: bigint | null, recent_buyers_revision: bigint | null, recent_buyers_notice?: RecentBuyersNotice | null, };

export type TtdAudienceSync = { crm_data_id: string, first_party_data_id: number, drop_reference_id: string | null, drop_state: TtdDropState | null,
/**
 * TTD's `TotalRecords` for the drop — one record per UID2 token LINE, not
 * per person, because a person can carry several tokens. Never compare it
 * to a person count; that is what `people_with_token` is for.
 */
record_count: bigint,
/**
 * People the prospect/customer selection held, before token coverage.
 * The denominator the other two are read against.
 *
 * These three replace the `matched_count` / `match_rate` pair removed in
 * schema v13. Those were never written by anything:
 * `CrmData_CrmDataDropProcessingStats` carries no match counter of any
 * kind, so both read `0` / `0.0` on every audience that ever synced, and
 * the audience drawer rendered a permanent "0 matched". A field that can
 * only ever report a failure it cannot observe is worse than no field.
 */
people_selected: bigint,
/**
 * People who held a minted UID2 and therefore reached TTD.
 */
people_with_token: bigint,
/**
 * People dropped from the body for having no minted UID2 — the
 * `skipped_no_uid2` counter, which until v13 existed only in one log line.
 * `people_with_token + people_without_token == people_selected`.
 */
people_without_token: bigint, datagroup_id: string | null, ttd_audience_id: string | null, datagroup_create_started_at: string | null, audience_create_started_at: string | null, last_polled_at: string | null, step_progress: StepProgress | null, recent_buyer_people_selected: bigint | null, recent_buyer_people_with_token: bigint | null, recent_buyer_people_without_token: bigint | null, recent_buyer_uid2_lines: bigint | null, recent_buyers_revision: bigint | null, recent_buyers_notice?: RecentBuyersNotice | null, };

export type TtdDropState = "Unknown" | "Created" | "PiiUploaded" | "Activated" | "Processing" | "Succeeded" | "Cancelled" | "Failed" | "Deactivated";

export type TtdPiiType = "Email" | "EmailHashedUnifiedId2" | "Phone" | "PhoneHashed" | "Uid2";

export type TtdMergeMode = "Add" | "Replace";

export type TtdRegion = "US" | "EU" | "APAC";

export type StepProgress = { rows_done: bigint, rows_total: bigint | null, fraction: number, eta_seconds: bigint | null, label: string | null, };

export type RecentBuyersNotice = { kind: RecentBuyersNoticeKind, since_ms: bigint, };

export type RecentBuyersNoticeKind = "held_companion_unavailable" | "held_writes_paused" | "waiting_for_delivery_ack" | "delivery_ack_overdue";

export type UploadInitRequest = { filename: string, mime: string, byte_size: bigint, };

export type UploadInitResponse = { asset_id: AssetId, upload_id: string, r2_key: string, part_size: bigint, part_count: number, expires_at: string, };

export type UploadPart = { part_number: number, etag: string, };

export type UploadPartResponse = { etag: string, };

export type UploadCompleteRequest = { asset_id: AssetId, parts: Array<UploadPart>, };

export type UploadCompleteResponse = { asset_id: AssetId, r2_key: string, byte_size: bigint, status: AssetStatus, };

export type UploadAbortRequest = { asset_id: AssetId, };

export type AssetStatus = "uploading" | "uploaded" | "aborted" | "failed";

export type ProviderCreativeVariant = { placement_tag: string, provider_creative_id: string, };

export type MetaCreativeAssetSync = { placement_tag: string, asset_id: string, upload_session_id: string | null,
/**
 * Meta image hash or permanent video id.
 */
provider_asset_id: string | null, start_offset: bigint, end_offset: bigint | null, finished: boolean, last_chunk_at: string | null, };

export type MetaCreativeSync = { phase: CreativeSyncPhase, upload_session_id: string | null, video_id: string | null, start_offset: bigint, end_offset: bigint | null, adcreative_id: string | null, last_chunk_at: string | null,
/**
 * Durable upload state for every `meta_sq` / `meta_fs` / `meta_story`
 * asset. Added with a default so already-persisted singular journals
 * remain readable and can be migrated by the queue consumer.
 */
asset_uploads: Array<MetaCreativeAssetSync>,
/**
 * Absent on older journals: finished videos must be checked, not uploaded
 * again. Never sent to the browser; it includes internal provider ids.
 */
video_processing: MetaVideoProcessing | null,
/**
 * The media ids Meta actually attached, per placement label.
 *
 * Observational only: the drift sweep does not currently read this field.
 * These are provider echo ids, not uploaded ids, and do not prove media
 * byte identity. Enforcing a same-AdCreative-id baseline needs a live
 * stability measurement first; legacy empty journals cannot prove earlier
 * identity. The owner-only canary records repeated echoes for that review.
 */
provider_media: Array<ProviderCreativeVariant>,
/**
 * The provider object's name **as minted**, never recomputed.
 *
 * Adoption by name and every readback compare against this, so a change
 * to the name format can never orphan an object already created — the
 * rule `platform_ads.provider_ad_name` already follows. `None` for a
 * journal written before the field existed; the caller then computes the
 * current name, which is what it did before.
 */
provider_name: string | null, variants: Array<ProviderCreativeVariant>, };

export type MetaVideoProcessing = { started_at_ms: bigint, deadline_at_ms: bigint, next_poll_at_ms: bigint | null, video_ids: Array<string>, ready_video_ids: Array<string>, poll_count: number, terminal_reason: string | null, };

export type TtdCreativeSync = { phase: TtdCreativePhase, upload_url: string | null, upload_url_expires_at: string | null,
/**
 * Exact `VideoContentType` returned beside TTD's signed upload URL.
 */
upload_content_type: string | null, start_offset: bigint,
/**
 * Opaque blob TTD returns alongside the upload URL; passed back on
 * `/v3/creative` so TTD can correlate the upload with the create call.
 */
upload_metadata: Record<string, unknown> | null, creative_id_external: string | null, processing_state: string | null, last_polled_at: string | null,
/**
 * Durable bound for provider audit polling. These fields survive Queue
 * retries so a perpetually pending TTD creative cannot churn forever.
 */
audit_started_at: string | null, audit_poll_attempts: number,
/**
 * The provider object's name **as minted**, never recomputed.
 *
 * Adoption by name and every readback compare against this, so a change
 * to the name format can never orphan an object already created — the
 * rule `platform_ads.provider_ad_name` already follows. `None` for a
 * journal written before the field existed; the caller then computes the
 * current name, which is what it did before.
 */
provider_name: string | null, variants: Array<ProviderCreativeVariant>, };

export type CreativeSyncPhase = "start" | "transfer" | "finish" | "done";

export type TtdCreativePhase = "init" | "upload" | "create" | "poll" | "done";

export type Capability = "campaigns_view" | "campaigns_edit" | "campaigns_launch" | "audiences_view" | "audiences_edit" | "creatives_view" | "creatives_edit" | "reports_view" | "reports_export" | "backend_view" | "backend_edit" | "pixels_view" | "pixels_manage" | "pixel_samples_view" | "integrations_manage" | "users_manage" | "roles_manage" | "logs_view" | "audit_view" | "tokens_issue" | "tokens_revoke" | "pii_reveal" | "config_manage" | "billing_manage";

export type User = { id: UserId, name: string, email: string, initials: string, role: Role, status: UserStatus, productions: Array<ProductionId> | null, last_seen: string | null, invited_at: string | null, invited_by: string | null, twofa: boolean, sso: boolean, };

export type AuditActor = { id: UserId, name: string, email: string, initials: string, };

export type AuditTarget = { kind: string, id: string, name: string, };

export type AuditDiff = { field: string, before: string, after: string, };

export type AuditVia = { kind: CredentialKind,
/**
 * The key's name, or the OAuth client's name for a grant.
 */
name: string, };

export type AuditEvent = { id: string, ts: string, actor: AuditActor, action: string, verb: string, target: AuditTarget, diff: Array<AuditDiff> | null, ip: string, user_agent: string,
/**
 * The credential the action came through; `None` for the browser, a queue message or
 * a cron tick.
 */
via: AuditVia | null, };

export type AppLogLine = { ts: string, level: LogLevel, source: LogSource, message: string, request_id: string | null,
/**
 * Truncated stack / error detail (redacted).
 */
stack_stub: string | null,
/**
 * Deployable component that emitted this event.
 */
component: Component | null,
/**
 * Cross-component correlation id (minted in the browser, propagated via
 * `X-Request-Id` through the API → batch-worker).
 */
trace_id: string | null,
/**
 * HTTP route / logical operation.
 */
route: string | null, http_status: number | null, latency_ms: bigint | null,
/**
 * Actor id only — never an email/name.
 */
user_id: string | null, tenant_id: string | null,
/**
 * Build/release id. The API (as a Worker) stamped its Cloudflare deployment version
 * id; the API, batch-worker and the frontend now stamp a short git SHA. Never a crate
 * version — `0.1.0` on every line for a week is what this doc used to
 * describe, and it made builds indistinguishable.
 */
release: string | null, env: string | null,
/**
 * Short machine tag for the error class (e.g. `d1_error`, `panic`).
 */
error_kind: string | null,
/**
 * Bounded, already-redacted JSON context (serialized string).
 */
context_json: string | null,
/**
 * Storage-side grouping signature over component, level, error kind,
 * normalized route/operation, and message template.
 */
signature: string | null, };

export type ManagedEdgeConfig = {
/**
 * Lets the campaign lane create and mutate real, spending campaigns at Meta
 * and TTD.
 */
campaign_platform_writes_enabled: boolean,
/**
 * Lets the creative lane mint real provider AdCreative objects. Meta preview
 * staging is unconditional and deliberately has no switch.
 */
creative_platform_writes_enabled: boolean,
/**
 * Lets the audience lane create real provider audience objects — at Meta, a
 * custom audience and the `/users` upload that fills it; at TTD, a CRM data
 * segment and the PII drop that fills it.
 *
 * This switch is younger than the lane it guards. Until schema 7 the
 * audience lane had none: `provider_dispatch_guard` named Campaign and
 * Creative and let Audience fall through to `Ok(())`, so every audience
 * create, PATCH, restore and refresh reached the provider with both other
 * switches off.
 *
 * It is checked in three places, because the two halves of the lane run in
 * different processes and only the second one uploads PII: the API's
 * queue consumer before the provider container is created, then in
 * batch-worker inside `run_ttd_target` immediately before the drop, and
 * inside `run_meta_target`'s upload branch immediately before the session
 * opens.
 *
 * That third arm was missing until the lookalike work traced it: the switch
 * gated only TTD — the provider that is entitlement-blocked anyway — while
 * Meta, the one that actually uploads, ignored it. Both provider checks sit
 * inside their upload branch rather than at the top of the handler, so
 * pausing uploads does not strand a session that is already matching
 * provider-side.
 *
 * On TTD it also covers membership, not only uploads: a DataGroup's
 * first-party elements and an Audience's DataGroups are each converged by a
 * PUT to a live targeting object, and with the switch off a set that
 * differs is left as read and the leg retries once writes resume. That
 * includes putting the recent-buyers companion back beside a lookalike,
 * whose restoration stays owed meanwhile.
 */
audience_platform_writes_enabled: boolean,
/**
 * Lets a **prospect** audience upload raw PII for people who are not
 * customers.
 *
 * Separate from [`Self::audience_platform_writes_enabled`] because it guards
 * a different disclosure, and the wider switch being on must never imply
 * this one. A customer upload sends digests the tenant already held; a
 * prospect upload sends a third party's raw email, phone, name and postal
 * code for someone who has never transacted with the tenant. Legal cleared
 * that path specifically, so it gets its own control that can be closed
 * without pausing ordinary audience refreshes.
 *
 * Checked in the two batch-worker legs that upload — inside
 * `run_meta_target`'s upload branch and at the top of `run_ttd_target`'s —
 * and deliberately **not** in the API's `provider_dispatch_guard`.
 * That guard runs before the provider container exists, and creating an
 * empty container carries no PII; blocking there would leave a prospect
 * audience unable to provision, which reads as a broken feature rather than
 * a paused one. It also cannot distinguish a prospect audience: it is keyed
 * only on `SyncEntityKind`/`SyncTarget`.
 */
prospect_audience_uploads_enabled: boolean,
/**
 * Expands every uploaded audience member to their full household at
 * upload time — all person_ids sharing the member's consumer-universe
 * `household_id`, capped per household — and uploads the household
 * members' PII to both providers alongside the member's own.
 *
 * Size estimates, the stored audience Size, and lookalike fitting all
 * stay person-grain; only the upload grows (~2.7× measured,
 * PROVISIONING §13b). Household members have no tenant `users` row, so
 * their PII comes from the third-party consumer universe and their UID2
 * tokens from the `uid2_prospect` mint lane — a prospect-grade
 * disclosure even on a customer audience, which is why this is its own
 * switch and off by default. Read only by batch-worker's upload legs,
 * like the prospect switch above.
 */
audience_household_expansion_enabled: boolean,
/**
 * Delivers recent buyers to every campaign that targets a lookalike
 * audience, in one of two ways.
 *
 * **Beside the lookalike (the companion).** The buyers are uploaded into
 * the managed `sam_recent_buyers` audience's own provider containers,
 * declared `USER_PROVIDED_ONLY` because that is what they are, and
 * delivery attaches that container next to the lookalike's: a second
 * custom audience on each Meta ad set, a second first-party element in the
 * TTD DataGroup. This is what the switch does on its own.
 *
 * **Inside the lookalike (embedded).** For a lookalike named in
 * `lookalike_recent_buyers_embed_audience_ids`, the same buyers also ride
 * the lookalike's own Replace, streamed after its prospects and their
 * households and committed with them in one upload. The embedded buyers
 * are never household-expanded (owner, 2026-10-02). Once the
 * lookalike's container provably carries them, settled for
 * `RECENT_BUYER_EMBED_SETTLE_HOURS` (skipped when they arrived with its
 * first fill) and, on TTD, ingested whether first fill or not, delivery
 * stops attaching the companion beside it and targets the one audience —
 * for as long as its last carrying Replace is younger than
 * `RECENT_BUYER_EMBED_MAX_AGE_HOURS`, so a lookalike whose refresh stops
 * completing gets the companion back.
 * On Meta that needs a container declared `USER_PROVIDED_ONLY` — which
 * every Meta container minted since 2026-10-02 is (Meta and legal) — or
 * the legacy `BOTH_USER_AND_PARTNER_PROVIDED`. A legacy
 * `PARTNER_PROVIDED_ONLY` container keeps uploading prospects only,
 * because tenant rows inside it would be a false attestation, and no path
 * ever changes a container's source; the `remint_meta_container`
 * remediation replaces it with a fresh USER container instead. Until
 * then the companion keeps delivering the buyers.
 *
 * Neither mode weakens the prospect lane. Prospects are still anti-joined
 * against the tenant's own people and customer contact digests, and no
 * un-excluded prospect row is reachable; the buyers are a separate stream
 * from the customer lane and never pass through the prospect producer.
 *
 * The companion is governed by `audience_platform_writes_enabled`, not by
 * `prospect_audience_uploads_enabled`: it uploads digests the tenant
 * already held, the ordinary customer disclosure. Embedding needs both,
 * because the buyers then ride a prospect upload. Off by default like
 * every switch here, because turning it on changes who a live campaign
 * reaches. Turning it off is the global stop for both modes: the
 * companion is detached, and each lookalike's next Replace goes out
 * without buyers.
 */
lookalike_recent_buyers_enabled: boolean,
/**
 * The trailing purchase window for the recent buyers, in days, in both
 * delivery modes.
 *
 * Compiles to `Rule::Recency { within_days }`, which is already exactly
 * "bought any production in this window" — a non-refund ticket with a
 * tenant user identity. The companion audience holds that definition, and
 * every embedded copy is compiled from the companion's own definition, so
 * the two modes can never disagree about who a recent buyer is. `0`
 * disables both as surely as the switch above; both are checked, because
 * a window of zero days is not a statement anyone means.
 */
lookalike_recent_buyer_days: number,
/**
 * The lookalike audiences whose own provider containers also carry the
 * recent buyers (the embedded mode described on
 * `lookalike_recent_buyers_enabled`).
 *
 * Empty, the default, embeds nothing: every lookalike delivers exactly as
 * it did before this field existed. Entries are audience ids, each listed
 * once, or exactly `["*"]` for every lookalike. The recent-buyers audience
 * itself is never embedded, and listing it is refused.
 *
 * A listed lookalike embeds only while `lookalike_recent_buyers_enabled`
 * is on, `lookalike_recent_buyer_days` is above zero, and both
 * `prospect_audience_uploads_enabled` and
 * `audience_platform_writes_enabled` are on. On Meta its container must
 * also be declared `USER_PROVIDED_ONLY` — every Meta container minted
 * since 2026-10-02 is — or the legacy `BOTH_USER_AND_PARTNER_PROVIDED`,
 * and Graph must echo the bound declaration on the tick that uploads;
 * this list never changes a declaration, and nothing else does either. A first fill that carried the buyers skips the settle but,
 * on TTD, not the wait for `IngestedAt`: the companion stays until TTD has
 * ingested the drop that began the carry. Removing an entry is the
 * per-lookalike rollback: the companion is restored beside it before a
 * Replace without buyers goes out.
 */
lookalike_recent_buyers_embed_audience_ids: Array<string>,
/**
 * Lets the managed custom-conversion reconciler apply changes at Meta.
 */
meta_custom_conversion_writes_enabled: boolean,
/**
 * Lets the provider drift sweep **repair** what it finds, instead of only
 * recording it.
 *
 * Detection is deliberately not gated: it performs no write, and a lane
 * held for a reason is exactly when you want to know the provider moved
 * underneath you. Only the repair half is switched, and it fails closed —
 * an unreadable managed config leaves a live campaign exactly as the
 * provider has it.
 *
 * Turning this on makes D1 unconditionally authoritative, which means the
 * provider UI becomes effectively read-only: an edit made in Ads Manager or
 * the TTD console is reverted within one scan interval. That is the
 * intended behaviour, and it is why the drift rows carry both values and
 * why a recurring divergence escalates — a trader whose change vanishes
 * needs to be able to find out why.
 */
provider_drift_remediation_enabled: boolean,
/**
 * Lets an operator extend a live or paused **Meta** campaign with a new
 * flight (campaign lifecycle 2b, 2026-10-21). The route commits the flight
 * synchronously and the next reconcile writes Meta's cumulative budget and
 * new end on the flight's first day. Off by default until the sandbox step
 * measures `update_ad_set_flight` on an ad set whose end has passed; also
 * needs `campaign_platform_writes_enabled`.
 */
campaign_extend_meta_enabled: boolean,
/**
 * Lets an operator extend a live or paused **TTD** campaign with a new
 * flight: the handler holds the campaign's ad groups off, creates the flight
 * at TTD, rewrites every flight's allocations and commits. Off by default
 * until the owner's canary measures appending after an expired flight; also
 * needs `campaign_platform_writes_enabled`.
 */
campaign_extend_ttd_enabled: boolean,
/**
 * Lets people create API keys (`sam_pat_…`, Account → API keys) and lets those keys
 * call the API. Off, creating one is refused and every key's request answers 503;
 * nothing is revoked, so switching it back on restores every key that was live.
 */
api_keys_enabled: boolean,
/**
 * Lets Claude and ChatGPT reach SMART through its MCP connector (a custom connector in
 * either app, OAuth sign-in). Off, the connector is refused; no grant is revoked.
 */
mcp_enabled: boolean,
/**
 * Lets an API key or Claude holding the Prepare scope create new audiences (SMART
 * starts uploading them to Meta and The Trade Desk; nothing is spent) and submit
 * creatives for review. Off, such a credential acts as Write (drafts only); nothing is
 * revoked, and work already queued runs on.
 */
credential_prepare_enabled: boolean,
/**
 * Lets an API key or Claude holding the Admin scope (an Admin with no production scope)
 * administer: config, alerts, remediations, invoices and users (Gio, 2026-10-30). Needs
 * `credential_prepare_enabled` too. Off, such a credential acts as Prepare; nothing is
 * revoked. A credential can never change this switch or the three above.
 */
credential_admin_enabled: boolean,
/**
 * Recipients for the dead-man's-switch liveness alert — the one alarm
 * batch-worker structurally cannot raise, because it fires when
 * batch-worker is the thing that died.
 *
 * Deliberately separate from `alerts.email_to`, which batch's spike scan
 * uses. Same inbox in practice; different producer, and coupling them would
 * mean editing batch's alert recipients silently repoints the liveness
 * alarm too.
 */
alert_email_to: Array<string>, };

export type ManagedEncoreproPostgresConfig = { host: string, port: number, database: string, user: string, sslmode: string, pool_size: number, rate_limit_rps: number, schema: string | null, tenant_id: string, connect_retry_interval_secs: bigint, connect_timeout_secs: bigint, connect_max_attempts: number, };

export type ManagedLogTriageEnvironment = { name: string, schema: string, production: boolean, };

export type ManagedLogTriageConfig = { lookback_hours: number, max_lines_per_environment: number, max_groups: number, samples_per_group: number, trace_lines_per_group: number, output_dir: string, environments: Array<ManagedLogTriageEnvironment>, };

export type ManagedPixelIngestConfig = { flush_max_rows: number, flush_max_bytes: number, flush_interval_secs: bigint, channel_capacity: number, resolve_identity: boolean, };

export type ManagedMetaConfig = { ad_account_id: string, graph_api_base: string,
/**
 * Facebook Page identity for provider-created story specs. Required before
 * Meta creative publishing; `queue/creative_sync.rs` fails the job closed
 * with a named error when it is empty. Required on the wire — see `edge`.
 */
page_id: string,
/**
 * Page-linked Instagram identity, required by manual Instagram placements.
 */
instagram_user_id: string,
/**
 * The one purchase Pixel this deployment uses.
 *
 * Both campaign optimization (the API's `promoted_object` and managed
 * custom conversions) and box-office ticket delivery (batch-worker's ticket
 * lane) must target the **same** Pixel — conversions may only be sent to the
 * Pixel the creatives track against. This was previously two settings that
 * happened to agree: a Worker var and `conversions.tickets.meta_pixel_id`.
 * It lives on `[meta]` rather than under `conversions` because that path is
 * triple-optional, so disabling the batch conversions lane would otherwise
 * break campaign creation — an unrelated switch.
 */
purchase_pixel_id: string,
/**
 * The Business Manager that owns `purchase_pixel_id` and the ad account.
 * Managed custom conversions are created on the AD ACCOUNT's own edge
 * (visible and bindable from birth — no manual share exists in the public
 * API); this id only disqualifies foreign objects whose readback names a
 * different owning business. Empty skips that check. The system user
 * should still hold an **Admin** role on the business — Employee fails
 * conversion writes with Graph code 10 `MANAGE_ADS_CONVERSIONS_AUDIT_NEEDED`.
 */
business_id: string,
/**
 * The Facebook App the access token and `MetaAppSecret` belong to. The
 * API's cron introspects the token with the app access token
 * `{app_id}|{app_secret}` (`/debug_token` + `/me`) and reports the
 * observed app, system user, scopes, and validity as the "Meta credential"
 * System Health tile — a rotated token paired with the wrong app fails
 * there within 15 minutes instead of as cryptic Graph errors. Empty means
 * the check reports unconfigured.
 */
app_id: string, };

export type ManagedTtdConfig = {
/**
 * The API root, **without** a version segment — `https://api.thetradedesk.com`.
 * Both workers append `/v3/…` themselves. The API (then a Worker) used to carry its own
 * copy that included `/v3`, so the two forms were not interchangeable
 * despite the matching field name.
 */
base: string, advertiser_id: string, region: TtdRegion,
/**
 * The one TTD conversion tracking tag. Campaign creation must expose
 * attributed conversions for exactly the tag the conversions lane delivers
 * against; this was previously duplicated as a Worker var and
 * `conversions.ttd.tracking_tag_id`, with only a comment holding them equal.
 * Promoted here for the same reason as `meta.purchase_pixel_id`.
 */
tracking_tag_id: string,
/**
 * The second conversion tag on the house four-column reporting layout —
 * the client-side image pixel's purchase tag ("ShubertImagePixel -
 * Purchase", live value `tg2lzfg`), reported beside `tracking_tag_id`
 * (EncorePro offline conversions) under both cross-device models. The
 * offline-conversion PUSH still references `tracking_tag_id` only; this
 * tag is a reporting column, never a delivery target. Empty blocks
 * campaign publish at preflight.
 */
secondary_tracking_tag_id: string,
/**
 * Kokai relevance seed stamped on every created campaign (`SeedId`; live
 * value `cj38m0xw`). Requires `Version: "Kokai"` on the create body, which
 * creation always sends. Empty blocks campaign publish at preflight.
 */
seed_id: string,
/**
 * The geography-inclusion geo segment every ad group targets via its
 * default-for-dimension bid list (live value `bpywp4wtwk`). Empty blocks
 * campaign publish at preflight.
 */
geo_segment_id: string,
/**
 * The ad-group optimization goal, as viewable-CPM in cents (live value
 * 1250 = $12.50). Cents rather than a float so the sealed document text
 * stays hash-stable; converted at the wire edge like flight budgets.
 * Zero blocks campaign publish at preflight.
 */
roi_goal_vcpm_cents: bigint,
/**
 * The advertiser-owned private-contract bid list every ad group must
 * associate, CTV and Display alike — the curated deal list (named "Gio"
 * in TTD, live id `606052474`; the owner adds the Display deals to it).
 * Paired with
 * `ContractTargeting.AllowOpenMarketBiddingWhenTargetingContracts=false`,
 * it makes the deal list a hard inventory boundary: no open-market
 * buys. Pinned by id, not name, so a rename in TTD's UI cannot detach the
 * deals. The list's *membership* stays agency-curated in TTD; creation
 * enforces only that the list is attached. Empty blocks campaign publish
 * at preflight.
 */
private_contract_bid_list_id: string,
/**
 * The CTV ad-group bid pair, as CPM in cents.
 *
 * Cents rather than floats for the same reason as `roi_goal_vcpm_cents`:
 * the sealed document text has to stay hash-stable, and the conversion
 * happens at the wire edge. Promoted out of `clients/ttd.rs` because the
 * number is a commercial decision measured against live inventory, not a
 * code constant (PROVISIONING records the measurement), so changing it
 * should not need a deploy. The values stay out of this comment: it is
 * shipped to every browser in the generated types, and a buy-side bid
 * beside the sell-side card rate gives away SMART's share (plan
 * 2026-10-15, §6).
 *
 */
ctv_base_bid_cpm_cents: bigint, ctv_max_bid_cpm_cents: bigint,
/**
 * The Display ad-group bid pair, as CPM in cents (owner 2026-10-08).
 * Display buys through the same deal list as CTV. Schema 30. The values
 * stay out of this comment for the reason the CTV pair's do.
 */
display_base_bid_cpm_cents: bigint, display_max_bid_cpm_cents: bigint, };

export type ManagedR2Config = { account_id: string, bucket: string, endpoint: string | null, };

export type ManagedSchedulerConfig = { encorepro_backfill_cron: string | null, attribution_cron: string | null, rollup_builder_cron: string | null, delivery_reporting_cron: string | null,
/**
 * Read-only TTD GraphQL current-day delivery. Unset uses :20 hourly ET.
 */
ttd_intraday_delivery_cron: string | null, sync_status_sweeper_cron: string | null, backfill_health_cron: string | null, pixel_health_cron: string | null, audience_materializer_cron: string | null, creative_upload_gc_cron: string | null, audit_gc_cron: string | null, alert_scan_cron: string | null, heartbeat_cron: string | null, conversion_delivery_cron: string | null, conversions_gc_cron: string | null, copilot_brief_cron: string | null, copilot_gc_cron: string | null, ticket_conversions_cron: string | null,
/**
 * Clears the IP and user agent from external creative-approval records
 * once their retention window passes. The decision itself is permanent.
 */
creative_review_gc_cron: string | null,
/**
 * Refreshes the immutable all-buyers and per-production Audience Planner
 * comparison baselines. Unset checks every hour from 05:15 to 23:15 New York
 * time and publishes once a day, as soon as the nightly jobs before it are done.
 */
audience_benchmark_refresh_cron: string | null,
/**
 * Nightly UID2 mint/refresh over `users` — default 04:15 NY, after the
 * 02:00 ingest wave and the 04:00 lookalike fit. Since v13 this job no
 * longer touches `uid2_prospect`; see `uid2_prospect_refresh_cron`.
 */
uid2_refresh_cron: string | null,
/**
 * UID2 mint/refresh over `uid2_prospect` — default hourly.
 *
 * Hourly rather than nightly because this is the lane a live campaign
 * waits on: a prospect audience cannot activate on TTD until its people
 * hold tokens, and one pass a night meant a freshly built audience
 * reached TTD at a fraction of its size and stayed there for days.
 */
uid2_prospect_refresh_cron: string | null,
/**
 * Daily invoicing scan — default 06:30 NY. Freezes any closed week
 * whose run weekday has passed and flags basis drift.
 */
invoicing_scan_cron: string | null,
/**
 * Invoice action executor — default every minute. Runs the intent rows
 * the Admin → Invoices screen queues.
 */
invoicing_actions_cron: string | null,
/**
 * Daily Xero payment-status readback — default 06:45 NY.
 */
invoicing_payment_sync_cron: string | null, lookalike_model_refresh_cron: string | null, };

export type ManagedDeliveryReportingConfig = { enabled: boolean, lookback_days: number,
/**
 * Days a provider may still restate **attribution** for. Not delivery —
 * see `delivery_settled_lag_days`.
 */
settled_lag_days: number,
/**
 * Days a provider may still restate **delivery** for, counted back from
 * today. Zero means a calendar day's impressions, clicks and spend are
 * final the moment the day ends, which is the honest answer for both
 * providers; the field exists so a provider that restates delivery can be
 * accommodated without dragging attribution's much longer clock onto it.
 */
delivery_settled_lag_days: number, max_mappings_per_run: number, max_pages_per_mapping: number, meta_page_size: number, ttd_creative_page_size: number, ttd_report_timeout_minutes: number, ttd_delivery_template_id: bigint | null, ttd_attribution_template_id: bigint | null,
/**
 * Independent current-day GraphQL lane. Defaults fail-closed for every
 * stored revision written before schema 17.
 */
ttd_intraday_mode: TtdIntradayMode, };

export type TtdIntradayMode = "off" | "shadow" | "publish";

export type ManagedEnrichConfig = { base: string, max_batch: number | null, max_concurrent_requests: number | null, };

export type ManagedEmailConfig = { api_base: string, account_id: string, from: string,
/**
 * Zone owning the sender domain. Empty disables delivery-log polling.
 */
analytics_zone_id: string, };

export type ManagedAlertsConfig = { enabled: boolean, email_to: Array<string>, console_url: string, spike_threshold: bigint, scan_window_minutes: bigint, suppression_hours: bigint, };

export type ManagedInvoicingConfig = {
/**
 * Off keeps every invoicing job a silent no-op.
 */
enabled: boolean,
/**
 * Xero Custom Connection client id (client_credentials grant).
 */
xero_client_id: string,
/**
 * The one Xero contact every invoice is raised against.
 */
xero_contact_id: string, xero_api_base: string, xero_identity_base: string,
/**
 * Sales account code for a line sold without an item. Must be blank
 * when `xero_item_code` is set: the item's own sales account applies,
 * and a line carrying an item never sends an account code (the fee
 * line included, under `xero_fee_item_code`). Defaults to blank: `200`
 * is Xero's demo Sales account, and this organisation refuses it
 * (2026-09-10).
 */
xero_account_code: string,
/**
 * The Xero item (`Code`, not the ItemID) every media line is sold as,
 * e.g. `MEDIA`. Sent as `LineItem.ItemCode`; blank sends no item.
 */
xero_item_code: string,
/**
 * The Xero item the management-fee line is sold as, e.g. `MGMT`.
 * Required while enabled with a non-zero fee rate.
 */
xero_fee_item_code: string,
/**
 * Management fee as a share of each campaign's gross, in basis points
 * (100 = 1%). Frozen onto an invoice when it is generated; changing
 * it never moves a reviewed or sent invoice.
 */
management_fee_bps: number,
/**
 * Prefix of the `InvoiceNumber` we allocate: `SHUBERT` gives
 * `SHUBERT-0001`, `SHUBERT-0002`, … (max in use + 1, across Xero and our
 * own table). Xero's own `INV-` sequence is not used.
 */
xero_invoice_number_prefix: string,
/**
 * Line tax type. `NONE` with tax-exclusive amounts, so the invoice total
 * equals the sum of campaign spend.
 */
xero_tax_type: string,
/**
 * The weekday after a week closes on which it is frozen for review:
 * 1 = Monday … 7 = Sunday. Tuesday by default, so Sunday's T-1 delivery
 * has landed. The invoice falls due on the Friday of the week it is
 * raised in (`crate::invoicing::due_date_for`).
 */
run_weekday: number,
/**
 * Skip the review hold: the daily scan approves and sends without a
 * human. Off by default.
 */
auto_approve: boolean, recipients_to: Array<string>, recipients_cc: Array<string>,
/**
 * Prefix of the Xero `Reference` (`SMART` gives `SMART-2026-08`).
 */
reference_prefix: string,
/**
 * Email subject with `{reference}` `{invoice_number}` `{period}` `{total}`
 * `{due_date}` tokens.
 */
email_subject_template: string,
/**
 * `YYYY-Www` (ISO week): the earliest week the catch-up scan may
 * invoice. Required when enabled, so a fresh deployment never bills
 * history by surprise.
 */
first_period: string,
/**
 * How many weeks after its run date a closed week stays in the lane's
 * window: still frozen by the daily scan if it was missed, still reported
 * by the dead-man if it was never sent. Measured from the run date so a
 * deadline (run date + grace) can never fall outside it.
 */
catch_up_weeks: number,
/**
 * Raise an adjustment invoice when a sent week's settled basis grows past
 * what was billed. Off by default: the daily scan then only stamps drift.
 * Adjustments always wait for a human approval, whatever `auto_approve`
 * says; a decrease is never invoiced (a credit note is raised by hand).
 */
adjustments_enabled: boolean,
/**
 * A week's net increase below this many cents keeps accumulating instead
 * of raising an adjustment. 2500 = $25.
 */
adjustment_min_cents: number,
/**
 * How many weeks after its run date a sent week is still compared with
 * its settled basis (and may be adjusted). Also widens the drift scan.
 */
adjustment_window_weeks: number,
/**
 * Subject of the adjustment email: the regular tokens (naming the
 * adjustment) plus `{original_invoice_number}` `{original_reference}`.
 */
adjustment_email_subject_template: string, };

export type ManagedPixelKvConfig = {
/**
 * Off keeps both projection jobs silent no-ops.
 */
enabled: boolean, api_base: string,
/**
 * The Cloudflare account that owns the namespace (32 hex characters).
 */
account_id: string,
/**
 * The namespace pixel-worker binds as `PIXEL_CACHE` in the environment
 * this batch-worker serves (32 hex characters).
 */
namespace_id: string, };

export type ManagedLogArchiveConfig = { enabled: boolean, prefix: string,
/**
 * Hot-store retention for `app_logs`, in days. Unset → the
 * job's built-in default; values below its floor are clamped, not obeyed.
 */
retention_days: bigint | null, };

export type ManagedConversionsMode = "log_only" | "live";

export type ManagedConversionsMetaConfig = { graph_api_base: string, test_event_code: string | null, batch_size: number, };

export type ManagedConversionsTtdConfig = { endpoint: string, data_provider_id: string, merchant_id: string | null, batch_size: number, };

export type ManagedUid2Config = { base_url: string, map_batch_size: number,
/**
 * Fallback staleness window (days) for stored UID2s whose operator
 * `refresh_from` is absent. `None` ⇒ trust `refresh_from` alone.
 */
refresh_staleness_days: bigint | null,
/**
 * Outbound `identity/map` call budget per `uid2_refresh` run — the
 * **`users`** sweep only, since v13 split the prospect lane into its own
 * job. Independent of the conversions delivery tick budget. Unfinished
 * work resumes on the next run (the stale predicate shrinks
 * monotonically).
 */
refresh_max_calls_per_run: number,
/**
 * The same budget for `uid2_prospect_refresh`, which walks `uid2_prospect`
 * on its own cron with its own page count and its own clock.
 *
 * Separate from `refresh_max_calls_per_run` because sharing one budget is
 * what starved this lane: `users` was walked first and holds ~3.85M rows
 * against what was then a 4 000-call ceiling (the default is 10 000 now), so it was
 * truncated every night and the
 * prospect lane only ever received its reserved floor.
 */
prospect_refresh_max_calls_per_run: number,
/**
 * Distinct identities one prospect TTD upload may mint inline for
 * `uid2_prospect` cache misses. Beyond it, misses are seeded pending for
 * `uid2_prospect_refresh` and the drop reports them as `skipped_no_uid2`
 * rather than stalling on the operator. `0` disables inline minting.
 *
 * Identities, not calls: an operator sizing this is thinking about people,
 * and the call count is an artifact of `map_batch_size` packing. The job
 * divides (rounding up) to get its call budget, so changing the batch size
 * no longer silently changes the coverage ceiling.
 */
prospect_inline_mint_max_identities: number,
/**
 * Seconds of UID2 **operator latency** the same inline mint may spend.
 *
 * A second bound in different units, for the reason `uid2_refresh` already
 * pairs `MAX_SCAN_PAGES` with `MAX_SCAN_DURATION`: an identity count bounds
 * how much work is attempted, not how long a degraded operator takes to
 * refuse it, and `MAX_CONCURRENT_AUDIENCES` allows four drops resident at
 * once in the single process that also owns the sqlite mutex, the lineage
 * flusher and the /insights handlers.
 *
 * Charged per `identity/map` call, NOT as a wall clock over the drop:
 * the cost of reading the next prospect page from the data store tracks its
 * load, not the operator, so a wall clock would cut the mint short on a busy
 * store for a reason the operator had no part in.
 */
prospect_inline_mint_max_seconds: number, };

export type ManagedConversionsDeliveryConfig = { claim_batch: bigint, max_attempts: bigint, retry_base_secs: bigint, freshness_max_hours: bigint | null, meta_freshness_max_hours: bigint, ttd_freshness_max_hours: bigint, retention_days: bigint, event_types: Array<string>, max_posts_per_tick: bigint, };

export type ManagedConversionsTicketsConfig = { enabled: boolean,
/**
 * Logical id for this lane's conversions — the queue key and delivery
 * grouping. NOT the destination Pixel: that is `meta.purchase_pixel_id`,
 * which is now the only source. `meta_pixel_id` used to sit here as an
 * override, and leaving it unset meant "resolve the Pixel from the D1
 * `pixels` row named by this field". That indirection is gone.
 */
pixel_id: string, meta: boolean, ttd: boolean, test_event_code: string | null, batch: bigint,
/**
 * Meta CAPI `action_source` for ticket Purchases, read at delivery so a
 * change applies to rows already queued. One of [`META_TICKET_ACTION_SOURCES`].
 */
action_source: string, };

export type ManagedConversionsConfig = { enabled: boolean, mode: ManagedConversionsMode, meta: ManagedConversionsMetaConfig | null, ttd: ManagedConversionsTtdConfig | null, uid2: ManagedUid2Config | null, delivery: ManagedConversionsDeliveryConfig, tickets: ManagedConversionsTicketsConfig | null, };

export type ManagedFlowTapConfig = { enabled: boolean, capacity: number, max_age_secs: bigint, };

export type ClaudeModel = "claude-opus-5-5" | "claude-sonnet-5-5" | "claude-haiku-5-5" | "claude-fable-5-1";

export type ClaudeEffort = "low" | "medium" | "high" | "xhigh" | "max";

export type CacheTtl = "5m" | "1h";

export type ManagedLlmConfig = {
/**
 * The Daily Briefing's model.
 */
brief_model: ClaudeModel,
/**
 * The Daily Briefing's effort; one `brief_model` accepts.
 */
brief_effort: ClaudeEffort,
/**
 * `max_tokens` for the Daily Briefing. Thinking counts against it.
 */
brief_max_tokens: number,
/**
 * The whole Daily Briefing exchange, retries included. It runs in the background.
 */
brief_timeout_secs: number,
/**
 * The Explain panels' model.
 */
explain_model: ClaudeModel,
/**
 * The Explain panels' effort; one `explain_model` accepts.
 */
explain_effort: ClaudeEffort,
/**
 * `max_tokens` for an Explain. Thinking counts against it.
 */
explain_max_tokens: number,
/**
 * The whole Explain exchange. It must finish inside the API's 30 s request deadline.
 */
explain_timeout_secs: number,
/**
 * Calls in flight at once, per task.
 */
max_concurrent: number,
/**
 * The lifetime of the one prompt-cache breakpoint. Caching is always on.
 */
cache_ttl: CacheTtl,
/**
 * Ask Anthropic to serve a refused call on its recommended fallback model, where the
 * chosen model supports it.
 */
refusal_fallback: boolean, };

export type ManagedCopilotConfig = { enabled: boolean, cache_retention_days: number, cache_fresh_secs: bigint, brief_enabled: boolean, brief_notify: boolean, };

export type ManagedConfigDocument = {
/**
 * See [`MANAGED_CONFIG_SCHEMA_VERSION`]. Not operator-editable; the Admin
 * Config editor renders an explicit section allowlist that omits it.
 */
schema_version: number,
/**
 * Cloudflare Worker settings.
 *
 * Deliberately NOT `#[serde(default)]`: a PUT is a full-document replace
 * into an immutable revision, so a client that omits this section must get
 * a 422, not a revision with every write switch silently reset and the
 * dead-man recipients cleared. Stored pre-schema-5 documents — which
 * genuinely lack it — are read through [`parse_stored_document`], which
 * supplies the fail-closed defaults.
 */
edge: ManagedEdgeConfig, encorepro_postgres: ManagedEncoreproPostgresConfig | null, log_triage: ManagedLogTriageConfig | null, pixel_ingest: ManagedPixelIngestConfig, meta: ManagedMetaConfig, ttd: ManagedTtdConfig, r2: ManagedR2Config, scheduler: ManagedSchedulerConfig, delivery_reporting: ManagedDeliveryReportingConfig | null, enrich: ManagedEnrichConfig, email: ManagedEmailConfig | null, alerts: ManagedAlertsConfig | null, log_archive: ManagedLogArchiveConfig | null, conversions: ManagedConversionsConfig | null, flow_tap: ManagedFlowTapConfig, llm: ManagedLlmConfig | null, copilot: ManagedCopilotConfig | null,
/**
 * Monthly Xero invoicing. Absent means no billing lane runs.
 */
invoicing: ManagedInvoicingConfig | null,
/**
 * pixel-worker's KV projection (`shared::pixel_projection`). Absent means
 * nothing is written to the namespace.
 */
pixel_kv: ManagedPixelKvConfig | null, };

export type ConfigSecretPath = "encorepro_postgres.password" | "meta.access_token" | "meta.app_secret" | "ttd.bearer" | "ttd.crm_bearer" | "r2.s3_access_key_id" | "r2.s3_secret" | "enrich.api_key" | "email.api_token" | "alerts.webhook_url" | "conversions.meta.access_token" | "conversions.ttd.auth_secret" | "conversions.uid2.api_key" | "conversions.uid2.client_secret_b64" | "llm.api_key" | "invoicing.xero_client_secret" | "pixel_kv.api_token";

export type ConfigSecretMetadata = { path: ConfigSecretPath, is_set: boolean, updated_at: string | null, };

export type ConfigSecretUpdate = { "action": "set", path: ConfigSecretPath, value: string, } | { "action": "clear", path: ConfigSecretPath, };

export type ConfigApplyStatus = "pending" | "preparing" | "applying" | "applied" | "failed";

export type ConfigRevisionMetadata = { version: bigint, created_at: string, created_by: string, };

export type ConfigVersionState = { desired_version: bigint | null, applied_version: bigint | null, status: ConfigApplyStatus, status_updated_at: string, apply_started_at: string | null, applied_at: string | null, last_error: string | null, };

export type ConfigNodeState = { node_id: string, applied_version: bigint | null, desired_version: bigint | null, status: ConfigApplyStatus, last_error: string | null, updated_at: string,
/**
 * The node has not reported within the staleness window — treat its
 * `status` as last-known, not current.
 */
stale: boolean, };

export type AdminConfigGetResponse = { revision: ConfigRevisionMetadata | null, document: ManagedConfigDocument | null, secrets: Array<ConfigSecretMetadata>, state: ConfigVersionState,
/**
 * Per-node convergence. Empty only before any node has reported.
 */
nodes: Array<ConfigNodeState>, };

export type AdminConfigPutRequest = { expected_version: bigint | null, document: ManagedConfigDocument, secret_updates: Array<ConfigSecretUpdate>, };

export type AdminConfigPutResponse = { revision: ConfigRevisionMetadata, state: ConfigVersionState, };

export type ConfigFieldChange = { path: string, value: unknown, };

export type AdminConfigPatchRequest = {
/**
 * The desired version the change was read from.
 */
expected_version: bigint, changes: Array<ConfigFieldChange>, dry_run: boolean,
/**
 * From this change's dry run; required unless `dry_run`.
 */
preview_token: string | null, };

export type AdminConfigRevertRequest = { revision: bigint,
/**
 * The desired version the revert was read from.
 */
expected_version: bigint, dry_run: boolean,
/**
 * From this revert's dry run; required unless `dry_run`.
 */
preview_token: string | null, };

export type ConfigLeafDiff = { path: string, before: unknown, after: unknown, };

export type AdminConfigChangeResponse = { dry_run: boolean, base_version: bigint, changes: Array<ConfigLeafDiff>, preview_token: string | null, preview_expires_at: string | null, committed_version: bigint | null, state: ConfigVersionState | null, };

export type RemediationAction = "reset_retry_budget" | "retry_binding_repair" | "retry_retired_ad_pause" | "force_audience_refresh" | "release_ttd_drop_intent" | "remint_meta_container" | "repin_failed_publish";

export type RemediationEntityKind = "campaign" | "creative" | "audience";

export type RemediationVerdict = "ready" | "refuse_not_found" | "refuse_status" | "refuse_nothing_to_do" | "refuse_provider_evidence" | "refuse_lease_held" | "refuse_writes_disabled" | "refuse_active_operation" | "refuse_archived" | "refuse_nonce";

export type RemediationTarget = { entity_kind: RemediationEntityKind, entity_id: string,
/**
 * Which provider leg. Absent for repairs that are not per-target.
 */
target: SyncTarget | null,
/**
 * The 32-char intent nonce, for [`RemediationAction::ReleaseTtdDropIntent`]
 * only. Required there, and required to *match* — an operator who has not
 * read the wedged intent cannot supply it, which is the point.
 */
nonce: string | null, operation_id?: string | null, };

export type RemediationChange = { table: string, column: string, before: string, after: string, };

export type RemediationStep = { name: string, rows_affected: number,
/**
 * False when the statement ran and matched nothing — which, because the
 * preflight predicates are repeated in the `WHERE`, means the row moved
 * under us and the repair correctly lost the race.
 */
applied: boolean, };

export type RemediationPreview = { action: RemediationAction, target: RemediationTarget, verdict: RemediationVerdict,
/**
 * A sentence for the operator, present for every verdict including
 * `Ready` — "nothing is wrong" and "we did not check" must not look alike.
 */
reason: string,
/**
 * Empty unless the verdict is `Ready`.
 */
changes: Array<RemediationChange>,
/**
 * A digest of the rows this preview read. `apply` recomputes it from live
 * state and refuses on drift.
 *
 * It is not a capability token and is not signed: the caller is already an
 * authenticated unscoped admin, so the risk is a stale page rather than
 * forgery. It is the *second* line of defence — the first is that every
 * predicate below travels into the `UPDATE`'s `WHERE`.
 */
state_fingerprint: string,
/**
 * The exact string the operator must type back to apply.
 */
confirmation: string, };

export type RemediationPreviewRequest = { action: RemediationAction, target: RemediationTarget, };

export type RemediationApplyRequest = { action: RemediationAction, target: RemediationTarget, state_fingerprint: string, confirmation: string, };

export type RemediationApplyResponse = { steps: Array<RemediationStep>,
/**
 * The `audit_events` row this repair wrote, so the operator can find it.
 */
audit_id: string,
/**
 * What happens next, in words — a repaired row is picked up by a cron, so
 * "applied" is not the same as "fixed".
 */
next: string, };

export type RemediationCandidate = { action: RemediationAction, target: RemediationTarget,
/**
 * The entity's human name, for an operator who does not read ids.
 */
label: string,
/**
 * Why it is a candidate — "stranded at 10 attempts", "intent recorded".
 */
detail: string, };

export type DriftLifecycle = "current" | "retired" | "safety";

export type DriftResolutionReason = "object_replaced" | "expectation_changed" | "field_verified" | "pause_verified" | "object_archived" | "legacy_superseded";

export type DriftFreshness = "fresh" | "overdue" | "unverified";

export type DriftRepairability = "automatic" | "detect_only" | "manual" | "not_applicable";

export type DriftFindingStatus = "action_required" | "repairing" | "recovery_blocked" | "known_limitation" | "verification_overdue" | "replaced" | "superseded" | "repaired" | "resolved";

export type DriftVerdict = "clean" | "known_limitations" | "action_required" | "repairing" | "recovery_blocked" | "verification_overdue" | "unverified";

export type DriftRepairProgress = { incident_id: string, state: string, attempts: number, next_retry_at: bigint | null, failure_kind: string | null, recurrence_count: number, };

export type DriftEvidence = { expectation_key: string, lifecycle: DriftLifecycle, generation: bigint | null, resolution_reason: DriftResolutionReason | null, replacement_id: string | null, receipt_ref: string | null, last_verified_at: bigint | null, freshness: DriftFreshness, repairability: DriftRepairability, repair: DriftRepairProgress | null, status: DriftFindingStatus, reason: string | null, };

export type DriftCoverage = { verdict: DriftVerdict, current_expected: number, current_verified: number, retired_expected: number, retired_verified: number, next_check_at: bigint | null, last_verified_at: bigint | null, overdue: boolean, account_cooldown_until: bigint | null, };

export type DriftSummary = { total: number, unresolved: number, known_limitations: number, campaigns: number, status_counts: { [key in string]?: number }, };

export type ProviderDriftEvent = { evidence?: DriftEvidence | null, id: string,
/**
 * The pass that opened the row. Groups every field that diverged together
 * on that pass, so "six fields moved at once" is distinguishable from six
 * separate passes.
 */
scan_id: string,
/**
 * The pass that last saw the field diverging. Equal to `scan_id` on a first
 * sighting; a repair keys on it.
 */
last_scan_id: string, campaign_id: string, campaign_revision: number,
/**
 * `meta` or `ttd`.
 */
target: string,
/**
 * `campaign`, `ad_set`, `ad`, `ad_group` or `creative`.
 */
object_kind: string, provider_object_id: string,
/**
 * The ladder's own field name — `base_bid_cpm`,
 * `targeting.custom_audiences`.
 */
field: string,
/**
 * What D1 and managed config say it should be. `None` means the plan does
 * not carry the field at all, which is a different finding from a
 * mismatch and must stay distinguishable in the UI.
 */
expected_value: string | null,
/**
 * What the provider held at the LAST sighting. `None` means absent there —
 * the finding that says a setting was removed rather than changed.
 */
observed_value: string | null,
/**
 * First sighting.
 */
detected_at: bigint,
/**
 * Last sighting — moves on every pass that still sees the drift. Absent
 * closing columns plus a recent `last_detected_at` is "still true".
 */
last_detected_at: bigint,
/**
 * Set when SAM's own repair verb wrote the field and a readback confirmed.
 * Never set by observation alone — that is `resolved_at`.
 */
repaired_at: bigint | null,
/**
 * Set when the row closed without SAM writing anything: a later pass saw
 * the field back in line (`resolution = "observed"`), or the plan moved on
 * (`"superseded"`).
 */
resolved_at: bigint | null,
/**
 * `"observed"` or `"superseded"`; present exactly when `resolved_at` is.
 */
resolution: string | null,
/**
 * Whether a full pre-image of the provider object was captured before the
 * repair overwrote it. Only ever true for Meta, whose targeting write
 * replaces the whole object; TTD patches merge and destroy nothing.
 */
has_pre_image: boolean, };

export type ProviderDriftResponse = { summary?: DriftSummary | null, next_cursor?: string | null, events: Array<ProviderDriftEvent>,
/**
 * The latest sweep outcome per (campaign, provider) — the denominator the
 * events list is a numerator of. Empty means no campaign has ever been
 * scanned, which is a different answer from "no drift".
 */
scans: Array<ProviderDriftScan>,
/**
 * How far back the window reaches, in milliseconds before now.
 */
window_ms: bigint,
/**
 * True when the row cap was hit and older rows in the window were not
 * returned. A truncated list that does not say so reads as "this is all
 * the drift there was".
 */
truncated: boolean, };

export type ProviderDriftScan = { coverage?: DriftCoverage | null, campaign_id: string, campaign_revision: number,
/**
 * `meta` or `ttd`.
 */
target: string,
/**
 * The pass's own status, never the coverage verdict: `pending` (no pass has
 * started for this revision), `running`, `complete`, or `failed` when the
 * last pass recorded an error. A campaign that is merely unverified is
 * `pending` or `complete` with an overdue `coverage`, not `failed`.
 */
status: string,
/**
 * What the scan concluded — `clean`, `drifted`, or `incomplete` when the
 * published hierarchy was missing objects the plan expects. Absent while a
 * scan is still running or if it failed before finishing.
 */
state: string | null,
/**
 * How many objects the scan actually read. A clean verdict over one object
 * is not the same as a clean verdict over nine.
 */
objects_examined: number | null,
/**
 * Present only on a failed scan. Already redacted by the journal writer.
 */
last_error: string | null, updated_at: bigint, };

export type RemediationCatalogResponse = { candidates: Array<RemediationCandidate>, };

export type ConfigReloadRequest = { desired_version: bigint | null, };

export type ConfigReloadResponse = { accepted_version: bigint | null, active_version: bigint, state: ConfigVersionState, };

export type ConfigValidationError = { path: string, message: string, };

export type ConfigValidationErrors = { errors: Array<ConfigValidationError>, };

export type CredentialKind = "api_key" | "oauth_grant";

export type CredentialScope = "read" | "write" | "prepare" | "admin";

export type CredentialSummary = { id: string, kind: CredentialKind,
/**
 * The key's name, or the OAuth client's name for a grant.
 */
name: string,
/**
 * The stored ceiling; the owner's role caps it again on every request.
 */
scope: CredentialScope,
/**
 * The OAuth client a grant was issued to; `None` for an API key.
 */
client_name: string | null,
/**
 * The host a grant redirects to (`claude.ai`, `chatgpt.com`); `None` for an API key.
 */
redirect_host: string | null,
/**
 * The first characters of an API key (`sam_pat_Ab12`), so a person can tell keys apart.
 * Never enough of the secret to use.
 */
display_prefix: string | null, created_at: string, last_used_at: string | null, expires_at: string, revoked_at: string | null,
/**
 * Why it was revoked: `owner`, `admin`, `user_disabled`, `user_deleted`,
 * `role_changed`, `password_reset`, `refresh_reuse`, `code_replay`, `client` (the app
 * revoked its own access) or `code_unused` (approved, never completed by the app).
 */
revoke_reason: string | null, };

export type CredentialListResponse = { credentials: Array<CredentialSummary>,
/**
 * `edge.api_keys_enabled`: off, no key can be created and every key's request is refused
 * (nothing is revoked).
 */
api_keys_enabled: boolean,
/**
 * `edge.mcp_enabled`: off, Claude's connector is refused (nothing is revoked).
 */
mcp_enabled: boolean,
/**
 * The most a credential this user holds may do (a Reporter's is `read`).
 */
max_scope: CredentialScope, };

export type CreateApiKeyRequest = {
/**
 * 1 to 100 characters after trimming.
 */
name: string,
/**
 * At most the caller's role allows (`max_scope`).
 */
scope: CredentialScope,
/**
 * One of [`API_KEY_LIFETIMES_DAYS`].
 */
expires_in_days: number, };

export type CreateApiKeyResponse = { credential: CredentialSummary,
/**
 * `sam_pat_…`, sent as `Authorization: Bearer <secret>`.
 */
secret: string, };

export type OauthConsentView = {
/**
 * The app's name, from its own metadata document: untrusted, shown only as text.
 */
client_name: string,
/**
 * Where the person is sent back, and so where the access goes: the one fact a person
 * checks before approving.
 */
redirect_host: string,
/**
 * What the app asked for.
 */
requested_scope: CredentialScope,
/**
 * The most this person's role lets a credential do (a Reporter's is `read`).
 */
max_scope: CredentialScope,
/**
 * `edge.mcp_enabled`: off, an approval is refused.
 */
mcp_enabled: boolean,
/**
 * When the request lapses unanswered.
 */
expires_at: string, };

export type OauthConsentDecision = { approve: boolean,
/**
 * The account the page showed as deciding (`/me`'s user id). The decision is refused
 * when the browser's session now belongs to someone else (another tab signed in as
 * another person), so a grant never lands on an account the person did not see.
 */
signed_in_as: string,
/**
 * Let the app edit drafts too: honoured only when the app asked for it and the role
 * allows it; otherwise the grant reads.
 */
allow_edits: boolean,
/**
 * Also let it prepare launches (create new audiences, submit creatives for review):
 * honoured only with `allow_edits`, when the app asked for `sam:prepare`, the role
 * allows it and `edge.credential_prepare_enabled` is on; otherwise edits or reads.
 */
allow_prepare: boolean,
/**
 * Also let it administer (config, alerts, remediations, invoices, users): honoured only
 * with `allow_edits` and `allow_prepare`, when the app asked for `sam:admin`, the person
 * is an Admin with no production scope and `edge.credential_admin_enabled` (and the
 * Prepare switch) is on; otherwise the grant falls to the next box down.
 */
allow_admin: boolean, };

export type OauthConsentRedirect = { redirect_to: string, };

export type ReportSummaryKpi = { spend_cents: bigint, impressions: bigint, clicks: bigint, assigned_tickets: bigint, halo_tickets: bigint, tickets: bigint, assigned_revenue_cents: bigint, halo_revenue_cents: bigint, revenue_cents: bigint, assigned_roas: number, total_roas: number,
/**
 * Compatibility alias for `total_roas`.
 */
roas: number, };

export type ReportSummaryPoint = { date: string, spend_cents: bigint, impressions: bigint, clicks: bigint, assigned_tickets: bigint, halo_tickets: bigint, tickets: bigint, assigned_revenue_cents: bigint, halo_revenue_cents: bigint, revenue_cents: bigint, };

export type ReportSummary = { kpi: ReportSummaryKpi, timeseries: Array<ReportSummaryPoint>, };

export type ReportByCampaignRow = { campaign_id: CampaignId, name: string, channel: Channel, production_id: ProductionId,
/**
 * The campaign's audience, when it has exactly one.
 *
 * `None` for a campaign holding several. This row is campaign-grain, so
 * there is no honest scalar to put here when the campaign spans a direct
 * audience and a lookalike — it used to report segment 0's and drop the
 * rest. Group by audience or audience type for the per-audience answer.
 */
audience_id?: AudienceId | null,
/**
 * The campaign's audience type, when every one of its audiences agrees.
 */
audience_type?: AudienceType | null, spend_cents: bigint, impressions: bigint, clicks: bigint, assigned_tickets: bigint, halo_tickets: bigint, tickets: bigint, assigned_revenue_cents: bigint, halo_revenue_cents: bigint, revenue_cents: bigint, assigned_roas: number, total_roas: number,
/**
 * Compatibility alias for `total_roas`.
 */
roas: number,
/**
 * One entry per day in the requested range, oldest-first; zero-padded.
 */
sparkline_tickets: Array<bigint>, };

export type ReportByProductionRow = { production_id: ProductionId, name: string, spend_cents: bigint, impressions: bigint, clicks: bigint, assigned_tickets: bigint, halo_tickets: bigint, tickets: bigint, assigned_revenue_cents: bigint, halo_revenue_cents: bigint, revenue_cents: bigint, assigned_roas: number, total_roas: number,
/**
 * Compatibility alias for `total_roas`.
 */
roas: number, };

export type ReportByChannelRow = { channel: Channel, spend_cents: bigint, impressions: bigint, clicks: bigint, assigned_tickets: bigint, halo_tickets: bigint, tickets: bigint, assigned_revenue_cents: bigint, halo_revenue_cents: bigint, revenue_cents: bigint, assigned_roas: number, total_roas: number,
/**
 * Compatibility alias for `total_roas`.
 */
roas: number, };

export type ReportByAudienceRow = { audience_id: AudienceId,
/**
 * Immutable audience definition that received this attributed delivery.
 */
audience_revision: number, name: string, audience_type: AudienceType, spend_cents: bigint, impressions: bigint, clicks: bigint, assigned_tickets: bigint, halo_tickets: bigint, tickets: bigint, assigned_revenue_cents: bigint, halo_revenue_cents: bigint, revenue_cents: bigint, assigned_roas: number, total_roas: number,
/**
 * Compatibility alias for `total_roas`.
 */
roas: number, };

export type ReportByAudienceTypeRow = { audience_type: AudienceType, spend_cents: bigint, impressions: bigint, clicks: bigint, assigned_tickets: bigint, halo_tickets: bigint, tickets: bigint, assigned_revenue_cents: bigint, halo_revenue_cents: bigint, revenue_cents: bigint, assigned_roas: number, total_roas: number,
/**
 * Compatibility alias for `total_roas`.
 */
roas: number, };

export type ReportByCreativeRow = { creative_id: CreativeId,
/**
 * `None` for rows whose facts predate the revision-bearing reporting key.
 */
creative_revision?: number | null, name: string, media_type: MediaType, format: string, production_id: ProductionId, spend_cents: bigint, impressions: bigint, clicks: bigint, assigned_tickets: bigint, halo_tickets: bigint, tickets: bigint, assigned_revenue_cents: bigint, halo_revenue_cents: bigint, revenue_cents: bigint, assigned_roas: number, total_roas: number,
/**
 * Compatibility alias for `total_roas`.
 */
roas: number, };

export type ReportAttributionScope = "all_effects" | "direct_only";

export type ReportDataset = "performance" | "delivery" | "attribution";

export type ReportDimension = "day" | "campaign" | "assigned_production" | "sold_production" | "channel" | "audience" | "audience_type" | "creative" | "placement" | "target" | "currency" | "provider_ad" | "provider_creative" | "attribution_effect" | "attribution_setting";

export type ReportSortDirection = "asc" | "desc";

export type ReportFilters = { assigned_production_ids: Array<ProductionId>, sold_production_ids: Array<ProductionId>, campaign_ids: Array<CampaignId>, audience_ids: Array<AudienceId>,
/**
 * Optional immutable revision pin for a single `audience_ids` entry.
 * This prevents a saved campaign report from drifting after the
 * reusable audience definition is edited.
 */
audience_revision?: number | null, creative_ids: Array<CreativeId>,
/**
 * Narrow to one revision of the named creatives. `None` spans every one.
 */
creative_revision?: number | null, channels: Array<Channel>, audience_types: Array<AudienceType>, targets: Array<SyncTarget>, placements: Array<string>,
/**
 * ISO-4217 currency code. Reporting never performs FX conversion, so a
 * request may select at most one currency. An omitted value defaults to
 * USD for backwards compatibility with the current media feeds.
 */
currencies: Array<string>, provider_ad_ids: Array<string>, provider_creative_ids: Array<string>, attribution_effects: Array<string>, attribution_settings: Array<string>, };

export type ReportOrder = {
/**
 * An additive or derived metric wire name. The API resolves this
 * through a closed allow-list; dimension ordering is not supported.
 */
field: string, direction: ReportSortDirection, };

export type ReportQueryRequest = { dataset: ReportDataset,
/**
 * Omitted requests preserve the historical all-effects behavior.
 * `direct_only` is valid only for the Performance dataset.
 */
attribution_scope?: ReportAttributionScope, date_from: string, date_to: string, group_by: Array<ReportDimension>, filters: ReportFilters, order: ReportOrder | null, cursor: string | null, limit: number | null, };

export type PerfHaloReportView = "overview" | "campaign" | "assigned_production" | "channel" | "audience" | "audience_type" | "creative" | "halo_analysis";

export type PerfHaloMetric = "tickets" | "ticket_value";

export type PerfHaloExportRequest = { view: PerfHaloReportView, date_from: string, date_to: string, filters: ReportFilters, order: ReportOrder | null, halo_metric: PerfHaloMetric, };

export type ReportMetricSet = {
/**
 * Provider media cost. `null` for every reader but an Admin: beside gross and
 * markup it would give the share of gross SMART retains (plan 2026-10-15).
 */
media_spend_cents: number | null, markup_cents: bigint, billable_spend_cents: bigint,
/**
 * Provider media cost over the same closed provider scopes as attribution.
 * This excludes unmatched current-day delivery from attribution ratios.
 * `null` for every reader but an Admin, as `media_spend_cents`.
 */
attribution_media_spend_cents: number | null,
/**
 * Client gross spend over the same closed provider scopes as attribution.
 * CPA and show-facing ROAS use this denominator.
 */
attribution_billable_spend_cents: bigint, impressions: bigint, clicks: bigint, video_starts: bigint, video_completions: bigint,
/**
 * Provider-attributed conversion counts retain six decimal places. Meta
 * can return modeled fractional conversions, so these are authoritative;
 * the integer ticket fields below are compatibility projections rounded
 * once after aggregation.
 */
assigned_conversions_micros: bigint, halo_conversions_micros: bigint, attributed_conversions_micros: bigint,
/**
 * Provider-reported currency-major-unit values at six decimal places.
 * Cents fields remain available for existing UI and CSV consumers.
 */
assigned_revenue_micros: bigint, halo_revenue_micros: bigint, attributed_revenue_micros: bigint, gross_tickets: bigint, refund_tickets: bigint, assigned_tickets: bigint, halo_tickets: bigint, net_tickets: bigint, assigned_revenue_cents: bigint, halo_revenue_cents: bigint, net_revenue_cents: bigint,
/**
 * Show-facing ratios divide by `attribution_billable_spend_cents`, the
 * client gross over exactly the provider scopes that published
 * attribution. This keeps open-day or missing-scope delivery from
 * diluting Tickets and Ticket Value.
 */
assigned_roas: number, total_roas: number,
/**
 * Return on the provider cost over those same attribution scopes. `null` for
 * every reader but an Admin: revenue over it would rebuild provider cost.
 */
media_roas: number | null,
/**
 * Deprecated alias for `total_roas`, now numerically identical to it.
 * Retained for one release so saved consumers keep resolving, then drop.
 */
billable_roas: number, };

export type ReportDimensionValues = { day?: string | null, campaign_id?: CampaignId | null, campaign_revision?: number | null, campaign_name?: string | null, assigned_production_id?: ProductionId | null, assigned_production_name?: string | null, sold_production_id?: ProductionId | null, sold_production_name?: string | null, channel?: Channel | null, audience_id?: AudienceId | null, audience_revision?: number | null, audience_name?: string | null, audience_type?: AudienceType | null, creative_id?: CreativeId | null,
/**
 * The revision of that creative the facts were delivered under.
 *
 * `None` for a fact written before the reporting key carried one — the
 * creative is still named, only the revision is unknown. Mirrors
 * `audience_revision` above, which has worked this way all along.
 */
creative_revision?: number | null, creative_name?: string | null, creative_media_type?: MediaType | null, creative_format?: string | null, placement?: string | null, target?: SyncTarget | null, currency?: string | null, provider_ad_id?: string | null, provider_creative_id?: string | null, attribution_effect?: string | null, attribution_setting?: string | null, };

export type ReportQueryRow = { dimensions: ReportDimensionValues, metrics: ReportMetricSet, };

export type ProviderAttributionQualityReason = {
/**
 * `mapped`, `excluded`, or `deduplicated`.
 */
disposition: string, reason: string, provider_entries: bigint, conversions_micros: bigint, revenue_micros: bigint, missing_value_entries: bigint, };

export type ProviderAttributionQuality = { target: SyncTarget, provider_entries: bigint, mapped_entries: bigint, excluded_entries: bigint, deduplicated_entries: bigint, excluded_conversions_micros: bigint, excluded_revenue_micros: bigint, missing_value_entries: bigint, reasons: Array<ProviderAttributionQualityReason>, };

export type ReportFreshness = { delivery_as_of?: string | null, attribution_as_of?: string | null,
/**
 * Whether the newest delivery publication is more than three hours old.
 * Null for historical ranges, ranges without expected scopes, or when no
 * delivery publication is available.
 */
delivery_is_stale?: boolean | null,
/**
 * Whether the newest attribution publication is more than three hours
 * old. Null has the same unavailable/historical meaning as delivery.
 */
attribution_is_stale?: boolean | null,
/**
 * Requested dates with at least one visible published delivery scope.
 * Dates with no active logical scope are also present because D1 proves
 * those dates are confirmed zero without a provider publication.
 */
delivery_published_days: Array<string>,
/**
 * Closed requested dates for which every expected
 * campaign-revision/audience-segment/target scope has a visible, settled
 * provider publication. Dates with no active logical scope are also
 * present because D1 proves those closed dates are confirmed zero.
 * Callers may zero-fill delivery metrics only for dates in this list.
 */
delivery_covered_days: Array<string>,
/**
 * Requested dates for which every filtered, RBAC-visible
 * campaign-revision/audience-segment/target scope has an atomically
 * published, settled provider snapshot. Dates with no active logical scope
 * are also present because D1 proves those dates are confirmed zero.
 * Callers may zero-fill attributed metrics only for dates in this list.
 */
attribution_published_days: Array<string>,
/**
 * `None` means at least one delivery scope was expected but none was
 * published. `false` means some expected scopes are missing or at least
 * one visible provider scope is partial/unsettled. A closed window with
 * no expected scopes is `Some(true)` and its dates are confirmed zero;
 * the current New York calendar day remains unsettled.
 */
delivery_is_complete?: boolean | null,
/**
 * `None` means at least one filtered/RBAC-visible attribution scope was
 * expected but none was published. `false` means some expected scopes are
 * missing/unsettled or the current New York calendar day remains open. A
 * closed window with no expected scopes is `Some(true)`.
 */
attribution_is_complete?: boolean | null,
/**
 * The current-day TTD delivery lane's configured mode at the time of
 * the read. Today's TTD spend, impressions and clicks are zero unless it
 * is `publish` — `off` (the default) and `shadow` write nothing — and a
 * zero looks identical in every other field, so the reader says why. Null
 * on responses that did not consult the managed config.
 */
ttd_intraday_mode?: TtdIntradayMode | null, delivery_sources: Array<string>,
/**
 * PII-free provider reconciliation warnings (for example a Meta custom
 * conversion id that could not be mapped to a production). Provider rows
 * with unresolved production identity are never silently assigned.
 */
attribution_warning_reasons: Array<string>,
/**
 * Per-provider reconciliation is intentionally exposed only for an
 * unrestricted, semantically unfiltered USD query. Scoped responses leave
 * this empty rather than leaking account-wide diagnostics.
 */
provider_attribution_quality: Array<ProviderAttributionQuality>,
/**
 * Compatibility names retained for existing clients. These now contain
 * the latest unrestricted provider snapshot's mapped and unmapped row
 * counts respectively; SAM no longer computes exposure matches.
 */
matched_exposures: bigint, unmatched_exposures: bigint, };

export type ReportMethodology = {
/**
 * `provider_reported_v1`: Meta/TTD are the sole attribution authority.
 * SAM classifies already-attributed conversions as direct or halo but
 * never joins tickets/impressions to assign credit.
 */
attribution_model: string,
/**
 * Effect scope actually enforced for this response.
 */
attribution_scope: ReportAttributionScope,
/**
 * Zero means the window is provider-declared and can differ by provider
 * or campaign object; it is not a SAM-side attribution lookback.
 */
attribution_window_days: number, timezone: string, currency_policy: string, };

export type ReportQueryResponse = { rows: Array<ReportQueryRow>, totals: ReportMetricSet, next_cursor?: string | null, freshness: ReportFreshness, methodology: ReportMethodology, revision: DataRevisionStamp, };

export type SpilloverByAudienceTypeRow = { audience_type: AudienceType,
/**
 * Provider-attributed conversion count in millionths. This is the
 * authoritative additive value; integer tickets remain for compatibility.
 */
direct_conversions_micros: bigint, halo_conversions_micros: bigint, direct_tickets: bigint, halo_tickets: bigint,
/**
 * Provider-attributed currency units in millionths.
 */
direct_revenue_micros: bigint, halo_revenue_micros: bigint, direct_revenue_cents: bigint, halo_revenue_cents: bigint, };

export type SpilloverSummary = { direct_conversions_micros: bigint, halo_conversions_micros: bigint, direct_tickets: bigint, halo_tickets: bigint, direct_revenue_micros: bigint, halo_revenue_micros: bigint, direct_revenue_cents: bigint, halo_revenue_cents: bigint, by_audience_type: Array<SpilloverByAudienceTypeRow>, };

export type SpilloverTopHaloRow = { advertised_production_id: ProductionId, advertised_name: string, purchased_production_id: ProductionId, purchased_name: string, attributed_conversions_micros: bigint, tickets: bigint, attributed_revenue_micros: bigint, revenue_cents: bigint, };

export type WebEventType = "Purchase" | "Pageview";

export type InternalField = "production" | "show" | "source_reference" | "venue" | "event" | "ticket" | "order" | "currency" | "value" | "units";

export type Transform = "none" | "trim" | "lower" | "upper";

export type PiiField = "email" | "phone" | "first_name" | "last_name" | "street1" | "city" | "state" | "zip";

export type ConsentMode = "always" | "opt_in" | "opt_out";

export type ExtractionRule = {
/**
 * Dot/bracket path into the flattened dataLayer, e.g.
 * `ecommerce.value` or `ecommerce.items[0].item_id`.
 */
source: string, target: InternalField, transform: Transform,
/**
 * Which event type this rule fires for. `None` = every event (shared);
 * `Some(_)` scopes it so a config can diverge purchase vs pageview
 * mappings. Additive + backward-compatible (absent ⇒ `None` ⇒ today's
 * behavior). The engine and the served tag both honor it.
 */
event_type: WebEventType | null, };

export type PiiRule = { source: string, field: PiiField,
/**
 * Which event type this rule fires for. `None` = every event (shared).
 */
event_type: WebEventType | null, };

export type PixelExtraction = {
/**
 * Snapshot `window.dataLayer` (and detected TMS globals) automatically.
 */
auto_harvest: boolean,
/**
 * Explicit dataLayer-path → internal-field rules.
 */
rules: Array<ExtractionRule>,
/**
 * dataLayer-path → PII-channel rules (in addition to the tag's form
 * autodetection, which is browser-side only).
 */
pii_rules: Array<PiiRule>, };

export type PixelPii = {
/**
 * Master switch: harvest any PII at all.
 */
enabled: boolean,
/**
 * Put client-hashed PII on the first-party Meta/TTD beacons.
 */
advanced_matching: boolean,
/**
 * Forward raw PII to the enrich API to resolve `person_id`.
 */
resolve_identity: boolean, consent: ConsentMode,
/**
 * dataLayer path whose truthiness drives `OptIn`/`OptOut`. Required for
 * those modes; ignored for `Always`.
 */
consent_var: string | null, };

export type EventToggle = { purchase: boolean, pageview: boolean, };

export type FiringConfig = { meta: EventToggle, ttd: EventToggle, };

export type RawPii = { email: string | null, phone: string | null, first_name: string | null, last_name: string | null, street1: string | null, city: string | null, state: string | null, zip5: string | null, };

export type MappedFields = { production_id: string | null,
/**
 * EncorePro house code (`productions.source_reference`). Resolved to a
 * `production_id` server-side when no explicit production id is present.
 */
source_reference: string | null, venue_id: string | null, event_id: string | null, ticket_id: string | null, order_id: string | null, currency: string | null, value: number | null, units: number | null, };

export type PageContext = { url: string, referrer: string, title: string, };

export type PixelConsent = {
/**
 * Client-side hashed advanced matching + enrich identity resolution.
 */
ads: boolean,
/**
 * dataLayer var persistence.
 */
analytics: boolean, };

export type CollectRequest = {
/**
 * Browser↔edge wire version. Independent of `WebEventIngestRequest`'s.
 */
schema_version: number, pixel_id: string,
/**
 * Per-hit UUID minted in the browser; becomes `sam.web_events.web_event_id`.
 */
web_event_id: string,
/**
 * The browser's `sam_did` device id (cookie value or freshly minted). The
 * collector prefers this over a server-minted one so the first event's
 * ad-beacon `external_id`/`td4` matches the stored `device_id`.
 */
device_id: string | null, event_type: WebEventType, occurred_at: string, page: PageContext, mapped: MappedFields,
/**
 * Explicit `sam('track', …, {…})` override fields, sent verbatim so the
 * server can honor them ABOVE its own rule re-derivation (`mapped` alone
 * can't distinguish override-sourced from rule-sourced values, and the
 * server-authoritative extraction would otherwise beat a page's explicit
 * override whenever analytics consent ships `vars`).
 */
overrides: MappedFields | null,
/**
 * Bounded NESTED dataLayer snapshot (the collector re-runs the same
 * nested-path extraction engine over it). `Record<string, unknown>` wire.
 */
vars: Record<string, unknown> | null, raw_pii: RawPii | null,
/**
 * Channel-agnostic platform identity (fbp/fbc, click ids, GA ids, …) —
 * additive at v1; the edge re-sanitizes and re-applies consent.
 */
identity: IdentityEnvelope | null, consent: PixelConsent, };

export type ParamSpec = {
/**
 * Canonical key — matches the `MappedFields` field / studio target name.
 */
key: string,
/**
 * Script-tag attribute form (`data-…`), when the parameter can be set
 * statically on the loader tag. `None` for per-event transaction fields.
 */
data_attr: string | null,
/**
 * Key inside `sam('track', event, {…})`. `None` for pixel_id and the
 * pseudo-parameters.
 */
sam_key: string | null,
/**
 * Human label for the builder checkbox row.
 */
label: string,
/**
 * Value shape shown in the reference table.
 */
shape: string,
/**
 * Example value used verbatim in generated snippets.
 */
example: string,
/**
 * `"number"` values are emitted unquoted in JS object literals;
 * everything else is single-quoted.
 */
value_kind: string,
/**
 * Event types for which the parameter is required (`"Purchase"` /
 * `"Pageview"`). Required parameters cannot be toggled off.
 */
required_for: Array<string>,
/**
 * Event types where the parameter is meaningful (drives per-event
 * snippet variants and the reference table).
 */
events: Array<string>,
/**
 * Builder grouping: `"attribution"`, `"transaction"`, or `"privacy"`.
 */
group: string,
/**
 * Initial checkbox state. Default-ON is deliberate: deleting a line is
 * easier than authoring one.
 */
default_on: boolean,
/**
 * Short trailing comment placed next to the parameter in snippets.
 * Empty ⇒ no comment.
 */
snippet_comment: string,
/**
 * Longer description for the reference table.
 */
description: string, };

export type IdentityEnvelope = {
/**
 * Tag-minted per-event UUID — the SAME value the browser Meta beacon
 * sends as `eid=` and the TTD beacon as `td5`. Never consent-cleared
 * (random per event, not an identifier of the person) — it is the Meta
 * CAPI `event_id` dedup key against our own browser pixel.
 */
beacon_event_id: string | null,
/**
 * Meta browser id cookie `_fbp` (`fb.N.<ms>.<random>`), unhashed per CAPI.
 */
fbp: string | null,
/**
 * Meta click cookie `_fbc` (`fb.N.<ms>.<fbclid>`). When absent and
 * `fbclid` is present, the edge synthesizes it via [`synthesize_fbc`].
 */
fbc: string | null,
/**
 * Raw `?fbclid=` landing-page parameter (source material for `fbc`).
 */
fbclid: string | null,
/**
 * Google Ads click id (`?gclid=`).
 */
gclid: string | null,
/**
 * GA4 client id — the last two dot-segments of the `_ga` cookie, or the
 * sGTM event-data `client_id` verbatim.
 */
ga_client_id: string | null,
/**
 * GA4 session id — sGTM event data only (the browser tag never parses
 * the `_ga_<stream>` cookie; its format broke GS1→GS2 in May 2025).
 */
ga_session_id: string | null,
/**
 * TikTok click id (`?ttclid=`) — captured for future TikTok Events API.
 */
ttclid: string | null,
/**
 * TikTok `_ttp` first-party cookie.
 */
ttp: string | null,
/**
 * The Trade Desk impression id captured from a landing-URL creative
 * macro (`?ttd_imp=`); round-trips as `imp` for click-through matching.
 */
ttd_impression_id: string | null, };

export type HashedPii = { email_sha256: string | null, phone_sha256: string | null, first_name_sha256: string | null, last_name_sha256: string | null, city_sha256: string | null, state_sha256: string | null, zip5_sha256: string | null, country_sha256: string | null, };

export type DeviceHint = { sam_did: string | null, ga_client_id: string | null, };

export type ServerCollectRequest = { schema_version: number, pixel_id: string,
/**
 * Absent ⇒ the edge mints a v4 (the sGTM sandbox has no UUID entropy).
 */
web_event_id: string | null, event_type: WebEventType,
/**
 * Epoch ms; the edge clamps to `[now − 7 d, now]` (Meta freshness bound).
 */
occurred_at_ms: bigint, page: PageContext, mapped: MappedFields, overrides: MappedFields | null, vars: Record<string, unknown> | null, raw_pii: RawPii | null, hashed_pii: HashedPii | null, identity: IdentityEnvelope | null, consent: PixelConsent, device: DeviceHint,
/**
 * The site visitor's IP (sGTM `ip_override`) — trusted only because the
 * server key authorized the caller.
 */
client_ip: string | null, user_agent: string | null,
/**
 * 2-alpha ISO country of the visitor; the edge NEVER uses its own
 * request geo here (that would be the GCP egress region).
 */
country: string | null,
/**
 * Meta CAPI Test Events routing code (drills only).
 */
test_event_code: string | null, };

export type IngestChannel = "pixel" | "pixel_sgtm";

export type PixelConversions = {
/**
 * Fire Meta Conversions API events for this pixel's `meta_pixel_id`.
 */
meta_capi: boolean,
/**
 * Fire The Trade Desk offline conversion events (requires UID2 creds).
 */
ttd: boolean,
/**
 * Meta Test Events routing code — drills only; remove after verifying.
 */
test_event_code: string | null,
/**
 * Reserved: set `false` to suppress the browser TTD image beacon if
 * server+browser double-counting is observed. `None` ⇒ beacon stays on.
 */
browser_ttd_beacon: boolean | null, };

export type ConversionSummaryRow = { source: string, channel: string, event_type: string, target: string, status: string,
/**
 * Rows whose status changed (for `delivered`: were sent) in the trailing
 * 24 h, keyed on `updated_at`.
 */
count_24h: bigint,
/**
 * The same over the trailing 168 h: send-time throughput, not sales.
 */
count_7d: bigint,
/**
 * Rows whose sale fell in the last seven completed America/New_York days,
 * keyed on `event_at`: the figure comparable with Meta Events Manager.
 */
count_sale_7d: bigint, };

export type ConversionCoverageRow = { target: string, channel: string, rows: bigint, signal_counts: Array<bigint>, };

export type ConversionRecentRow = { source: string, source_event_id: string, target: string, pixel_id: string, channel: string, event_type: string, status: string, reason: string | null, attempts: bigint,
/**
 * Presence mask aligned to [`CONVERSION_SIGNALS`].
 */
signals: string, last_error: string | null, event_at: bigint, updated_at: bigint, };

export type ConversionsHealthResponse = {
/**
 * Batch-worker snapshot time. The API may cache this response, so the
 * frontend must show this age instead of equating poll cadence with data age.
 */
as_of: string, enabled: boolean,
/**
 * "log_only" | "live" | "disabled".
 */
mode: string, meta_enabled: boolean, ttd_enabled: boolean, queue_depth: bigint, oldest_pending_age_secs: bigint | null,
/**
 * A destination whose delivery breaker is latched OPEN: the provider
 * rejected everything it was sent, so `conversion_delivery` stopped
 * sending and is holding rows in `pending` rather than quarantining them.
 * This is a hard outage, not a degradation — nothing is reaching that
 * destination until the underlying fault is fixed.
 */
meta_circuit_open: boolean, ttd_circuit_open: boolean,
/**
 * The UID2 operator's own breaker (mapping is a different host from TTD's
 * conversion endpoint). Open means TTD rows without stored tokens wait in
 * `pending`; rows that already carry tokens still send.
 */
uid2_circuit_open: boolean,
/**
 * When the earliest still-open breaker tripped. `None` when all are shut.
 */
circuit_open_since: string | null, summary: Array<ConversionSummaryRow>, coverage: Array<ConversionCoverageRow>, recent: Array<ConversionRecentRow>, recent_runs: Array<JobRunSummary>, };

export type FieldCheck = "absent" | "valid" | "invalid";

export type PiiPresence = { email: FieldCheck, phone: FieldCheck, first_name: FieldCheck, last_name: FieldCheck, street1: FieldCheck, city: FieldCheck, state: FieldCheck, zip5: FieldCheck, };

export type HashedPiiPresence = { email_sha256: FieldCheck, phone_sha256: FieldCheck, first_name_sha256: FieldCheck, last_name_sha256: FieldCheck, city_sha256: FieldCheck, state_sha256: FieldCheck, zip5_sha256: FieldCheck, country_sha256: FieldCheck, };

export type IdentityPresence = { beacon_event_id: FieldCheck, fbp: FieldCheck, fbc: FieldCheck, fbclid: FieldCheck, gclid: FieldCheck, ga_client_id: FieldCheck, ga_session_id: FieldCheck, ttclid: FieldCheck, ttp: FieldCheck, ttd_impression_id: FieldCheck, };

export type FlowEventRedacted = {
/**
 * Monotonic tap-buffer sequence — the reveal key.
 */
seq: bigint, captured_at: string,
/**
 * Edge route provenance; serializes `"pixel"` / `"pixel_sgtm"`.
 */
channel: IngestChannel, pixel_id: string, tenant_id: string,
/**
 * Wire event type (`"pageview"` / `"purchase"`).
 */
event_type: string, web_event_id: string, event_at: string, domain: string, country: string, colo: string, ua_family: string, production_id: string | null, source_reference: string | null, venue_id: string | null, ticket_id: string | null,
/**
 * Business data — shown openly (not PII).
 */
value: number | null, currency: string | null, units: number | null, consent: PixelConsent, pii: PiiPresence, hashed_pii: HashedPiiPresence, identity: IdentityPresence,
/**
 * dataLayer snapshot: presence + size ONLY (vars may hold site PII).
 */
vars: FieldCheck, vars_bytes: bigint | null,
/**
 * Flag only — query strings can carry PII.
 */
page_url: FieldCheck,
/**
 * `[]` | `["meta"]` | `["meta","ttd"]`.
 */
conversion_targets: Array<string>,
/**
 * 13-char positional mask aligned to
 * [`crate::types::insights::CONVERSION_SIGNALS`]; `None` when no
 * conversion was seeded.
 */
conversion_signals: string | null,
/**
 * `None` = still pending flush, `Some(false)` = no match / no PII.
 */
person_id_resolved: boolean | null, user_id_present: boolean, };

export type FlowEventRaw = { redacted: FlowEventRedacted, raw_pii: RawPii | null, hashed_pii: HashedPii | null, identity: IdentityEnvelope | null,
/**
 * Raw dataLayer JSON, verbatim.
 */
vars_json: string | null, page_url: string | null, person_id: string | null, user_id: string | null, };

export type FlowLivePage = { as_of: string, latest_seq: bigint,
/**
 * Tap-buffer start ("collecting since") — the buffer is in-memory only.
 */
started_at: string, dropped_captures: bigint, events: Array<FlowEventRedacted>, };

export type FlowRevealResponse = { event: FlowEventRaw, audit_id: string, };

export type TicketFlowRedacted = { ticket_id: string,
/**
 * The SOURCE booking-ref `ticket_id` (repeats across a ticket's sale/refund
 * lines) — the key the pipeline-lineage TRACE keys on (`tkt:{source_ticket_id}`).
 * Distinct from `ticket_id` above, which is the per-line PK (`sam.tickets.id`)
 * used for reveal + as the row key. `None` when the source row had no booking ref.
 */
source_ticket_id: string | null, created_at: string, production_id: string | null, emails: FieldCheck, phones: FieldCheck, name: FieldCheck, address: FieldCheck, amount: FieldCheck, currency: FieldCheck, person_id_resolved: boolean, household_id_present: boolean, anonymous: boolean, };

export type TicketFlowRaw = { redacted: TicketFlowRedacted, customer_emails: Array<string> | null, customer_phones: Array<string> | null, first_name: string | null, last_name: string | null, address_1: string | null, city: string | null, state: string | null, post_code: string | null, country: string | null, amount: number | null, person_id: string | null, household_id: string | null, };

export type TicketFlowPage = { as_of: string, rows: Array<TicketFlowRedacted>, };

export type TicketRevealResponse = { ticket: TicketFlowRaw, audit_id: string, };

export type FlowRateRow = { source: IngestChannel, event_type: string, count_5m: bigint, count_1h: bigint, count_24h: bigint, last_at: string | null, };

export type FlowResolveStats = {
/**
 * Every web event in the hour, of any type.
 */
events_1h: bigint,
/**
 * Events of any type with a matched person.
 */
person_resolved_1h: bigint, user_id_1h: bigint,
/**
 * Purchase events in the hour: the denominator of "Person match · purchases",
 * the same one the history lane divides by.
 */
purchases_1h: bigint,
/**
 * Purchase events in the hour with a matched person.
 */
purchase_person_1h: bigint, };

export type FlowTapStats = { enabled: boolean,
/**
 * Configured in-memory ring capacity. This is safe operational metadata;
 * the buffer contents remain on the batch-worker.
 */
capacity: bigint,
/**
 * Configured privacy TTL for raw revealable events.
 */
max_age_secs: bigint, buffered: bigint, latest_seq: bigint, dropped_captures: bigint, oldest_at: string | null, };

export type FlowTargetStats = {
/**
 * `"meta"` | `"ttd"`.
 */
target: string, pending: bigint, enqueued_1h: bigint, delivered_1h: bigint, failed_1h: bigint, delivered_24h: bigint,
/**
 * Terminal refusals: `failed` and `quarantined`.
 */
failed_24h: bigint,
/**
 * `log_only` rows: built and logged, never sent. Not delivered.
 */
logged_1h: bigint, logged_24h: bigint, };

export type FlowSourceTargetStats = {
/**
 * The queue's `source`: `"web_event"` (pixel lanes) | `"ticket"` (EncorePro).
 */
source: string,
/**
 * `"meta"` | `"ttd"`.
 */
target: string, pending: bigint, enqueued_1h: bigint, delivered_1h: bigint, failed_1h: bigint, delivered_24h: bigint,
/**
 * Terminal refusals: `failed` and `quarantined`.
 */
failed_24h: bigint,
/**
 * `log_only` rows: built and logged, never sent. Not delivered.
 */
logged_1h: bigint, logged_24h: bigint, };

export type FlowConversionsStats = { enabled: boolean,
/**
 * `"log_only"` | `"live"`.
 */
mode: string, meta_enabled: boolean, ttd_enabled: boolean, queue_depth: bigint, oldest_pending_age_secs: bigint | null,
/**
 * Latched delivery-breaker state. This is more authoritative than a
 * trailing failure-rate heuristic for whether a destination is halted now.
 */
meta_circuit_open: boolean, ttd_circuit_open: boolean,
/**
 * The UID2 operator's breaker: tokenless TTD rows wait while it is open.
 */
uid2_circuit_open: boolean, circuit_open_since: string | null,
/**
 * Per destination, every source together.
 */
by_target: Array<FlowTargetStats>,
/**
 * The same counters per (source, destination), read in the same snapshot.
 */
by_source_target: Array<FlowSourceTargetStats>, };

export type FlowJobSchedule = { job_name: string, cron: string, timezone: string, };

export type FlowEncoreStats = { schedule: FlowJobSchedule, };

export type FlowProductionStats = {
/**
 * When the rollup last wrote a row in scope (`max(snapshot_at)`); `None`
 * when it holds none.
 */
last_snapshot_at: string | null,
/**
 * Rows in scope: one per (production, UTC day) over the rolling window.
 */
rows: bigint, };

export type FlowBatchSnapshot = { web_events: Array<FlowRateRow>, resolve_1h: FlowResolveStats, tap: FlowTapStats, conversions: FlowConversionsStats, encorepro: FlowEncoreStats, production_stats: FlowProductionStats, };

export type FlowDisplayPolicy = { source_fresh_secs: bigint, source_quiet_secs: bigint, snapshot_cache_ttl_secs: bigint, run_heartbeat_stale_secs: bigint, };

export type PipelineFlowSnapshot = { as_of: string, display_policy: FlowDisplayPolicy, health: HealthSummary,
/**
 * Server-computed metric bands and thresholds captured alongside the
 * topology. The browser never invents operational cutoffs.
 */
metrics: SystemHealthResponse, batch: FlowBatchSnapshot,
/**
 * The newest TERMINAL run in scope: unscoped, the newest scheduled run
 * (never a manual one, the rule ingest health uses); scoped to a
 * production, the newest run that covers it (`production_id` NULL or that
 * production).
 */
last_ingest_run: IngestRun | null,
/**
 * A run in scope that is still `running` (else `queued`), under the same
 * scope rule; `None` when nothing is in flight.
 */
running_ingest_run: IngestRun | null, };

export type HistoryBucket = "hour" | "day";

export type WebEventsHistoryRow = { bucket: string, source: IngestChannel, event_type: string, total: bigint,
/**
 * Identity envelope present.
 */
identity_ct: bigint,
/**
 * Person matched.
 */
person_ct: bigint,
/**
 * PII flowed (user hash present).
 */
user_ct: bigint, vars_ct: bigint, value_ct: bigint, currency_ct: bigint,
/**
 * Price present: a value AND a currency on the same event.
 */
priced_ct: bigint, };

export type WebEventsHistoryResponse = { as_of: string, bucket: HistoryBucket, rows: Array<WebEventsHistoryRow>,
/**
 * True when the row cap cut the response: the totals are short.
 */
truncated: boolean, };

export type TicketsHistoryRow = { bucket: string, total: bigint, email_ct: bigint, phone_ct: bigint, person_ct: bigint, household_ct: bigint, anon_ct: bigint, amount_ct: bigint,
/**
 * A usable contact: an email OR a phone on the ticket.
 */
contact_ct: bigint, };

export type TicketsHistoryResponse = { as_of: string, bucket: HistoryBucket, rows: Array<TicketsHistoryRow>,
/**
 * True when the row cap cut the response: the totals are short.
 */
truncated: boolean,
/**
 * Distinct days of the daily rollup (`production_stats_daily`) with rows
 * in the range, scoped to the production when the request is. Rollup days
 * are UTC dates; a day counts when its UTC midnight is in `[from, to)`.
 */
stats_days: bigint, };

export type ConversionsHistoryRow = { bucket: string,
/**
 * The queue's `source`: `"web_event"` | `"ticket"`. A (bucket, target,
 * status) cell is split by source; sum over it for the per-target figure.
 */
source: string,
/**
 * `"meta"` | `"ttd"`.
 */
target: string,
/**
 * `"delivered"` | `"logged"` | `"failed"` | `"quarantined"` |
 * `"skipped"` | `"pending"`.
 */
status: string, n: bigint,
/**
 * Rows in this cell whose delivery resolved a UID2 token.
 */
uid2_ct: bigint, };

export type ConversionsHistoryResponse = { as_of: string, bucket: HistoryBucket, history_floor: string | null, rows: Array<ConversionsHistoryRow>, };

export type LineageStage = "ingest" | "enrich" | "enqueue" | "meta_build" | "meta_send" | "uid2_map" | "ttd_build" | "ttd_send";

export type LineageStatus = "ok" | "pending" | "skipped" | "quarantined" | "failed" | "retrying";

export type PipelineLineageRow = {
/**
 * Per-emit ULID — a stable React key and tie-breaker for same-`ts` rows.
 */
lineage_id: string, correlation_id: string,
/**
 * `"web_event"` | `"ticket"`.
 */
source: string,
/**
 * `web_event_id` | `ticket_id`.
 */
source_event_id: string, stage: LineageStage, status: LineageStatus, ts: string,
/**
 * `"meta"` | `"ttd"` for the delivery stages; `None` otherwise.
 */
target: string | null,
/**
 * Delivery attempt number (from the queue row's `attempts`).
 */
attempt: number | null,
/**
 * Machine-readable reason: `delivered`/`logged`/`stale`/`no_destination`/
 * `no_uid2`/`bad_payload`/`meta_400`/`ttd_failed_lines`/`max_attempts`/…
 */
outcome: string | null,
/**
 * Sanitized platform error text (never PII); source truncates to 300.
 */
error_summary: string | null, http_status: number | null, fbtrace_id: string | null, tenant_id: string | null, pixel_id: string | null,
/**
 * `"pixel"` | `"pixel_sgtm"` | `"ticket"`.
 */
channel: string | null, event_type: string | null,
/**
 * = `ticket_id` (the platform dedup key).
 */
order_id: string | null, event_id: string | null, production_id: string | null, venue_id: string | null, value: number | null, currency: string | null, units: number | null,
/**
 * 13-char positional mask aligned to [`crate::CONVERSION_SIGNALS`].
 */
signals: string | null, has_email_hash: boolean | null, has_phone_hash: boolean | null, has_name_hash: boolean | null,
/**
 * UID2 tokens returned by the map call (`0` = mapped nothing).
 */
uid2_token_count: number | null, person_id_present: boolean | null, household_id_present: boolean | null, user_id_present: boolean | null,
/**
 * `error_summary` in words (`crate::failure_copy::describe_conversion`),
 * built at read time; `None` when the row carries no error.
 */
failure?: FailureView | null, };

export type DestinationFailureKind = "terminal" | "retrying" | "resolved";

export type LineageDestination = {
/**
 * `"meta"` | `"ttd"`.
 */
target: string,
/**
 * The queue row's status: `"pending"` | `"sending"` (in flight) or
 * `"delivered"` | `"logged"` | `"failed"` | `"quarantined"` | `"skipped"`
 * (terminal).
 */
status: string,
/**
 * When the queue row last changed. A terminal status is committed before
 * the lineage row recording that attempt is flushed, so a trace keeps
 * polling for a short while after it to let the timeline catch up.
 */
updated_at: string,
/**
 * The machine reason the queue recorded (`stale`, `no_destination`,
 * `no_uid2`, `refunded`, `meta_400`, `max_attempts`, …).
 */
reason?: string | null,
/**
 * When the queue row was created.
 */
created_at?: string | null,
/**
 * When the next attempt is due; meaningful only while `pending`.
 */
next_attempt_at?: string | null,
/**
 * The queue's `attempts`: how many attempts failed and were recorded.
 * A first-send success and an immediate quarantine never add to it, so it
 * is not a count of sends.
 */
retry_failures?: number | null,
/**
 * The production the queued payload names, for authorization. The payload
 * itself never leaves batch.
 */
production_id?: string | null,
/**
 * How the recorded error stands against the current status; `None` when
 * the row has no error.
 */
failure_kind?: DestinationFailureKind | null,
/**
 * The recorded error in words.
 */
failure?: FailureView | null, };

export type LineageWindow = {
/**
 * The oldest instant the read may return (`as_of` − 30 days).
 */
query_floor: string, earliest_returned: string | null, latest_returned: string | null,
/**
 * Rows returned (after any unreadable row was skipped).
 */
returned: number,
/**
 * Older observations inside the window exist but were not returned.
 */
truncated: boolean, };

export type LineageSubject = { production_id: string | null,
/**
 * `None` when the catalog has no row for the id.
 */
production_name: string | null, venue_id: string | null, venue_name: string | null, venue_city: string | null, event_id: string | null,
/**
 * The performance's start, from the catalog.
 */
performance_at: string | null,
/**
 * The EncorePro order (`tickets.order_id`), `None` when the lines carry
 * none or disagree (`orders_mixed`).
 */
order_id: string | null, orders_mixed: boolean,
/**
 * More than one production across the lines and rows.
 */
productions_mixed: boolean,
/**
 * The catalog disagrees with itself: the event names another production or
 * venue than the ticket does.
 */
catalog_mismatch: boolean,
/**
 * The platforms' dedup key (= `ticket_id` / `web_event_id`); the lineage
 * rows' `order_id` holds this, not the EncorePro order.
 */
conversion_key: string | null, };

export type TicketLineKind = "refund" | "sale";

export type Uid2State = { "state": "none" } | { "state": "unreadable" } | { "state": "parsed", elements: number,
/**
 * Elements carrying a token.
 */
tokens: number,
/**
 * Neither a token nor a refresh time: never mapped (or re-pended).
 */
pending: number,
/**
 * No token but a refresh time: UID2 declined to map the identity.
 */
declined: number,
/**
 * More identities than the refresh maps (`MAX_IDENTITIES_PER_ROW`): it
 * strips such an account's tokens and maps none of its identities. An
 * account can be oversized and still hold tokens until that refresh.
 */
oversized: boolean, };

export type BuyerState = { "state": "no_user_id" } | { "state": "unavailable" } | { "state": "known", first_seen: string, last_seen: string, person_resolved: boolean, household_present: boolean, uid2: Uid2State,
/**
 * `users.uid2_minted_at`: set by a mapping pass or a strip, cleared when
 * ingest appends an identity, left alone by a partial pass. A processing
 * checkpoint, not "when the token was minted".
 */
processing_checkpoint: string | null,
/**
 * `users.uid2_refresh_from_at`: the earliest refresh time UID2 gave.
 */
retry_checkpoint: string | null, };

export type TicketLineState = { line_id: string, kind: TicketLineKind, type_name: string | null,
/**
 * `created_at_datetime`: when EncorePro created the line.
 */
created_in_source: string,
/**
 * `last_saved`: when EncorePro last saved it.
 */
last_saved_in_source: string,
/**
 * `written_at`: when SAM last stored it. There is no first-stored time.
 */
last_stored_in_sam: string, is_free: boolean | null, amount_present: boolean, production_id: string | null, venue_id: string | null, event_id: string | null, order_id: string | null, person_resolved: boolean, household_present: boolean, buyer: BuyerState, };

export type TicketIneligibleReason = "no_amount" | "no_net_units" | "negative_net";

export type TicketEligibility = { eligible: boolean, reason: TicketIneligibleReason | null, net_units: number, net_amount_cents: bigint | null, currency: string | null, };

export type TicketLookup = "found" | "not_found" | "incomplete" | "no_tenant" | "not_a_uuid";

export type TicketCurrentState = {
/**
 * At most [`TICKET_TRACE_SHOWN_LINES`], oldest first.
 */
lines: Array<TicketLineState>,
/**
 * The earliest `created_at_datetime` and latest `last_saved` over every
 * line, not only the shown ones.
 */
first_created_in_source: string, last_saved_in_source: string, lines_total: number, lines_truncated: boolean, sale_lines: number, refund_lines: number, eligibility: TicketEligibility,
/**
 * Every distinct production, venue, event and order over all the lines.
 */
production_ids: Array<string>, venue_ids: Array<string>, event_ids: Array<string>, order_ids: Array<string>, };

export type PipelineLineageResponse = { as_of: string, correlation_id: string, rows: Array<PipelineLineageRow>,
/**
 * Every destination the item is queued for, read from the conversion queue
 * at `as_of`. Empty means the queue holds no row for it: never enqueued
 * (not a conversion, not yet enqueued, or an `unk:` id) or already removed
 * by `conversions_gc` (terminal rows past retention). With lineage rows
 * present and none in flight, treat it as historical and terminal.
 */
destinations: Array<LineageDestination>,
/**
 * What the lineage read covered.
 */
window?: LineageWindow | null,
/**
 * The item in names (the API fills it).
 */
subject?: LineageSubject | null,
/**
 * The ticket lane's current-state read; `None` on the web lane.
 */
ticket_lookup?: TicketLookup | null, ticket?: TicketCurrentState | null,
/**
 * The tenant this lane's records must carry: the deployment's EncorePro
 * tenant for a `tkt:` trace, the pixel's tenant for a `web:` one. `None`
 * when it cannot be known (no tenant configured, no ingest row retained).
 */
lane_tenant?: string | null,
/**
 * Retained rows from another tenant than the lane's, left out of the
 * subject.
 */
foreign_tenant_rows: number,
/**
 * Queue rows from another tenant than the lane's, dropped.
 */
foreign_tenant_destinations: number, };

export type ExplainKind = "chart_point" | "alert" | "trace" | "ingest_run" | "production";

export type ExplainParams = { "kind": "chart_point",
/**
 * History lane (`"web_events"` / `"tickets"` / `"conversions"`).
 */
lane: string,
/**
 * Metric within the lane (e.g. `"total"`, `"person_ct"`).
 */
metric: string,
/**
 * Bucket start instant.
 */
bucket: string,
/**
 * Bucket granularity — reuses the flow-lane vocabulary.
 */
granularity: HistoryBucket,
/**
 * Optional production filter active on the chart.
 */
production_id: string | null, } | { "kind": "alert", alert_id: string, } | { "kind": "trace", trace_id: string, } | { "kind": "ingest_run", run_id: string, } | { "kind": "production", production_id: string, };

export type ExplainRequest = { params: ExplainParams, };

export type EvidenceItem = {
/**
 * Citation key, unique within the response (e.g. `"ev1"`).
 */
id: string,
/**
 * Evidence category (e.g. `"metric"`, `"delta"`, `"log"`, `"run"`).
 */
kind: string,
/**
 * Short human label ("Purchases, 2–3 PM").
 */
label: string,
/**
 * Pre-formatted value, verbatim source for any numeral in claim text.
 */
value: string,
/**
 * Optional in-app deep link to the underlying record/screen.
 */
link: string | null, };

export type ExplainClaim = { text: string,
/**
 * [`EvidenceItem::id`]s backing this claim; never empty on the wire.
 */
refs: Array<string>,
/**
 * Optional DISPLAY anchor to a [`Visual::id`] ("— see chart"). Never part
 * of the citation/backing check (that stays `refs`-only); the server drops
 * an unknown value but keeps the claim. `default` so pre-feature cached
 * rows still deserialize.
 */
visual_ref: string | null, };

export type NextStep = { text: string,
/**
 * `"deterministic"` | `"model"`.
 */
source: string, };

export type PipelineStage = "ingest" | "enrich" | "enqueue" | "meta" | "ttd";

export type VisualKind = "bars" | "line" | "coverage";

export type VisualSeries = {
/**
 * Series label (e.g. "email", "person"). Redacted at bundle build.
 */
name: string,
/**
 * CSS color token the frontend applies (e.g. `"var(--good)"`).
 */
color: string,
/**
 * One value per parent `buckets` entry.
 */
values: Array<number>, };

export type Visual = {
/**
 * Citation/anchor key, unique within the response (e.g. `"v1"`).
 */
id: string, kind: VisualKind,
/**
 * Chart title.
 */
label: string,
/**
 * Optional one-line reading beneath the title.
 */
caption: string | null,
/**
 * X-axis labels (e.g. `"Jul 7"`), positionally aligned with each series.
 */
buckets: Array<string>,
/**
 * Plotted series (`Coverage` / `Bars` / `Line` all read these).
 */
series: Array<VisualSeries>,
/**
 * Optional unit suffix (`"%"`, `"ms"`, `""`).
 */
unit: string | null,
/**
 * For `Coverage`: the dashed house-average line (0–100), when known.
 */
avg: number | null,
/**
 * Optional in-app deep link (same role as `EvidenceItem::link`).
 */
link: string | null, };

export type StageAttribution = { stage: PipelineStage, band: StatusBand,
/**
 * 1-based; 1 = most implicated. Ranked worst-first, ties broken by pipeline order.
 */
rank: number,
/**
 * Derived: `rank == 1` AND `band` is a real breach (`Warn`/`Critical`).
 * An `Unknown`/stale stage never flags a culprit ("we don't know" ≠ "the
 * problem"), even though `Unknown` outranks `Ok`.
 */
most_implicated: boolean,
/**
 * Deterministic, PII-free reason ("Meta CAPI failure rate 12%").
 */
detail: string,
/**
 * Id of the [`EvidenceItem`] carrying this stage's metric, when present.
 */
evidence_ref: string | null, };

export type StageVerdict = {
/**
 * The five stages in pipeline order (Ingest → … → Ttd).
 */
stages: Array<StageAttribution>,
/**
 * The most-implicated stage, when one stands out above `Ok`.
 */
most_implicated: PipelineStage | null,
/**
 * Deterministic one-line summary ("Meta CAPI send is the likely stage").
 */
summary: string, };

export type ExplainResponse = {
/**
 * Echo of the request discriminant (see [`ExplainKind`]).
 */
kind: ExplainKind, generated_at: string,
/**
 * Model identifier that produced the prose (or the fallback marker).
 */
model: string,
/**
 * Prompt-template version — bump invalidates caches.
 */
prompt_version: number,
/**
 * `true` when served from cache rather than freshly generated.
 */
cached: boolean,
/**
 * One-line summary; same verbatim-numeral rule as `claims`.
 */
headline: string,
/**
 * Cited claims, in display order.
 */
claims: Array<ExplainClaim>,
/**
 * The deterministic facts the claims cite.
 */
evidence: Array<EvidenceItem>,
/**
 * Server-built charts the model may cite by id (never authored by it).
 * `default` so pre-feature cached rows still deserialize.
 */
visuals: Array<Visual>,
/**
 * The deterministic ranked stage verdict, when this surface localizes.
 * Drives the highlighted pipeline diagram; present even when `degraded`.
 */
verdict: StageVerdict | null, next_step: NextStep | null,
/**
 * `"high"` | `"low"` — the server's own confidence in the explanation.
 */
confidence: string,
/**
 * `true` = deterministic fallback (model unavailable/failed validation).
 */
degraded: boolean, };

export type BriefingCardStatus = "healthy" | "watch" | "attention";

export type BriefingFlightEvent = { campaign_id: string, campaign_name: string, production_name: string, channel: Channel, date: string, link: string, };

export type BriefingToday = {
/**
 * "Friday, Sep 18"
 */
date_label: string, launches: Array<BriefingFlightEvent>, endings: Array<BriefingFlightEvent>,
/**
 * Performances across the narrated shows today.
 */
performances: number, invoices_due: number, open_alerts: bigint, attention_cards: number, };

export type BriefingPerformance = { time: string, sold: string | null, status: string, };

export type BriefingSales = { tickets_7d: bigint | null, tickets_prev_7d: bigint | null, tickets_yesterday: bigint | null, revenue_7d_cents: bigint | null, performances_today: Array<BriefingPerformance>, next_performance_at: string | null, on_sale_7d: bigint, };

export type BriefingCampaign = { campaign_id: string, name: string, channel: Channel, status: CampaignStatus, operational: CampaignOperationalStatus | null, pacing: CampaignOverviewPacing, budget_cents: bigint, gross_spend_cents: bigint | null, impressions: bigint | null, clicks: bigint | null, ctr: number | null, cpm_cents: number | null,
/**
 * Retired: always `null` (see [`retired_field`]).
 */
tickets?: null | null,
/**
 * Retired: always `null`.
 */
roas?: null | null,
/**
 * Direct sales flight to date, the figures `/reports` → Performance shows for
 * this campaign: tickets and value in millionths, and the gross spend on the
 * attributed scopes that CPA and ROAS divide. `None` until attribution is
 * published for the campaign.
 */
direct_tickets_micros?: bigint | null, direct_ticket_value_micros?: bigint | null, attribution_gross_spend_cents?: bigint | null, start_date: string, end_date: string, next_flight_date?: string | null,
/**
 * Why this campaign needs a look, set by rule.
 */
attention: string | null, link: string, };

export type BriefingProductionCard = {
/**
 * The citation area id (`prod_a`…), never the production's uuid.
 */
id: string, production_id: string, name: string, tag: string | null, venue: string | null, opens: string | null, closes: string | null, status: BriefingCardStatus,
/**
 * "NEEDS A LOOK" · "WATCH" · "ON TRACK" · "NO ADS RUNNING" · "STARTS SEP 24"
 */
status_label: string, status_reason: string | null,
/**
 * Whether the model was asked to write about this card.
 */
narrated: boolean, sales: BriefingSales, gross_spend_cents: bigint, budget_cents: bigint, margin_cents: bigint,
/**
 * Retired: always `null` (see [`retired_field`]).
 */
tickets?: null | null,
/**
 * Retired: always `null`.
 */
ticket_value?: null | null,
/**
 * Retired: always `null`.
 */
roas?: null | null,
/**
 * The show's direct sales across ALL its campaigns (not only the listed
 * ones), flight to date: [`BriefingCampaign`]'s fields summed. `None` when
 * none of its campaigns has published attribution.
 */
direct_tickets_micros?: bigint | null, direct_ticket_value_micros?: bigint | null, attribution_gross_spend_cents?: bigint | null,
/**
 * Planned spend and impressions across all its campaigns, summed before
 * the campaign list is shortened, so the card's pace and totals cover
 * every campaign.
 */
planned_spend_cents?: bigint | null, impressions?: bigint | null, campaigns: Array<BriefingCampaign>, more_campaigns: number,
/**
 * The model's sentences for this card; refs resolve against
 * [`DailyBriefing::evidence`].
 */
claims: Array<ExplainClaim>, visuals: Array<Visual>, link: string, };

export type BriefingInvoice = { id: string, period: string, reference: string, state: string, total_cents: bigint, amount_due_cents: bigint | null,
/**
 * `YYYY-MM-DD`, from Xero.
 */
due: string | null,
/**
 * Whole days past `due` as of the briefing's day, only while an amount
 * is still owed; `None` when the invoice is not overdue.
 */
days_past_due: bigint | null, verdict: string | null, link: string, };

export type BriefingMoney = {
/**
 * `2026-W38`
 */
period: string,
/**
 * `Week 38, 2026 (Sep 14-20)`
 */
period_label: string, through: string, gross_cents: bigint, media_cents: bigint, fee_cents: bigint, fee_bps: number, margin_cents: bigint,
/**
 * Every invoice not yet paid, voided or skipped, newest period first.
 */
invoices: Array<BriefingInvoice>,
/**
 * Sum of what Xero still shows as due across `invoices`.
 */
outstanding_cents: bigint, outstanding_count: number,
/**
 * The part of `outstanding_cents` whose due date has passed.
 */
past_due_cents: bigint, past_due_count: number, };

export type BriefingAction = { severity: BriefingCardStatus,
/**
 * `campaign` | `invoice` | `alert` | `system`
 */
kind: string, text: string,
/**
 * The second line: why, how bad, since when — an alert's severity and
 * category, a campaign's reason, an invoice's state.
 */
detail: string | null,
/**
 * `RETRY` · `APPROVE` · `REVIEW` · `RESEND` · `ACK` · `CHECK` · `PUBLISH` · `RESUME`
 */
verdict: string, link: string, };

export type BriefingSystemNote = { ok: boolean, text: string, ingest_lag_hours: bigint | null, delivery_through: string | null, attribution_through: string | null, link: string, };

export type DailyBriefing = {
/**
 * [`DAILY_BRIEFING_SCHEMA`]; required so a legacy row fails to parse.
 */
schema: number,
/**
 * [`BRIEFING_BASIS`]; empty on a briefing stored before it existed,
 * which is never served.
 */
basis: string,
/**
 * The morning this is for, `YYYY-MM-DD` in America/New_York.
 */
day: string, generated_at: string, model: string, prompt_version: number,
/**
 * `true` when the model failed and the page carries no prose.
 */
degraded: boolean,
/**
 * `"high"` | `"low"`
 */
confidence: string, headline: string, one_thing: ExplainClaim | null, summary: Array<ExplainClaim>,
/**
 * Every citable item, hero and cards and money alike.
 */
evidence: Array<EvidenceItem>, today: BriefingToday,
/**
 * Worst first, then by spend; capped (the rest count in `more_productions`).
 */
productions: Array<BriefingProductionCard>, more_productions: number,
/**
 * Present for admins only.
 */
money: BriefingMoney | null, needs_human: Array<BriefingAction>,
/**
 * Rows the `needs_human` cap left out, the least severe. Absent from a
 * briefing stored before it existed, where it reads 0.
 */
more_needs_human: number, system: BriefingSystemNote,
/**
 * Delivery channels the briefing went out on (e.g. `"email"`).
 */
delivered: Array<string>, };

export type GenerationOutcome = "ok" | "degraded" | "failed" | "skipped";

export type LastGeneration = { finished_at: string, outcome: GenerationOutcome, };

export type CopilotStatus = { enabled: boolean, briefing: DailyBriefing | null,
/**
 * A briefing is being written now. Absent from a status written before
 * 2026-11-01 (an older batch worker, a stored fixture), where it reads false.
 */
generating: boolean,
/**
 * When the run writing it started (the lease's acquisition); `None` when
 * nothing is writing.
 */
generating_since: string | null,
/**
 * The newest finished briefing run; `None` before the first.
 */
last_generation: LastGeneration | null, };

export type RegenerateStarted = { started: boolean,
/**
 * When the run now writing the briefing started, this one's or the one
 * already running; `None` when that could not be read.
 */
generating_since: string | null, };

export type LlmUsageTotals = {
/**
 * HTTP attempts, failed ones included.
 */
calls: bigint,
/**
 * Uncached input: the tail after the cache breakpoint.
 */
input_tokens: bigint, cache_write_5m_tokens: bigint, cache_write_1h_tokens: bigint, cache_read_tokens: bigint,
/**
 * Output, thinking included.
 */
output_tokens: bigint,
/**
 * The part of `output_tokens` spent thinking. `None` on a model row: the API reports it
 * for a whole attempt, not per model.
 */
thinking_tokens: bigint | null,
/**
 * Cache reads over every prompt token (uncached input, cache writes and cache reads),
 * 0 to 1; `None` with no prompt tokens at all.
 */
cache_hit_share: number | null,
/**
 * The sum of the known costs, micro-dollars (`1_000_000` = $1). A lower bound when
 * `unknown_cost_calls` is above zero.
 */
cost_micros: bigint,
/**
 * Attempts whose cost is unknown and so left out of `cost_micros`.
 */
unknown_cost_calls: bigint, };

export type LlmTaskUsage = {
/**
 * `daily_briefing` or `explain`.
 */
task: string, usage: LlmUsageTotals, };

export type LlmModelUsage = {
/**
 * The Claude API model id (`claude-opus-5-5`, or a fallback target such as
 * `claude-opus-5`).
 */
model: string, usage: LlmUsageTotals, };

export type LlmLastCall = { at: string,
/**
 * `daily_briefing` or `explain`.
 */
task: string,
/**
 * `ok`, `refused`, `truncated`, `invalid`, or `error:<kind>` (`error:timeout`,
 * `error:rate_limit_error`, …).
 */
outcome: string, };

export type LlmUsageResponse = {
/**
 * When the readout was taken.
 */
as_of: string,
/**
 * The start of the current calendar month in America/New_York.
 */
month_start: string,
/**
 * `[month_start, as_of]`.
 */
month_to_date: LlmUsageTotals,
/**
 * `[as_of − 30 days, as_of]`.
 */
last_30_days: LlmUsageTotals,
/**
 * Month to date, by task (task order).
 */
by_task: Array<LlmTaskUsage>,
/**
 * Month to date, by model (model id order).
 */
by_model: Array<LlmModelUsage>,
/**
 * `None` before the first call.
 */
last_call: LlmLastCall | null,
/**
 * The day the list prices behind every cost were read (`YYYY-MM-DD`): costs are
 * estimates from them, not Anthropic's invoice.
 */
prices_as_of: string, };

export type InvoiceState = "pending_review" | "approved" | "xero_created" | "pdf_stored" | "emailed" | "sent" | "paid" | "voided" | "skipped" | "failed";

export type InvoiceActionKind = "generate_period" | "approve_send" | "resend_email" | "sync_xero" | "mark_voided" | "raise_adjustment";

export type InvoiceActionStatus = "queued" | "running" | "done" | "failed";

export type InvoiceEmailStatus = "not_sent" | "delivered" | "queued" | "bounced" | "failed" | "unknown";

export type InvoiceAdjustmentStatus = "settled" | "accumulating" | "awaiting_settlement" | "adjustment_open" | "credit_needed";

export type InvoiceSummary = { id: string,
/**
 * `YYYY-Www`, the ISO week (Monday–Sunday) the invoice covers.
 */
period: string, state: InvoiceState,
/**
 * The step a `failed` invoice will resume at.
 */
failed_from_state: InvoiceState | null,
/**
 * The Xero `Reference`, e.g. `SMART-2026-W37`.
 */
reference: string, currency: string, line_count: bigint, total_cents: bigint,
/**
 * Digest of the frozen lines. An approval names the fingerprint the
 * admin reviewed, and the executor refuses when the row has moved on.
 */
basis_fingerprint: string,
/**
 * The management-fee rate the invoice was frozen at, in basis points.
 */
management_fee_bps: number,
/**
 * Completeness labels recorded when the basis was frozen. Never a
 * filter: the operator reads them before approving.
 */
warnings: Array<string>, xero_invoice_id: string | null, xero_invoice_number: string | null,
/**
 * `DRAFT` / `SUBMITTED` / `AUTHORISED` / `PAID` / `VOIDED` / `DELETED` as
 * Xero last reported it.
 */
xero_status: string | null, xero_amount_due_cents: bigint | null, xero_amount_paid_cents: bigint | null,
/**
 * `YYYY-MM-DD` as Xero reports it.
 */
xero_fully_paid_on: string | null, xero_online_url: string | null, xero_last_synced_at: string | null, pdf_available: boolean, email_status: InvoiceEmailStatus, email_attempts: bigint, email_delivery_checked_at?: string, email_delivery_error_kind?: string, last_emailed_at: string | null,
/**
 * Set when the week's delivery restated after the lines were frozen and
 * the invoice had already left `pending_review`.
 */
basis_drift_detected_at: string | null,
/**
 * Current basis total minus the frozen total, in cents.
 */
basis_drift_total_cents: bigint | null,
/**
 * Non-financial reporting restatement; never a billing-drift alert.
 */
basis_reporting_changed_at?: string, basis_reporting_impressions_delta?: bigint, approved_at: string | null, approved_by_name: string | null, auto_approved: boolean, emailed_at: string | null, sent_at: string | null, paid_at: string | null, voided_at: string | null, skipped_at: string | null, last_error_kind: string | null,
/**
 * Sanitized before it is stored; safe to render.
 */
last_error: string | null, last_error_at: string | null,
/**
 * The failure the invoice is in now, in words (`shared::failure_copy`): set while it
 * is `failed`, or `emailed` with Xero's sent flag still pending. A sent invoice never
 * carries one, whatever `last_error` still holds.
 */
failure?: FailureView, created_at: string, updated_at: string,
/**
 * 0 is the week's invoice; 1, 2, … are adjustments to it
 * (`SMART-YYYY-Www-ADJn`), each billing only what the settled basis
 * added after everything before it was sent.
 */
adjustment_seq: number,
/**
 * The week's invoice an adjustment supplements; `None` on the week's own.
 */
adjusts_invoice_id?: string,
/**
 * That invoice's number (`SHUBERT-0008`), for the "Adjustment 1 to …" chip.
 */
adjusts_invoice_number?: string,
/**
 * That invoice's reference (`SMART-2026-W39`).
 */
adjusts_reference?: string,
/**
 * That invoice's state. An adjustment is approved, retried or resent
 * only while it is `sent` or `paid`
 * (`shared::invoicing::adjustment_original_payable`), so the drawer can
 * refuse first what both workers refuse.
 */
adjusts_state?: InvoiceState,
/**
 * On the week's invoice: cents billed above the settled basis on the
 * campaigns that fell (positive), when the basis decreased or moved both
 * ways. Never invoiced: a human issues a credit note in Xero.
 */
basis_credit_cents?: bigint, basis_credit_detected_at?: string,
/**
 * On the week's invoice: where it stands against its settled basis
 * (`shared::invoicing::adjustment_status`). `None` on an adjustment.
 */
adjustment_status?: InvoiceAdjustmentStatus, };

export type InvoiceLine = { line_no: bigint, campaign_id: string,
/**
 * Every campaign revision that carried spend in the week.
 */
campaign_revisions: Array<bigint>, campaign_name: string, production_id: string, production_name: string, channel: Channel,
/**
 * Client gross: `SUM(billable_spend_cents)` over the week — the sum
 * of the media line and the fee line.
 */
spend_cents: bigint,
/**
 * The management fee frozen on this line (its own Xero line, the MGMT
 * item); the media line is `spend_cents - fee_cents`.
 */
fee_cents: bigint, markup_cents: bigint, impressions: bigint, clicks: bigint, days_with_spend: bigint,
/**
 * Exactly the `LineItem.Description` sent to Xero.
 */
description: string,
/**
 * On an adjustment line: the gross billed before (the week's invoice
 * plus earlier adjustments). `spend_cents` is then the additional gross,
 * `final_spend_cents - adjusts_spend_cents`.
 */
adjusts_spend_cents?: bigint,
/**
 * On an adjustment line: the impressions invoiced before.
 */
adjusts_impressions?: bigint,
/**
 * On an adjustment line: the settled gross the adjustment brings the
 * week to.
 */
final_spend_cents?: bigint,
/**
 * On an adjustment line: the settled impressions.
 */
final_impressions?: bigint, };

export type InvoiceEmailAttempt = { id: string, attempt: bigint,
/**
 * `initial` or `resend`.
 */
kind: string,
/**
 * `sending` / `sent` / `failed` / `unknown`.
 */
status: string, to: Array<string>, cc: Array<string>,
/**
 * How many addresses `to` and `cc` hold. An API key or Claude reads this in place of
 * the lists, which it receives empty (no address ever reaches a credential).
 */
recipient_count?: number, subject: string, http_status: bigint | null, delivered: Array<string>, permanent_bounces: Array<string>, queued: Array<string>,
/**
 * Dropped by the provider's suppression list: never delivered.
 */
suppressed: Array<string>, provider_message_id: string | null, delivery_status?: InvoiceEmailStatus, failed?: Array<string>, delivery_checked_at?: string, delivery_error_kind?: string, error_kind: string | null, requested_by_name: string | null, created_at: string, sent_at: string | null,
/**
 * This attempt's send failure in words (refused, or an outcome that was lost).
 */
failure?: FailureView,
/**
 * Why delivery could not be confirmed, in words, when the check itself is failing
 * (`delivery_error_kind`); never a statement about the recipients.
 */
delivery_failure?: FailureView, };

export type InvoiceAction = { id: string, kind: InvoiceActionKind, status: InvoiceActionStatus, period: string | null, invoice_id: string | null,
/**
 * `None` when the system queued it (auto-approve, cron).
 */
requested_by_name: string | null, result_json: Record<string, unknown> | null, error_kind: string | null, error_message: string | null, created_at: string, started_at: string | null, finished_at: string | null,
/**
 * A failed action's failure in words.
 */
failure?: FailureView,
/**
 * A failed action that a later step made moot: the invoice went past the step it
 * failed at, or a later action of the same kind on the same target finished. History,
 * not something to act on.
 */
resolved: boolean,
/**
 * A failed action whose invoice was rebuilt afterwards (Generate period or Raise
 * adjustment re-froze it): the failure belongs to a version that no longer exists.
 */
superseded: boolean, };

export type InvoicingSettings = { email_delivery_tracking_enabled: boolean, enabled: boolean, auto_approve: boolean,
/**
 * 1 = Monday … 7 = Sunday: when a closed week is frozen for review.
 */
run_weekday: number,
/**
 * `recipients_to` + `recipients_cc`.
 */
recipient_count: number,
/**
 * The configured fee rate a new invoice would be frozen at.
 */
management_fee_bps: number,
/**
 * `invoicing.adjustments_enabled`: the daily scan raises adjustments.
 */
adjustments_enabled: boolean,
/**
 * `invoicing.adjustment_min_cents`: a week's net increase below this
 * keeps accumulating.
 */
adjustment_min_cents: number, };

export type InvoiceListResponse = { invoices: Array<InvoiceSummary>,
/**
 * The most recent actions across every invoice, newest first.
 */
actions: Array<InvoiceAction>, settings: InvoicingSettings, };

export type InvoiceDetailResponse = { invoice: InvoiceSummary, lines: Array<InvoiceLine>, emails: Array<InvoiceEmailAttempt>, actions: Array<InvoiceAction>,
/**
 * On the week's invoice: its adjustments (`adjustment_seq` 1, 2, …), in
 * order, so the drawer can name the open one or the ones already sent.
 * Empty on an adjustment, whose `adjusts_*` fields name the week's invoice.
 */
adjustments: Array<InvoiceSummary>, };

export type InvoicePreviewResponse = { period: string, period_day_from: string, period_day_to: string, lines: Array<InvoiceLine>, total_cents: bigint, line_count: bigint, warnings: Array<string>, basis_fingerprint: string, basis_delivery_revision: bigint | null,
/**
 * The invoice already covering this period, if any.
 */
existing_invoice: InvoiceSummary | null, generated_at: string, };

export type InvoiceActionRequest = { kind: InvoiceActionKind,
/**
 * Required for `generate_period`.
 */
period: string | null,
/**
 * Required for every other kind (`raise_adjustment` names the week's
 * invoice, never an adjustment).
 */
invoice_id: string | null,
/**
 * Required for `approve_send`: the `basis_fingerprint` the admin saw.
 * The request is refused (409 `basis_changed`) when the invoice no
 * longer carries it, and the executor checks again before touching Xero.
 */
expected_basis_fingerprint: string | null, };

export type InvoiceActionEnqueued = { action_id: string, };

export type InvoicePdfUrlResponse = { url: string, expires_at: string, };

export type RuleGroup = { op: RuleOp, rules: Array<Rule>, };

export type Rule = { "kind": "purchase_history", production_ids: Array<ProductionId>, within_days: number, } | { "kind": "purchase_history_between", production_ids: Array<ProductionId>, from: string, to?: string | null, } | { "kind": "recency", within_days: number, } | { "kind": "last_purchase_between", from: string, to?: string | null, } | { "kind": "frequency", min_purchases: number, within_days: number, } | { "kind": "geo", zip_prefixes: Array<string>, } | { "kind": "age", min: number, max: number, } | { "kind": "age_bands", bands: Array<string>, } | { "kind": "gender", values: Array<string>, } | { "kind": "income", min_band: string, max_band: string, } | { "kind": "income_bands", bands: Array<string>, } | { "kind": "credit_score", values: Array<string>, } | { "kind": "education", values: Array<string>, } | { "kind": "religion", values: Array<string>, } | { "kind": "household_has_children", value: boolean, } | { "kind": "subscriber_tier", tiers: Array<string>, } | { "kind": "pixel_action", pixel_id: PixelId, action: WebEventType, within_days: number, } | { "kind": "web_visit", domain: string, within_days: number, } | { "kind": "web_action", event_name: string, within_days: number, } | { "kind": "lookalike", seed: AudienceId, top_share_bps: number, } | { "kind": "group" } & RuleGroup;

export type RuleOp = "any" | "all";

export type AudienceBucketDimension = "age" | "gender" | "income" | "credit_score" | "education" | "religion" | "household_has_children";

export type AudienceBucketValue = string | boolean;

export type SyncState = { target: SyncTarget, status: SyncStatus, external_id: string | null, last_synced_at: string | null, last_error: string | null, attempts: number, };

export const META_CREATIVE_CTA_VALUES = [
  "Buy Tickets",
  "Learn More"
] as const;

export const META_CREATIVE_CTA_ACCEPTED_LABELS = [
  "Get Tickets",
  "Buy Tickets",
  "Learn More"
] as const;

export const META_CREATIVE_CTA_REFUSALS: Readonly<Record<string, string>> = {
  "Book Now": "Meta refuses Book Now on sales campaigns (Graph v26.0, code 100, subcode 1885396, measured 2026-10-02), so no ad can be created with it. Choose Buy Tickets or Learn More.",
  "See Showtimes": "See Showtimes has not been verified with Meta on sales campaigns, so it cannot be published. Choose Buy Tickets or Learn More.",
  "Sign Up": "Sign Up has not been verified with Meta on sales campaigns, so it cannot be published. Choose Buy Tickets or Learn More."
};

export const META_POST_TEXT_RECOMMENDED_CHARS = 125;

export const META_HEADLINE_RECOMMENDED_CHARS = 40;

export const META_LINK_DESCRIPTION_RECOMMENDED_CHARS = 30;

export const META_POST_TEXT_MAX_CHARS = 5000;

export const META_HEADLINE_MAX_CHARS = 255;

export const META_LINK_DESCRIPTION_MAX_CHARS = 255;

export const AUDIENCE_AGE_RANGE_VALUES = [
  "18-24",
  "25-34",
  "35-44",
  "45-54",
  "55-64",
  "65+"
] as const;

export const AUDIENCE_GENDER_VALUES = [
  "F",
  "M",
  "U"
] as const;

export const AUDIENCE_INCOME_BANDS = [
  "Less than $20,000",
  "$20,000 to $44,999",
  "$45,000 to $59,999",
  "$60,000 to $74,999",
  "$75,000 to $99,999",
  "$100,000 to $149,999",
  "$150,000 to $199,999",
  "$200,000 to $249,999",
  "$250,000+"
] as const;

export const AUDIENCE_CREDIT_SCORE_VALUES = [
  "499 or less",
  "500-549",
  "550-599",
  "600-649",
  "650-699",
  "700-749",
  "750-799",
  "800+"
] as const;

export const AUDIENCE_EDUCATION_VALUES = [
  "High School Dropout",
  "Completed High School",
  "College Dropout",
  "Completed Vocational/Technical School",
  "Completed College",
  "Completed Graduate School"
] as const;

export const AUDIENCE_RELIGION_VALUES = [
  "Buddhist",
  "Catholic",
  "Eastern Orthodox",
  "Greek Orthodox",
  "Hindu",
  "Islamic",
  "Jewish",
  "Lutheran",
  "Mormon",
  "Protestant",
  "Shinto",
  "Sikh"
] as const;

export const AUDIENCE_HOUSEHOLD_HAS_CHILDREN_VALUES = [
  true,
  false
] as const;

export const AUDIENCE_BUCKET_CATALOG = [
  {
    "dimension": "age",
    "value": "18-24",
    "display_label": "18-24",
    "order": 0
  },
  {
    "dimension": "age",
    "value": "25-34",
    "display_label": "25-34",
    "order": 1
  },
  {
    "dimension": "age",
    "value": "35-44",
    "display_label": "35-44",
    "order": 2
  },
  {
    "dimension": "age",
    "value": "45-54",
    "display_label": "45-54",
    "order": 3
  },
  {
    "dimension": "age",
    "value": "55-64",
    "display_label": "55-64",
    "order": 4
  },
  {
    "dimension": "age",
    "value": "65+",
    "display_label": "65+",
    "order": 5
  },
  {
    "dimension": "gender",
    "value": "F",
    "display_label": "Female",
    "order": 0
  },
  {
    "dimension": "gender",
    "value": "M",
    "display_label": "Male",
    "order": 1
  },
  {
    "dimension": "gender",
    "value": "U",
    "display_label": "Unknown",
    "order": 2
  },
  {
    "dimension": "income",
    "value": "Less than $20,000",
    "display_label": "Less than $20,000",
    "order": 0
  },
  {
    "dimension": "income",
    "value": "$20,000 to $44,999",
    "display_label": "$20,000 to $44,999",
    "order": 1
  },
  {
    "dimension": "income",
    "value": "$45,000 to $59,999",
    "display_label": "$45,000 to $59,999",
    "order": 2
  },
  {
    "dimension": "income",
    "value": "$60,000 to $74,999",
    "display_label": "$60,000 to $74,999",
    "order": 3
  },
  {
    "dimension": "income",
    "value": "$75,000 to $99,999",
    "display_label": "$75,000 to $99,999",
    "order": 4
  },
  {
    "dimension": "income",
    "value": "$100,000 to $149,999",
    "display_label": "$100,000 to $149,999",
    "order": 5
  },
  {
    "dimension": "income",
    "value": "$150,000 to $199,999",
    "display_label": "$150,000 to $199,999",
    "order": 6
  },
  {
    "dimension": "income",
    "value": "$200,000 to $249,999",
    "display_label": "$200,000 to $249,999",
    "order": 7
  },
  {
    "dimension": "income",
    "value": "$250,000+",
    "display_label": "$250,000+",
    "order": 8
  },
  {
    "dimension": "credit_score",
    "value": "499 or less",
    "display_label": "<500",
    "order": 0
  },
  {
    "dimension": "credit_score",
    "value": "500-549",
    "display_label": "500-549",
    "order": 1
  },
  {
    "dimension": "credit_score",
    "value": "550-599",
    "display_label": "550-599",
    "order": 2
  },
  {
    "dimension": "credit_score",
    "value": "600-649",
    "display_label": "600-649",
    "order": 3
  },
  {
    "dimension": "credit_score",
    "value": "650-699",
    "display_label": "650-699",
    "order": 4
  },
  {
    "dimension": "credit_score",
    "value": "700-749",
    "display_label": "700-749",
    "order": 5
  },
  {
    "dimension": "credit_score",
    "value": "750-799",
    "display_label": "750-799",
    "order": 6
  },
  {
    "dimension": "credit_score",
    "value": "800+",
    "display_label": "800+",
    "order": 7
  },
  {
    "dimension": "education",
    "value": "High School Dropout",
    "display_label": "Did Not Complete High School",
    "order": 0
  },
  {
    "dimension": "education",
    "value": "Completed High School",
    "display_label": "Completed High School",
    "order": 1
  },
  {
    "dimension": "education",
    "value": "College Dropout",
    "display_label": "Attended But Did Not Complete College",
    "order": 2
  },
  {
    "dimension": "education",
    "value": "Completed Vocational/Technical School",
    "display_label": "Completed Vocational/Technical School",
    "order": 3
  },
  {
    "dimension": "education",
    "value": "Completed College",
    "display_label": "Completed College",
    "order": 4
  },
  {
    "dimension": "education",
    "value": "Completed Graduate School",
    "display_label": "Completed Graduate School",
    "order": 5
  },
  {
    "dimension": "religion",
    "value": "Buddhist",
    "display_label": "Buddhist",
    "order": 0
  },
  {
    "dimension": "religion",
    "value": "Catholic",
    "display_label": "Catholic",
    "order": 1
  },
  {
    "dimension": "religion",
    "value": "Eastern Orthodox",
    "display_label": "Eastern Orthodox",
    "order": 2
  },
  {
    "dimension": "religion",
    "value": "Greek Orthodox",
    "display_label": "Greek Orthodox",
    "order": 3
  },
  {
    "dimension": "religion",
    "value": "Hindu",
    "display_label": "Hindu",
    "order": 4
  },
  {
    "dimension": "religion",
    "value": "Islamic",
    "display_label": "Islamic",
    "order": 5
  },
  {
    "dimension": "religion",
    "value": "Jewish",
    "display_label": "Jewish",
    "order": 6
  },
  {
    "dimension": "religion",
    "value": "Lutheran",
    "display_label": "Lutheran",
    "order": 7
  },
  {
    "dimension": "religion",
    "value": "Mormon",
    "display_label": "Mormon",
    "order": 8
  },
  {
    "dimension": "religion",
    "value": "Protestant",
    "display_label": "Protestant",
    "order": 9
  },
  {
    "dimension": "religion",
    "value": "Shinto",
    "display_label": "Shinto",
    "order": 10
  },
  {
    "dimension": "religion",
    "value": "Sikh",
    "display_label": "Sikh",
    "order": 11
  },
  {
    "dimension": "household_has_children",
    "value": true,
    "display_label": "Yes",
    "order": 0
  },
  {
    "dimension": "household_has_children",
    "value": false,
    "display_label": "No",
    "order": 1
  }
] as const;

export const AUDIENCE_AGE_MIN = 18;

export const AUDIENCE_AGE_MAX = 120;

export const AUDIENCE_PURCHASE_HISTORY_START = "2021-01-01";

export const AUDIENCE_MAX_WITHIN_DAYS = 3660;

export const CAMPAIGN_OVERVIEW_DOMAINS = [
  "catalog",
  "campaigns",
  "audiences",
  "creatives",
  "delivery_reporting",
  "attribution_reporting"
] as const satisfies readonly DataDomain[];

export const CANONICAL_REPORT_DOMAINS = [
  "catalog",
  "campaigns",
  "audiences",
  "creatives",
  "delivery_reporting",
  "attribution_reporting"
] as const satisfies readonly DataDomain[];

export const DELIVERY_COMPAT_DOMAINS = [
  "campaigns",
  "audiences",
  "delivery_reporting"
] as const satisfies readonly DataDomain[];

export const MARKUP_COMPAT_DOMAINS = [
  "campaigns",
  "delivery_reporting"
] as const satisfies readonly DataDomain[];

export const SPILLOVER_COMPAT_DOMAINS = [
  "catalog",
  "campaigns",
  "audiences",
  "creatives",
  "attribution_reporting"
] as const satisfies readonly DataDomain[];

export const INSTALL_PARAMS: ParamSpec[] = [
  {
    "key": "pixel_id",
    "data_attr": "data-pixel-id",
    "sam_key": null,
    "label": "Pixel ID",
    "shape": "[a-z0-9_-]{1,64}",
    "example": "summer25",
    "value_kind": "string",
    "required_for": [
      "Pageview",
      "Purchase"
    ],
    "events": [
      "Pageview",
      "Purchase"
    ],
    "group": "attribution",
    "default_on": true,
    "snippet_comment": "",
    "description": "Deployment identifier. Must match an active pixel."
  },
  {
    "key": "source_reference",
    "data_attr": "data-source-reference",
    "sam_key": "source_reference",
    "label": "Source reference",
    "shape": "[A-Za-z0-9_\\-.:] max 64",
    "example": "AATTAA",
    "value_kind": "string",
    "required_for": [],
    "events": [
      "Pageview",
      "Purchase"
    ],
    "group": "attribution",
    "default_on": true,
    "snippet_comment": "production house code — the server resolves the production",
    "description": "EncorePro house code; resolved server-side to a production when no explicit production id is present. The raw code is always stored."
  },
  {
    "key": "production_id",
    "data_attr": "data-production-id",
    "sam_key": "production_id",
    "label": "Production ID",
    "shape": "[A-Za-z0-9_\\-.:/] max 64",
    "example": "PRD-2f6c1e",
    "value_kind": "string",
    "required_for": [],
    "events": [
      "Pageview",
      "Purchase"
    ],
    "group": "attribution",
    "default_on": true,
    "snippet_comment": "exact production id (wins over source reference)",
    "description": "Exact production identifier. Takes precedence over source_reference."
  },
  {
    "key": "venue_id",
    "data_attr": "data-venue-id",
    "sam_key": "venue_id",
    "label": "Venue ID",
    "shape": "[A-Za-z0-9_\\-.:/] max 64",
    "example": "V-majestic",
    "value_kind": "string",
    "required_for": [],
    "events": [
      "Pageview",
      "Purchase"
    ],
    "group": "attribution",
    "default_on": true,
    "snippet_comment": "venue id",
    "description": "Venue identifier."
  },
  {
    "key": "event_id",
    "data_attr": null,
    "sam_key": "event_id",
    "label": "Event (performance) ID",
    "shape": "[A-Za-z0-9_\\-.:/] max 64",
    "example": "EVT-2026-05-14-eve",
    "value_kind": "string",
    "required_for": [],
    "events": [
      "Pageview",
      "Purchase"
    ],
    "group": "attribution",
    "default_on": true,
    "snippet_comment": "performance reference",
    "description": "EncorePro performance/showtime reference — not the web-event row id."
  },
  {
    "key": "ticket_id",
    "data_attr": null,
    "sam_key": "ticket_id",
    "label": "Ticket ID",
    "shape": "[A-Za-z0-9_\\-.:] max 128",
    "example": "TKT-9f3a-001",
    "value_kind": "string",
    "required_for": [
      "Purchase"
    ],
    "events": [
      "Purchase"
    ],
    "group": "transaction",
    "default_on": true,
    "snippet_comment": "unique per transaction (dedup key)",
    "description": "Required for Purchase. Unique per transaction — used for dedup."
  },
  {
    "key": "order_id",
    "data_attr": null,
    "sam_key": "order_id",
    "label": "Order ID",
    "shape": "[A-Za-z0-9_\\-.:] max 128",
    "example": "ORD-2026-0001",
    "value_kind": "string",
    "required_for": [],
    "events": [
      "Purchase"
    ],
    "group": "transaction",
    "default_on": true,
    "snippet_comment": "",
    "description": "Order identifier; falls back to ticket_id when absent."
  },
  {
    "key": "currency",
    "data_attr": null,
    "sam_key": "currency",
    "label": "Currency",
    "shape": "ISO 4217 uppercase",
    "example": "USD",
    "value_kind": "string",
    "required_for": [
      "Purchase"
    ],
    "events": [
      "Purchase"
    ],
    "group": "transaction",
    "default_on": true,
    "snippet_comment": "ISO 4217 uppercase",
    "description": "Three-letter currency code. Required for Purchase."
  },
  {
    "key": "value",
    "data_attr": null,
    "sam_key": "value",
    "label": "Value",
    "shape": "decimal, 0 < v ≤ 1,000,000",
    "example": "129.50",
    "value_kind": "number",
    "required_for": [
      "Purchase"
    ],
    "events": [
      "Purchase"
    ],
    "group": "transaction",
    "default_on": true,
    "snippet_comment": "total order value, decimal (not cents)",
    "description": "Total order value as a decimal (129.50, not 12950). Required for Purchase."
  },
  {
    "key": "units",
    "data_attr": null,
    "sam_key": "units",
    "label": "Units",
    "shape": "integer 1..10,000",
    "example": "2",
    "value_kind": "number",
    "required_for": [],
    "events": [
      "Purchase"
    ],
    "group": "transaction",
    "default_on": true,
    "snippet_comment": "ticket count (fan-out when > 1)",
    "description": "Ticket count; per-ticket beacon fan-out when > 1 (defaults to 1)."
  },
  {
    "key": "explicit_track",
    "data_attr": null,
    "sam_key": null,
    "label": "Explicit track call example",
    "shape": "commented JS block",
    "example": "",
    "value_kind": "string",
    "required_for": [],
    "events": [
      "Pageview",
      "Purchase"
    ],
    "group": "privacy",
    "default_on": true,
    "snippet_comment": "",
    "description": "Includes a commented sam('track', …) example. Usually unnecessary: the tag auto-detects GTM purchases and your studio extraction rules read the dataLayer."
  },
  {
    "key": "consent_hook",
    "data_attr": null,
    "sam_key": null,
    "label": "Consent hook",
    "shape": "boolean global",
    "example": "window.__samConsent = false",
    "value_kind": "string",
    "required_for": [],
    "events": [
      "Pageview",
      "Purchase"
    ],
    "group": "privacy",
    "default_on": true,
    "snippet_comment": "",
    "description": "Page-side kill switch: set window.__samConsent = false before the tag loads to suppress all tracking. GPC and DNT are always honored regardless."
  }
];

export const DEFAULT_PIXEL_HOST = "pixel.shubertads.com";

export const PIXEL_FANOUT_CAP = 20;

export const SGTM_PARAMS: ParamSpec[] = [
  {
    "key": "pixelId",
    "data_attr": null,
    "sam_key": null,
    "label": "Pixel ID",
    "shape": "TEXT (required)",
    "example": "summer25",
    "value_kind": "string",
    "required_for": [],
    "events": [
      "Pageview",
      "Purchase"
    ],
    "group": "required",
    "default_on": true,
    "snippet_comment": "",
    "description": "The SAM pixel this container forwards to."
  },
  {
    "key": "serverKey",
    "data_attr": null,
    "sam_key": null,
    "label": "Server key",
    "shape": "TEXT (required, secret)",
    "example": "samsk_summer25_…",
    "value_kind": "string",
    "required_for": [],
    "events": [
      "Pageview",
      "Purchase"
    ],
    "group": "required",
    "default_on": true,
    "snippet_comment": "",
    "description": "Minted once in Ads Manager → pixel drawer → Server key. Store as a server-container constant; rotating takes ≤5 min at the edge."
  },
  {
    "key": "sendPageviews",
    "data_attr": null,
    "sam_key": null,
    "label": "Send pageviews",
    "shape": "CHECKBOX",
    "example": "true",
    "value_kind": "string",
    "required_for": [],
    "events": [
      "Pageview",
      "Purchase"
    ],
    "group": "routing",
    "default_on": true,
    "snippet_comment": "",
    "description": "Forward page_view events as Pageview (audience building)."
  },
  {
    "key": "purchaseEventNames",
    "data_attr": null,
    "sam_key": null,
    "label": "Purchase event names",
    "shape": "TABLE",
    "example": "purchase, ecommerce_purchase",
    "value_kind": "string",
    "required_for": [],
    "events": [
      "Pageview",
      "Purchase"
    ],
    "group": "routing",
    "default_on": true,
    "snippet_comment": "",
    "description": "Event names mapped to Purchase. Everything else is dropped."
  },
  {
    "key": "valuePath",
    "data_attr": null,
    "sam_key": null,
    "label": "Value path",
    "shape": "TEXT",
    "example": "value",
    "value_kind": "string",
    "required_for": [],
    "events": [
      "Pageview",
      "Purchase"
    ],
    "group": "commerce",
    "default_on": true,
    "snippet_comment": "",
    "description": "Event-data path for the order value."
  },
  {
    "key": "currencyPath",
    "data_attr": null,
    "sam_key": null,
    "label": "Currency path",
    "shape": "TEXT",
    "example": "currency",
    "value_kind": "string",
    "required_for": [],
    "events": [
      "Pageview",
      "Purchase"
    ],
    "group": "commerce",
    "default_on": true,
    "snippet_comment": "",
    "description": "Event-data path for the ISO 4217 currency."
  },
  {
    "key": "transactionIdPath",
    "data_attr": null,
    "sam_key": null,
    "label": "Transaction id path",
    "shape": "TEXT",
    "example": "transaction_id",
    "value_kind": "string",
    "required_for": [],
    "events": [
      "Pageview",
      "Purchase"
    ],
    "group": "commerce",
    "default_on": true,
    "snippet_comment": "",
    "description": "Event-data path for the order/transaction id (dedup key on both platforms)."
  },
  {
    "key": "unitsMode",
    "data_attr": null,
    "sam_key": null,
    "label": "Units mode",
    "shape": "SELECT",
    "example": "sum_items_quantity",
    "value_kind": "string",
    "required_for": [],
    "events": [
      "Pageview",
      "Purchase"
    ],
    "group": "commerce",
    "default_on": true,
    "snippet_comment": "",
    "description": "How ticket count is derived: sum items[].quantity, a custom event param, or constant 1."
  },
  {
    "key": "sourceReference",
    "data_attr": null,
    "sam_key": null,
    "label": "Source reference",
    "shape": "TEXT",
    "example": "AATTAA",
    "value_kind": "string",
    "required_for": [],
    "events": [
      "Pageview",
      "Purchase"
    ],
    "group": "sam",
    "default_on": false,
    "snippet_comment": "",
    "description": "EncorePro house code — resolved to a production server-side. Event param sam_source_reference overrides per event."
  },
  {
    "key": "productionId",
    "data_attr": null,
    "sam_key": null,
    "label": "Production ID",
    "shape": "TEXT",
    "example": "",
    "value_kind": "string",
    "required_for": [],
    "events": [
      "Pageview",
      "Purchase"
    ],
    "group": "sam",
    "default_on": false,
    "snippet_comment": "",
    "description": "Exact production id (wins over source reference). Override: sam_production_id."
  },
  {
    "key": "venueId",
    "data_attr": null,
    "sam_key": null,
    "label": "Venue ID",
    "shape": "TEXT",
    "example": "",
    "value_kind": "string",
    "required_for": [],
    "events": [
      "Pageview",
      "Purchase"
    ],
    "group": "sam",
    "default_on": false,
    "snippet_comment": "",
    "description": "Venue id. Override: sam_venue_id."
  },
  {
    "key": "eventId",
    "data_attr": null,
    "sam_key": null,
    "label": "Event (performance) ID",
    "shape": "TEXT",
    "example": "",
    "value_kind": "string",
    "required_for": [],
    "events": [
      "Pageview",
      "Purchase"
    ],
    "group": "sam",
    "default_on": false,
    "snippet_comment": "",
    "description": "EncorePro performance reference. Override: sam_event_id."
  },
  {
    "key": "collectMetaCookies",
    "data_attr": null,
    "sam_key": null,
    "label": "Collect Meta cookies",
    "shape": "CHECKBOX",
    "example": "true",
    "value_kind": "string",
    "required_for": [],
    "events": [
      "Pageview",
      "Purchase"
    ],
    "group": "identity",
    "default_on": true,
    "snippet_comment": "",
    "description": "Read _fbp/_fbc first-party cookies (ads consent required) — powers Meta CAPI matching."
  },
  {
    "key": "collectClickIds",
    "data_attr": null,
    "sam_key": null,
    "label": "Collect click ids",
    "shape": "CHECKBOX",
    "example": "true",
    "value_kind": "string",
    "required_for": [],
    "events": [
      "Pageview",
      "Purchase"
    ],
    "group": "identity",
    "default_on": true,
    "snippet_comment": "",
    "description": "Parse fbclid/gclid/ttclid from page_location + the _ttp cookie (ads consent required)."
  },
  {
    "key": "collectGaIds",
    "data_attr": null,
    "sam_key": null,
    "label": "Collect GA ids",
    "shape": "CHECKBOX",
    "example": "true",
    "value_kind": "string",
    "required_for": [],
    "events": [
      "Pageview",
      "Purchase"
    ],
    "group": "identity",
    "default_on": true,
    "snippet_comment": "",
    "description": "Forward client_id/session_id from event data (analytics consent required)."
  },
  {
    "key": "enablePii",
    "data_attr": null,
    "sam_key": null,
    "label": "Collect PII",
    "shape": "CHECKBOX",
    "example": "false",
    "value_kind": "string",
    "required_for": [],
    "events": [
      "Pageview",
      "Purchase"
    ],
    "group": "pii",
    "default_on": false,
    "snippet_comment": "",
    "description": "Read GA4 user_data (raw + sha256_*) for identity resolution and CAPI advanced matching. Raw email additionally enables UID2 → The Trade Desk."
  },
  {
    "key": "adsConsent",
    "data_attr": null,
    "sam_key": null,
    "label": "Ads consent",
    "shape": "SELECT",
    "example": "denied",
    "value_kind": "string",
    "required_for": [],
    "events": [
      "Pageview",
      "Purchase"
    ],
    "group": "consent",
    "default_on": false,
    "snippet_comment": "",
    "description": "granted | denied | from event path. Fail-closed: anything not granted ⇒ marketing ids + PII stripped."
  },
  {
    "key": "analyticsConsent",
    "data_attr": null,
    "sam_key": null,
    "label": "Analytics consent",
    "shape": "SELECT",
    "example": "denied",
    "value_kind": "string",
    "required_for": [],
    "events": [
      "Pageview",
      "Purchase"
    ],
    "group": "consent",
    "default_on": false,
    "snippet_comment": "",
    "description": "granted | denied | from event path. Gates GA ids + forwarded vars."
  },
  {
    "key": "forwardVars",
    "data_attr": null,
    "sam_key": null,
    "label": "Forward vars",
    "shape": "TABLE",
    "example": "",
    "value_kind": "string",
    "required_for": [],
    "events": [
      "Pageview",
      "Purchase"
    ],
    "group": "advanced",
    "default_on": false,
    "snippet_comment": "",
    "description": "Whitelisted event-data keys copied into vars for the extraction engine."
  },
  {
    "key": "testEventCode",
    "data_attr": null,
    "sam_key": null,
    "label": "Test event code",
    "shape": "TEXT",
    "example": "TEST12345",
    "value_kind": "string",
    "required_for": [],
    "events": [
      "Pageview",
      "Purchase"
    ],
    "group": "advanced",
    "default_on": false,
    "snippet_comment": "",
    "description": "Routes Meta CAPI deliveries to Events Manager Test Events (drills only)."
  },
  {
    "key": "endpoint",
    "data_attr": null,
    "sam_key": null,
    "label": "Endpoint",
    "shape": "TEXT",
    "example": "https://pixel.shubertads.com/v1/collect/server",
    "value_kind": "string",
    "required_for": [],
    "events": [
      "Pageview",
      "Purchase"
    ],
    "group": "advanced",
    "default_on": true,
    "snippet_comment": "",
    "description": "Collect endpoint. Permission-pinned to *.shubertads.com — staging only."
  },
  {
    "key": "debugLogging",
    "data_attr": null,
    "sam_key": null,
    "label": "Debug logging",
    "shape": "CHECKBOX",
    "example": "false",
    "value_kind": "string",
    "required_for": [],
    "events": [
      "Pageview",
      "Purchase"
    ],
    "group": "advanced",
    "default_on": false,
    "snippet_comment": "",
    "description": "logToConsole in preview/debug mode."
  }
];

export const CONVERSION_SIGNALS: [string, string][] = [
  [
    "V",
    "value"
  ],
  [
    "C",
    "currency"
  ],
  [
    "O",
    "order_id"
  ],
  [
    "E",
    "beacon_event_id"
  ],
  [
    "P",
    "pii_email"
  ],
  [
    "N",
    "pii_phone_or_name"
  ],
  [
    "F",
    "fbp"
  ],
  [
    "B",
    "fbc"
  ],
  [
    "G",
    "click_id"
  ],
  [
    "A",
    "ga_ids"
  ],
  [
    "U",
    "uid2"
  ],
  [
    "I",
    "client_ip"
  ],
  [
    "W",
    "user_agent"
  ]
];

